// Section-line tool support: the geometry that cuts the plan along a line, and
// the 2D section elevation it generates. Inspired by mechanical-CAD section
// views — for every object the line crosses, a profile as wide as the cut chord
// and as tall as the object is drawn on a ground line, offset from the plan.

import type {
  BenchProfilePoint,
  GlassSegment,
  Polygon,
  ProductInstance,
  SectionLine,
  Vec2,
  Wall,
} from '../shared/types';
import { traitsOf } from '../shared/categories';
import {
  instanceToWorld,
  pointInPolygon,
  rotatePoint,
  toInstanceLocal,
} from './geometry';
import { drawGrips } from './grip';
import {
  contentMemo,
  copyData,
  HEAVY_FIELDS,
  sameData,
} from './plainData';
import { Viewport } from './viewport';
import { wallThicknessOf } from './dent';
import {
  ceilingCreases,
  type CeilingField,
  ceilingHex6,
  ceilingPieces,
  facetCurvePiece,
  hasCeiling,
  isFacetSeam,
  isFlatCeiling,
  outerAt,
  ownCeilingColor,
  profileAlong,
  type ProfilePt,
  resolveCeiling,
  ringArcCorners,
  roomCrown,
  surfaceNormal,
  underAt,
} from './ceiling';
import {
  ceilingThicknessOf,
  externalHeight,
  glassPaneBase,
  glassPaneTop,
  heaterWallBase,
  heaterWallTop,
  innerWallBase,
  innerWallTop,
  innerWallTopAt,
  interiorHeight,
  openingArchitrave,
  openingSill,
  partHeight,
  partSpan,
  polygonBase,
  polygonHeight,
  roomBase,
  wallBase,
  wallHeight,
} from './heights';
import {
  glazingOfPane,
  glazingWorldSolids,
  type RoomGlazing,
  roomGlazings,
} from './roomGlazing';
import {
  heaterWallBands,
  isJoint,
  isPartCut,
  isPolyRoom,
  openingHoleBand,
  outerDoorSpans,
  PART_CUT_PREFIX,
  polyEdges,
  polygonArea,
  polyInner,
  polyInnerWallFill,
  polyWall,
  resolvedOutline,
  resolvedOutlinePaths,
  withWallCuts,
} from './polyRoom';
import {
  nodeBenchSolids,
  roomBenchSolids,
  structureColor,
} from './benchSolids';
import { roomFeaturePieces } from './roomFeaturePieces';
import {
  backrestColor,
  backrestsOf,
  backrestSolids,
} from './backrest';
import {
  BEAM_DEFAULTS,
  type BeamCone,
  type BeamCones,
  beamFan,
  type BeamPt,
  ceilingUnder,
  channelFigures,
  channelsOf,
  channelSolids,
  coveGeometry,
  coveOf,
  coveRecess,
  coveStripSolids,
  fibreKitsOf,
  kitArcs,
  kitFibres,
  LIGHT_AMBER,
  LIGHT_BEAM_RGB,
  LIGHT_FIXTURE_COLOR,
  lightBeam,
  lightEmitters,
  projectorMiddle,
  skySolids,
  SPOT_BODY_COLOR,
  SPOT_LENS_DROP,
  spotMount,
  spotsOf,
  spotSolids,
  STRIP_HEIGHT,
  STRIP_WIDTH,
  stripSolids,
} from './lights';
import {
  paintViewTitle,
  type TitleBox,
} from './viewTitle';
import {
  doorGlassSpans,
  GLASS_STROKE_WIDTH,
  glassPolysLocal,
  glassStroke,
  hexToRgba,
} from './glass';
import { wallBand } from './wallTool';
import {
  type WallPiece,
  wallPockets,
  wallSectionPieces,
} from './wallFeatures';
import { inWallParts } from './wallCuts';
import {
  partRisers,
  RISER_COLORS,
  riserRing,
} from './risers';
import {
  profileCut,
  profileExtent,
  type ProfilePt as BenchPt,
  profileRing,
  profileSweepFaces,
  type SweepFace,
  sweepPlaneCut,
  type Vec3,
} from './benchProfile';
import {
  sculptColor,
  sculptedBenchesOf,
  sculptLayout,
  sculptSpan,
} from './sculptBench';
import { sculptTrestles } from './sculptTrestles';
import {
  BENCH_TIER_COLORS,
  type BenchTier,
  STEAM_BENCH_TIER_COLORS,
} from './benches';
import {
  occludeFaces,
  type SecCall,
  type SecFace,
  type SecKind,
  type SecShown,
} from './selfOcclusion';
import {
  byTag,
  OrderKey,
  type PaintCall,
  type PaintGroup,
  paintOrder,
  type PlanRings,
  rememberedOrder,
  rememberOrder,
} from './sectionOrder';
import { roomDoor } from './roomDoor';
export { paintOrderStats } from './sectionOrder';
import { roomDoors } from './door';
import {
  bodyInSection,
  bodyOf,
  isTilted,
  planOutline,
  tiltedExtents,
} from './tilt';
import {
  modelInSection,
  type ModelPiece,
  partModel,
  solidsInSection,
} from './partModel';
import {
  type PartAttachment,
  solidLook,
} from './partLook';
import { unreachable } from '../shared/exhaustive';
import { lightenHex } from './hexTone';
import {
  grilleColor,
  grilleLayout,
  grilleUnder,
} from './floorGrille';

const USER_WALL_COLOR = '#d8d2c4';
// Elevation glazing: lighter than the plan glass fill (rgba…,0.6) so projected
// geometry behind the pane stays readable and the glass looks clearly see-through.
const SECTION_GLASS_FILL = 'rgba(74, 163, 255, 0.22)';
/** World footprint polygon of a user-drawn wall (skips thin line-tool strokes). */
function wallFootprint(wall: Wall): Vec2[] | null {
  if (wall.style === 'line') return null;
  const band = wallBand(wall.points, wall.thickness, wall.align);
  return band.length >= 3 ? band : null;
}

/**
 * One footprint band PER SEGMENT of a node object's run (heated bench, salt
 * wall, sauna bench, drain channel) — empty for anything that is not one.
 *
 * These are ribbons, exactly like a user-drawn wall, and they were being
 * sectioned through `instanceFootprint`, which for a node object falls back to
 * the TEMPLATE's `width` x `depth` box centred on the instance origin. That box
 * is not the run: a bent bench came out as one block, in the wrong place, at a
 * depth taken from the box rather than from the leg you are actually looking at.
 *
 * The node line is the BACK edge and the body extends `depth` along the segment
 * normal `(dy, -dx)` — the convention `saltAxes` / `drawHeatedBench` use in the
 * plan. Joints are not mitred (a section reads only each piece's span and
 * depth), matching `wallSectionPieces`.
 */
function nodeSegmentFootprints(inst: ProductInstance): Vec2[][] {
  const nodes = inst.nodes;
  if (!nodes || nodes.length < 2) return [];
  const d = inst.depth;
  const out: Vec2[][] = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 1e-6) continue;
    const nx = (b.y - a.y) / len, ny = -(b.x - a.x) / len;
    out.push([a, b, { x: b.x + nx * d, y: b.y + ny * d }, { x: a.x + nx * d, y: a.y + ny * d }]);
  }
  return out;
}

// Wall default colours (mirrors render.ts, kept local to avoid an import cycle).
const SAUNA_WALL_DEFAULT = '#e7b079';
const STEAM_WALL_DEFAULT = '#6b4e30';
type GlazingSolids = ReturnType<typeof glazingWorldSolids>;
const GLAZING_SECTIONS = new WeakMap<GlazingSolids, Map<string, ReturnType<typeof solidsInSection<GlazingSolids[number]>>>>();

/** A glazing's solids sliced by a section line: kept with its world solids (the
 *  same until the room is edited or moved) for each line, so a frame that only
 *  zooms or pans slices nothing (~40 ms a glazing). */
function glazingInSection(solids: GlazingSolids, p: { a: Vec2; u: Vec2; look: Vec2 }, len: number): ReturnType<typeof solidsInSection<GlazingSolids[number]>> {
  const key = [p.a.x, p.a.y, p.u.x, p.u.y, p.look.x, p.look.y, len].join();
  let byLine = GLAZING_SECTIONS.get(solids);
  if (!byLine) GLAZING_SECTIONS.set(solids, byLine = new Map());
  let out = byLine.get(key);
  if (!out) {
    if (byLine.size > 16) byLine.clear();
    byLine.set(key, out = solidsInSection(solids, p, [0, len]));
  }
  return out;
}

function wallColorOf(inst: ProductInstance): string {
  return inst.wallColor || (inst.category === 'steamroom' ? STEAM_WALL_DEFAULT : SAUNA_WALL_DEFAULT);
}
function benchColorOf(inst: ProductInstance, band: { id: string; level: BenchTier }): string {
  return inst.polyBenches?.find((p) => p.id === band.id)?.color
    ?? inst.benchColor
    ?? (inst.category === 'steamroom' ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS)[band.level];
}

function darken(hex: string, k: number): string {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  const d = (v: number): number => Math.round(v * k);
  return `rgb(${d((n >> 16) & 255)}, ${d((n >> 8) & 255)}, ${d(n & 255)})`;
}
/** An opaque, lightened version of a colour — blends `k` (0..1) of the way to
 *  white. Used for the ground under a hatched cut face so the material reads as
 *  solid (not see-through) while the hatch lines still stand out over it. */
function lighten(hex: string, k: number): string {
  const h = hex.replace('#', '');
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  const l = (v: number): number => Math.round(v + (255 - v) * k);
  return `rgb(${l((n >> 16) & 255)}, ${l((n >> 8) & 255)}, ${l(n & 255)})`;
}

const DEFAULT_HEIGHT_MM = 2000;

/** The instance footprint as a world-space polygon (poly room outline or body box). */
export function instanceFootprint(inst: ProductInstance): Vec2[] {
  // The resolved outline, so a cut through a rounded corner meets the arc where
  // the plan draws it rather than at a phantom sharp corner.
  if (inst.polygon && inst.polygon.length >= 3) return resolvedOutline(inst, 0).map((p) => instanceToWorld(inst, p));
  // A tilted part: the box its tilted body fills, along its plan turn.
  const { hw, hd } = isTilted(inst) ? tiltedExtents(inst, { w: inst.width, d: inst.depth, h: partHeight(inst) }) : { hw: inst.width / 2, hd: inst.depth / 2 };
  return [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }].map((p) => instanceToWorld(inst, p));
}

/** Best-effort object height (mm) for the section elevation. */
export function instanceSectionHeight(inst: ProductInstance): number {
  return inst.height || DEFAULT_HEIGHT_MM;
}

/** Vertical extent [floor, top] (mm) of a non-room object in the section:
 *  `partSpan`'s on the part's own figures (a section reads no catalogue), so a
 *  heater or a shower floats by its Height from ground and a part sunk below the
 *  floor reference stays sunk. */
function instanceElevation(inst: ProductInstance): [number, number] {
  return partSpan(inst);
}

/** Local-frame corners of an axis-aligned box, CCW. */
function boxCornersLocal(b: { x0: number; y0: number; x1: number; y1: number }): Vec2[] {
  return [{ x: b.x0, y: b.y0 }, { x: b.x1, y: b.y0 }, { x: b.x1, y: b.y1 }, { x: b.x0, y: b.y1 }];
}
/** Subtract a set of hole intervals from [a,b], returning the surviving spans. */
function subtractSpans(a: number, b: number, holes: Array<[number, number]>): Array<[number, number]> {
  let spans: Array<[number, number]> = [[a, b]];
  for (const [h0, h1] of holes) {
    if (h1 <= h0) continue;
    const next: Array<[number, number]> = [];
    for (const [s0, s1] of spans) {
      if (h1 <= s0 || h0 >= s1) { next.push([s0, s1]); continue; } // no overlap
      if (h0 > s0) next.push([s0, Math.min(h0, s1)]); // left remainder
      if (h1 < s1) next.push([Math.max(h1, s0), s1]); // right remainder
    }
    spans = next;
  }
  return spans.filter(([s0, s1]) => s1 - s0 > 1e-3);
}


/** Parameter t∈[0,1] on segment a-b where it crosses segment c-d, or null. */
function segParam(a: Vec2, b: Vec2, c: Vec2, d: Vec2): number | null {
  const rx = b.x - a.x, ry = b.y - a.y, sx = d.x - c.x, sy = d.y - c.y;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-9) return null;
  const qpx = c.x - a.x, qpy = c.y - a.y;
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  if (t >= -1e-9 && t <= 1 + 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) return Math.min(1, Math.max(0, t));
  return null;
}

/** Where segment a-b crosses a polygon: [tStart,tEnd] as distances (mm) from a, or null. */
export function segmentPolyChord(a: Vec2, b: Vec2, poly: Vec2[]): [number, number] | null {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len < 1e-6) return null;
  const ts: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const t = segParam(a, b, poly[i], poly[(i + 1) % poly.length]);
    if (t != null) ts.push(t * len);
  }
  if (pointInPolygon(a, poly)) ts.push(0);
  if (pointInPolygon(b, poly)) ts.push(len);
  if (ts.length < 2) return null;
  const t0 = Math.min(...ts), t1 = Math.max(...ts);
  return t1 - t0 < 1e-6 ? null : [t0, t1];
}

/** Every stretch of segment a-b inside a polygon, as [tStart,tEnd] distances
 *  (mm) from a, in order. Unlike `segmentPolyChord` a concave polygon yields one
 *  interval per piece: a cut across both arms of a U is two, not one spanning
 *  the void between them. Crossings within 1e-9·len are one (a chord through a
 *  vertex meets both of its edges there), and a piece takes its first crossing's
 *  lowest value and its last's highest, as `segmentPolyChord` takes min / max —
 *  so a convex polygon gives exactly that chord. Pieces meeting end to end (a
 *  chord through a reflex vertex, or along an edge up to one) are one piece. */
export function segmentPolyIntervals(a: Vec2, b: Vec2, poly: Vec2[]): Array<[number, number]> {
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len < 1e-6 || poly.length < 3) return [];
  const raw: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const t = segParam(a, b, poly[i], poly[(i + 1) % poly.length]);
    if (t != null) raw.push(t * len);
  }
  if (pointInPolygon(a, poly)) raw.push(0);
  if (pointInPolygon(b, poly)) raw.push(len);
  raw.sort((x, y) => x - y);
  const cl: Array<{ lo: number; hi: number }> = [];
  for (const t of raw) {
    const last = cl[cl.length - 1];
    if (last && t - last.hi <= 1e-9 * len) last.hi = t;
    else cl.push({ lo: t, hi: t });
  }
  const out: Array<[number, number]> = [];
  for (let k = 0; k + 1 < cl.length; k++) {
    const s0 = cl[k].hi, s1 = cl[k + 1].lo;
    if (s1 - s0 < 1e-6) continue;
    const m = (s0 + s1) / (2 * len);
    if (!pointInPolygon({ x: a.x + (b.x - a.x) * m, y: a.y + (b.y - a.y) * m }, poly)) continue;
    const t0 = cl[k].lo, t1 = cl[k + 1].hi;
    const prev = out[out.length - 1];
    if (prev && t0 - prev[1] <= 1e-6) prev[1] = t1;
    else out.push([t0, t1]);
  }
  return out;
}

/**
 * Cut-line endpoints of every section except `exceptId` — the alignment targets
 * offered while drawing or reshaping a section line.
 *
 * Deliberately NOT distance-limited (unlike `nearbyOrthoVerts`): sections span a
 * whole plan and the point of aligning one to another is that they line up right
 * across it. The screen-space guide threshold is what keeps the snap local.
 */
export function sectionLineVertices(sections: readonly SectionLine[] | null | undefined, exceptId?: string | null): Vec2[] {
  const out: Vec2[] = [];
  for (const s of sections ?? []) {
    if (exceptId && s.id === exceptId) continue;
    out.push({ ...s.a }, { ...s.b });
  }
  return out;
}

/** Unit direction along the line and the chosen (signed) normal. */
export function sectionFrame(sec: Pick<SectionLine, 'a' | 'b' | 'side'>): { u: Vec2; n: Vec2; len: number } {
  const dx = sec.b.x - sec.a.x, dy = sec.b.y - sec.a.y;
  const len = Math.hypot(dx, dy) || 1;
  const u = { x: dx / len, y: dy / len };
  const n = { x: -u.y * sec.side, y: u.x * sec.side }; // rot90 * side
  return { u, n, len };
}

/** World frame of the generated section drawing. `O` is the baseline start;
 *  `T` runs along the drawing's length, `N` is the (up) height axis. Honours
 *  `viewOrigin` / `viewAngle` when the user has moved or rotated the drawing;
 *  otherwise it sits at the perpendicular `offset` aligned with the line. */
export interface SectionView { O: Vec2; T: Vec2; N: Vec2; len: number; }
export function sectionView(sec: SectionLine): SectionView {
  const { u, n, len } = sectionFrame(sec);
  const angle = sec.viewAngle ?? Math.atan2(u.y, u.x);
  const T = { x: Math.cos(angle), y: Math.sin(angle) };
  const N = { x: -T.y * sec.side, y: T.x * sec.side };
  const O = sec.viewOrigin ?? { x: sec.a.x + n.x * sec.offset, y: sec.a.y + n.y * sec.offset };
  return { O, T, N, len };
}

/** Tallest profile (mm) the drawing will show — its bounding-box height. */
export function sectionViewMaxHeight(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = []): number {
  let h = 0;
  for (const inst of instances) {
    if (!segmentPolyChord(sec.a, sec.b, instanceFootprint(inst))) continue;
    const ih = isRoomInstance(inst) ? roomBase(inst) + roomCrown(inst) : instanceElevation(inst)[1];
    if (ih > h) h = ih;
  }
  for (const wall of walls) {
    const fp = wallFootprint(wall);
    const top = wallBase(wall) + wallHeight(wall); // honour the wall's height-from-ground
    if (fp && segmentPolyChord(sec.a, sec.b, fp) && top > h) h = top;
  }
  for (const poly of polygons) {
    const top = polygonBase(poly) + polygonHeight(poly);
    if (polygonHeight(poly) > 0 && poly.points.length >= 3 && segmentPolyChord(sec.a, sec.b, poly.points) && top > h) h = top;
  }
  return h || DEFAULT_HEIGHT_MM;
}

/** The letter shown for the section at index `i` (A, B, … Z, A1, B1 …). Derived
 *  from position so deletions renumber the rest for continuity. */
export function sectionLabel(i: number): string {
  return String.fromCharCode(65 + (i % 26)) + (i >= 26 ? String(Math.floor(i / 26)) : '');
}

interface Draw2 { worldToScreen(p: Vec2): Vec2; }

/** The drawing's extents in its own `(t, h)` frame. */
export interface SectionExtent { t0: number; t1: number; h0: number; h1: number; }

/** The section marker's proportions, in screen px on the canvas (the DXF scales
 *  them, `SECTION_ARROW_MM`): each view arrow's length, its head's length and
 *  half-width, and the letter's offset off the line's first end, away from the view. */
export const SECTION_MARKER = { arrow: 20, head: 6, headHalf: 3.5, letter: 14 } as const;

/** Where the drawing's A–A title hangs from, in its view frame: centred under
 *  the drawing's own bottom edge — the floor unless something sinks beneath it. */
export function sectionTitleAt(len: number, ext: Pick<SectionExtent, 'h0' | 'h1'>): { t: number; h: number } {
  return { t: len / 2, h: ext.h0 - (200 + (ext.h1 - ext.h0) * 0.08) };
}

/** Where a section's title hangs, as drawn, printed and exported: `sectionTitleAt`
 *  moved by its `titleOffset` (the view's frame) — null once it is deleted. */
export function sectionTitlePos(sec: Pick<SectionLine, 'titleOffset' | 'titleHidden'>, len: number, ext: Pick<SectionExtent, 'h0' | 'h1'>): { t: number; h: number } | null {
  if (sec.titleHidden) return null;
  const at = sectionTitleAt(len, ext), o = sec.titleOffset;
  return o ? { t: at.t + o.t, h: at.h + o.h } : at;
}


/** What the generated drawing actually COVERS, measured by replaying the same
 *  emitter that draws it — so the selection box, the rotate handle and the title
 *  can never disagree with what is on screen.
 *
 *  `sectionViewMaxHeight` is not that box: it only measures objects the plane
 *  CUTS, while the drawing also shows everything standing beyond the plane in
 *  elevation. A short cut wall in front of a tall object therefore gave a box
 *  ending far below the drawing's own top edge. Height comes from the emitter;
 *  `t` always spans the whole cut because the ground line does, and `h` always
 *  includes 0 for the same reason. */
export function sectionViewExtent(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): SectionExtent {
  // Asked by the title, the box and the hit tests on every repaint: kept while
  // the section and what it cuts hold (a hover or a pan replays nothing).
  return EXTENTS.get(sec.id, () => [sec, instances, walls, polygons, pipes], () => extentOf(sec, instances, walls, polygons, pipes));
}
const EXTENTS = contentMemo<SectionExtent>({ max: 64, skip: HEAVY_FIELDS });

function extentOf(sec: SectionLine, instances: ProductInstance[], walls: Wall[], polygons: Polygon[], pipes: readonly SectionPipe[]): SectionExtent {
  const len = sectionView(sec).len;
  let h0 = 0, h1 = 0;
  const span = (a: number, b: number): void => { h0 = Math.min(h0, a, b); h1 = Math.max(h1, a, b); };
  const rec = (_t0: number, _t1: number, a: number, b: number): void => span(a, b);
  const collect: SecDraw = {
    src: () => {},
    profile: (_t0, _t1, a, b) => span(a, b),
    band: rec, elev: rec, outline: rec, glass: rec, window: rec,
    vedge: (_t, a, b) => span(a, b),
    poly: (pts) => { for (const p of pts) span(p.h, p.h); },
    line: (pts) => { for (const p of pts) span(p.h, p.h); },
  };
  emitSectionGeometry(sec, instances, walls, len, '#000000', collect, polygons, false, pipes);
  // Nothing drawn at all (a cut that crosses nothing): keep the box usable so an
  // empty section is still selectable and movable.
  if (h1 - h0 < 1e-6) h1 = h0 + sectionViewMaxHeight(sec, instances, walls, polygons);
  return { t0: 0, t1: len, h0, h1 };
}

/** The extent's four corners in world space. */
function extentQuad(view: SectionView, ext: SectionExtent): Vec2[] {
  const at = (t: number, h: number): Vec2 => ({
    x: view.O.x + view.T.x * t + view.N.x * h,
    y: view.O.y + view.T.y * t + view.N.y * h,
  });
  return [at(ext.t0, ext.h0), at(ext.t1, ext.h0), at(ext.t1, ext.h1), at(ext.t0, ext.h1)];
}

/** The drawing's bounding box in world space — the outline its hover and
 *  selection both read. */
export function sectionViewOutline(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): Vec2[] {
  return extentQuad(sectionView(sec), sectionViewExtent(sec, instances, walls, polygons, pipes));
}

/** True if `screen` falls inside the section drawing's bounding box. */
export function sectionViewContains(vp: Draw2, sec: SectionLine, instances: ProductInstance[], screen: Vec2, walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): boolean {
  const view = sectionView(sec);
  const ext = sectionViewExtent(sec, instances, walls, polygons, pipes);
  const quad = extentQuad(view, ext).map((p) => vp.worldToScreen(p));
  let inside = false;
  for (let i = 0, j = quad.length - 1; i < quad.length; j = i++) {
    const a = quad[i], b = quad[j];
    if ((a.y > screen.y) !== (b.y > screen.y) && screen.x < ((b.x - a.x) * (screen.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Screen position of the drawing's rotate handle, above the top edge of `ext`. */
function rotateHandleAt(vp: Draw2, view: SectionView, ext: SectionExtent): Vec2 {
  const { O, T, N } = view;
  const mid = (ext.t0 + ext.t1) / 2;
  const topS = vp.worldToScreen({ x: O.x + T.x * mid + N.x * ext.h1, y: O.y + T.y * mid + N.y * ext.h1 });
  const upS = vp.worldToScreen({ x: O.x + T.x * mid + N.x * (ext.h1 + 1), y: O.y + T.y * mid + N.y * (ext.h1 + 1) });
  const dx = upS.x - topS.x, dy = upS.y - topS.y; const l = Math.hypot(dx, dy) || 1;
  return { x: topS.x + (dx / l) * 28, y: topS.y + (dy / l) * 28 };
}

/** Screen position of the drawing's rotate handle (above its top edge). */
export function sectionRotateHandle(vp: Draw2, sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): Vec2 {
  return rotateHandleAt(vp, sectionView(sec), sectionViewExtent(sec, instances, walls, polygons, pipes));
}

/** A box in a section's own `(t, h)` frame. */
export interface SecBox { t0: number; t1: number; h0: number; h1: number }

/** What a section view in the plan marks over its drawing: a box round the
 *  pieces of each selected target (`srcMatches`: an object, or one of its
 *  features), and — while a click drills into it — its own frame, dashed. */
export interface SectionMarks {
  selected: readonly SecSrc[];
  drilled?: boolean;
  /** false: no light's beam (the Beams toggle). */
  beams?: boolean;
  /** false: the lights hidden (the Lighting toggle) — no beam, no fibre arc. */
  lights?: boolean;
  /** The drawing's beam sizes (`Drawing.lightBeams`), else `BEAM_DEFAULTS`. */
  cones?: { linear: BeamCone; spot: BeamCone };
  /** Its title is the selection: painted in the selection blue, boxed. */
  titleSelected?: boolean;
  /** Told where its title was painted (screen px): the plan's press finds it there. */
  onTitle?: (box: TitleBox) => void;
}

/** Whether a call of source `s` shows `target`: the same object and, when the
 *  target names one of its features, that feature — a bench's, its supports'
 *  trestles too. */
export function srcMatches(s: SecSrc | null | undefined, target: SecSrc): boolean {
  if (!s || s.id !== target.id) return false;
  const p = s.part, t = target.part;
  if (!t) return true;
  return !!p && ((p.kind === t.kind && p.id === t.id) || (t.kind === 'bench' && p.bench === t.id));
}

/** `api` that also grows, for each target, the box of the calls showing it. */
function boxing(api: SecDraw, targets: readonly SecSrc[]): { api: SecDraw; boxes: Array<SecBox | null> } {
  const boxes: Array<SecBox | null> = targets.map(() => null);
  let hits: number[] = [];
  const grow = (t: number, h: number): void => {
    for (const i of hits) {
      const b = boxes[i];
      if (!b) boxes[i] = { t0: t, t1: t, h0: h, h1: h };
      else { b.t0 = Math.min(b.t0, t); b.t1 = Math.max(b.t1, t); b.h0 = Math.min(b.h0, h); b.h1 = Math.max(b.h1, h); }
    }
  };
  const quad = (t0: number, t1: number, h0: number, h1: number): void => { if (hits.length) { grow(t0, h0); grow(t1, h1); } };
  const pts = (ps: readonly SecPt[]): void => { if (hits.length) for (const p of ps) grow(p.t, p.h); };
  if (!targets.length) return { api, boxes };
  return {
    boxes,
    api: {
      src: (s) => { hits = []; targets.forEach((t, i) => { if (srcMatches(s, t)) hits.push(i); }); api.src(s); },
      profile: (t0, t1, h0, h1, filled, color) => { quad(t0, t1, h0, h1); api.profile(t0, t1, h0, h1, filled, color); },
      band: (t0, t1, h0, h1, color, alpha) => { quad(t0, t1, h0, h1); api.band(t0, t1, h0, h1, color, alpha); },
      elev: (t0, t1, h0, h1, color) => { quad(t0, t1, h0, h1); api.elev(t0, t1, h0, h1, color); },
      outline: (t0, t1, h0, h1, color) => { quad(t0, t1, h0, h1); api.outline(t0, t1, h0, h1, color); },
      vedge: (t, h0, h1, color) => { quad(t, t, h0, h1); api.vedge(t, h0, h1, color); },
      glass: (t0, t1, h0, h1, color) => { quad(t0, t1, h0, h1); api.glass(t0, t1, h0, h1, color); },
      window: (t0, t1, h0, h1, color) => { quad(t0, t1, h0, h1); api.window(t0, t1, h0, h1, color); },
      poly: (ps, style, color, alpha) => { pts(ps); api.poly(ps, style, color, alpha); },
      line: (ps, color, dashed) => { pts(ps); api.line(ps, color, dashed); },
    },
  };
}

const NO_DRAW: SecDraw = {
  src: () => {}, profile: () => {}, band: () => {}, elev: () => {}, outline: () => {},
  vedge: () => {}, glass: () => {}, window: () => {}, poly: () => {}, line: () => {},
};

/** The box of each target's pieces in the section's view — null for a target it
 *  does not show. What `drawSection` outlines, and the drill-in's hover. */
export function sectionSourceBoxes(sec: SectionLine, instances: ProductInstance[], walls: Wall[], polygons: Polygon[], targets: readonly SecSrc[], pipes: readonly SectionPipe[] = []): Array<SecBox | null> {
  const b = boxing(NO_DRAW, targets);
  emitSectionGeometry(sec, instances, walls, sectionView(sec).len, '#000000', b.api, polygons, true, pipes);
  return b.boxes;
}

/** A box of the view's frame as a world ring, `pad` mm out on every side. */
export function sectionBoxRing(sec: SectionLine, b: SecBox, pad = 0): Vec2[] {
  return extentQuad(sectionView(sec), { t0: b.t0 - pad, t1: b.t1 + pad, h0: b.h0 - pad, h1: b.h1 + pad });
}

/** The colour of a selection box (the plan's and the 3D view's yellow). */
const SECTION_SELECT_COLOR = '#ffd166';
/** How far a selection box stands off its pieces, in screen px. */
export const SECTION_BOX_PAD_PX = 4;

/** Draw a section line marker plus the 2D section it generates. `label` is the
 *  letter shown next to the marker (derived from section order by the caller);
 *  `marks` what the plan's live view outlines over it (none on a sheet or a print). */
export function drawSection(ctx: CanvasRenderingContext2D, vp: Viewport & Draw2, sec: SectionLine, instances: ProductInstance[], selected: boolean, label = '', walls: Wall[] = [], polygons: Polygon[] = [], marks?: SectionMarks, pipes: readonly SectionPipe[] = []): void {
  const { n } = sectionFrame(sec);
  const view = sectionView(sec);
  const col = selected ? '#4aa3ff' : '#c0392b';
  const A = vp.worldToScreen(sec.a), B = vp.worldToScreen(sec.b);

  ctx.save();
  // The cut line (dash-dot, like a section line).
  ctx.strokeStyle = col;
  ctx.lineWidth = selected ? 2.2 : 1.6;
  ctx.setLineDash([12, 4, 3, 4]);
  ctx.beginPath(); ctx.moveTo(A.x, A.y); ctx.lineTo(B.x, B.y); ctx.stroke();
  ctx.setLineDash([]);

  // Parallel view arrows at both ends, pointing toward the side the section looks
  // at (and thus what it projects). `n` is a world-space normal; screen Y is
  // flipped, so negate its y to get the on-screen direction.
  const ns = { x: n.x, y: -n.y };
  const { arrow: AR, head, headHalf, letter } = SECTION_MARKER;
  for (const end of [A, B]) {
    const tip = { x: end.x + ns.x * AR, y: end.y + ns.y * AR };
    ctx.beginPath(); ctx.moveTo(end.x, end.y); ctx.lineTo(tip.x, tip.y); ctx.stroke();
    const back = { x: tip.x - ns.x * head, y: tip.y - ns.y * head };
    const perp = { x: -ns.y * headHalf, y: ns.x * headHalf };
    ctx.beginPath();
    ctx.moveTo(tip.x, tip.y);
    ctx.lineTo(back.x + perp.x, back.y + perp.y);
    ctx.lineTo(back.x - perp.x, back.y - perp.y);
    ctx.closePath(); ctx.fillStyle = col; ctx.fill();
  }
  // Label near the first end, offset away from the view side.
  if (label) {
    ctx.fillStyle = col;
    ctx.font = 'bold 13px system-ui, sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText(label, A.x - ns.x * letter, A.y - ns.y * letter);
  }

  // The generated section: ground line + a profile per crossed object, laid out
  // in the (possibly moved/rotated) view frame.
  const base = (t: number, h: number): Vec2 => ({
    x: view.O.x + view.T.x * t + view.N.x * h,
    y: view.O.y + view.T.y * t + view.N.y * h,
  });
  const len = view.len;
  const ext = sectionViewExtent(sec, instances, walls, polygons, pipes);
  const g0 = vp.worldToScreen(base(0, 0)), g1 = vp.worldToScreen(base(len, 0));
  ctx.strokeStyle = col;
  ctx.setLineDash([2, 3]);
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(g0.x, g0.y); ctx.lineTo(g1.x, g1.y); ctx.stroke(); // ground
  ctx.setLineDash([]);

  // A cut profile quad from chord [t0,t1] at heights [h0,h1], coloured by the
  // sectioned object. Both variants are OPAQUE — a cut material must occlude
  // whatever lies behind it, never read as see-through (only genuinely
  // transparent materials, e.g. glass, are drawn translucent). `filled` benches
  // read as solid material colour; cut faces (walls) get a light opaque ground
  // with hatch lines over it.
  const profile = (t0: number, t1: number, h0: number, h1: number, filled: boolean, color: string): void => {
    const q0 = vp.worldToScreen(base(t0, h0)), q1 = vp.worldToScreen(base(t1, h0));
    const q2 = vp.worldToScreen(base(t1, h1)), q3 = vp.worldToScreen(base(t0, h1));
    ctx.beginPath();
    ctx.moveTo(q0.x, q0.y); ctx.lineTo(q1.x, q1.y); ctx.lineTo(q2.x, q2.y); ctx.lineTo(q3.x, q3.y); ctx.closePath();
    ctx.fillStyle = filled ? color : lighten(color, 0.78);
    ctx.fill();
    ctx.strokeStyle = darken(color, 0.55); ctx.lineWidth = 1.3; ctx.stroke();
    if (!filled) hatch(ctx, [q0, q1, q2, q3], color);
  };

  const quad = (t0: number, t1: number, h0: number, h1: number): Vec2[] =>
    [base(t0, h0), base(t1, h0), base(t1, h1), base(t0, h1)].map((p) => vp.worldToScreen(p));
  // A flat fill (wall faces, opaque; glass, translucent).
  const band = (t0: number, t1: number, h0: number, h1: number, color: string, alpha: number): void => {
    const q = quad(t0, t1, h0, h1);
    ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath();
    ctx.fillStyle = hexToRgba(color, alpha); ctx.fill();
  };
  // Every dashed stroke dashes in step with its line (`anchoredDashes`): a
  // call's edge and a mask's repeat of it, or an edge's pieces, read as one line.
  const dashed = (segs: ReadonlyArray<readonly [Vec2, Vec2]>, on: number, off: number): void => {
    ctx.beginPath();
    for (const [p, q] of segs) for (const [a, b] of anchoredDashes(p, q, on, off)) { ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); }
    ctx.setLineDash([on, off]); ctx.lineDashOffset = 0; ctx.stroke(); ctx.setLineDash([]);
  };
  const sides = (q: readonly Vec2[]): Array<[Vec2, Vec2]> => q.map((p, i) => [p, q[(i + 1) % q.length]]);
  // Geometry in elevation: an OPAQUE lightened fill plus the dashed outline. Opaque
  // is what makes the painter's ordering read as real occlusion — a heater behind a
  // heater wall is hidden by it, exactly as it would be on site.
  const elev = (t0: number, t1: number, h0: number, h1: number, color: string): void => {
    const q = quad(t0, t1, h0, h1);
    ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath();
    ctx.fillStyle = lighten(color, 0.45);
    ctx.fill();
    ctx.strokeStyle = darken(color, 0.6); ctx.lineWidth = 1; dashed(sides(q), 4, 2);
  };
  // A dashed outline rectangle: geometry seen in elevation beyond the cut plane.
  const outline = (t0: number, t1: number, h0: number, h1: number, color: string): void => {
    ctx.strokeStyle = darken(color, 0.6); ctx.lineWidth = 1; dashed(sides(quad(t0, t1, h0, h1)), 4, 2);
  };
  // A glass pane in the elevation: a light, more-transparent fill (so anything
  // projected behind it still reads through) with the same crisp outline as the
  // plan glazing. Fainter than the plan fill because in section the glass is often
  // stacked over the wall face behind it.
  const glass = (t0: number, t1: number, h0: number, h1: number, color?: string): void => {
    const q = quad(t0, t1, h0, h1);
    ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath();
    ctx.fillStyle = color ? hexToRgba(color, 0.22) : SECTION_GLASS_FILL; ctx.fill();
    ctx.strokeStyle = glassStroke({ color }); ctx.lineWidth = GLASS_STROKE_WIDTH; ctx.setLineDash([]); ctx.stroke();
  };
  // A window opening: an UNFILLED frame — whatever is projected behind the wall
  // shows through — with a crisp solid outline (plus sill/head lines) so it reads
  // as a window rather than a plain void.
  const windowFrame = (t0: number, t1: number, h0: number, h1: number, color: string): void => {
    const q = quad(t0, t1, h0, h1);
    ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath();
    ctx.strokeStyle = darken(color, 0.55); ctx.lineWidth = 1.3; ctx.setLineDash([]); ctx.stroke();
  };
  // A single vertical edge (a room corner projected in elevation) at distance `t`.
  const vedge = (t: number, h0: number, h1: number, color: string): void => {
    ctx.strokeStyle = darken(color, 0.5); ctx.lineWidth = 1; dashed([[vp.worldToScreen(base(t, h0)), vp.worldToScreen(base(t, h1))]], 5, 3);
  };
  // A closed polygon in one of the looks above (a shaped ceiling's pieces and
  // what stands under it). A roof piece is stroked in its own fill colour: it
  // hides the antialias seams between neighbouring pieces, which is safe only
  // because every piece is convex (no bridge edges) and degenerate ones are culled.
  const trace = (pts: readonly SecPt[], close: boolean): Vec2[] => {
    const q = pts.map((p) => vp.worldToScreen(base(p.t, p.h)));
    ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    if (close) ctx.closePath();
    return q;
  };
  // The ring is filled whole; only its edges that are no clip edge (`x`) are stroked.
  const ownSides = (pts: readonly SecPt[], q: readonly Vec2[]): Array<[Vec2, Vec2]> => {
    const all = !pts.some((p) => p.x);
    return sides(q).filter((_, i) => all || !pts[i].x);
  };
  const edges = (pts: readonly SecPt[], q: readonly Vec2[]): void => {
    const first = pts.findIndex((p) => p.x);
    if (first < 0) return;
    ctx.beginPath();
    let open = false;
    for (let k = 1; k <= pts.length; k++) {
      const i = (first + k) % pts.length;
      if (pts[i].x) { open = false; continue; }
      if (!open) { ctx.moveTo(q[i].x, q[i].y); open = true; }
      const j = (i + 1) % pts.length;
      ctx.lineTo(q[j].x, q[j].y);
    }
  };
  const poly = (pts: readonly SecPt[], style: PolyStyle, color: string, alpha = 1): void => {
    if (pts.length < 3) return;
    const q = trace(pts, true);
    ctx.setLineDash([]);
    if (style === 'cut' || style === 'cut-hatched') {
      ctx.fillStyle = style === 'cut' ? color : lighten(color, 0.78); ctx.fill();
      edges(pts, q);
      ctx.strokeStyle = darken(color, 0.55); ctx.lineWidth = 1.3; ctx.stroke();
      if (style === 'cut-hatched') hatch(ctx, q, color);
    } else if (style === 'band') {
      ctx.fillStyle = hexToRgba(color, alpha); ctx.fill();
    } else if (style === 'elev') {
      const f = lighten(color, 0.45);
      ctx.fillStyle = f; ctx.fill();
      // Its unstroked edges are seams with a face of its own: painted over, as a roof's.
      if (pts.some((p) => p.x)) {
        ctx.beginPath();
        sides(q).forEach(([a, b], i) => { if (pts[i].x) { ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); } });
        ctx.strokeStyle = f; ctx.lineWidth = 1; ctx.stroke();
      }
      ctx.strokeStyle = darken(color, 0.6); ctx.lineWidth = 1; dashed(ownSides(pts, q), 4, 2);
    } else if (style === 'roof') {
      const f = lighten(color, 0.45);
      ctx.fillStyle = f; ctx.fill(); ctx.strokeStyle = f; ctx.lineWidth = 1; ctx.stroke();
    } else if (style === 'glass') {
      ctx.fillStyle = color ? hexToRgba(color, 0.22) : SECTION_GLASS_FILL; ctx.fill();
      edges(pts, q);
      ctx.strokeStyle = glassStroke({ color: color || undefined }); ctx.lineWidth = GLASS_STROKE_WIDTH; ctx.stroke();
    } else if (style === 'window') {
      edges(pts, q);
      ctx.strokeStyle = darken(color, 0.55); ctx.lineWidth = 1.3; ctx.stroke();
    } else {
      ctx.strokeStyle = darken(color, 0.6); ctx.lineWidth = 1; dashed(ownSides(pts, q), 4, 2);
    }
  };
  const line = (pts: readonly SecPt[], color: string, dash: boolean): void => {
    if (pts.length < 2) return;
    const q = trace(pts, false);
    ctx.strokeStyle = darken(color, dash ? 0.6 : 0.55); ctx.lineWidth = dash ? 1 : 1.3;
    if (dash) dashed(sides(q).slice(0, -1), 4, 2);
    else { ctx.setLineDash([]); ctx.stroke(); }
  };
  const api: SecDraw = { src: () => {}, profile, band, elev, outline, vedge, glass, window: windowFrame, poly, line };
  const boxed = boxing(api, marks?.selected ?? []);

  emitSectionGeometry(sec, instances, walls, len, col, boxed.api, polygons, true, pipes);

  // The lights' beams lie in the plane, so over all that stands beyond it: each
  // fades from its light to nothing; its light is painted again over it.
  if (marks?.beams !== false && marks?.lights !== false) {
    for (const b of sectionLightBeams(sec, instances, walls, polygons, marks?.cones ?? BEAM_DEFAULTS)) {
      const q = b.fan.map((p) => vp.worldToScreen(base(p.t, p.h)));
      const c = q[0], r = Math.max(1, b.cone.length * vp.scale);
      const g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, r);
      g.addColorStop(0, `rgba(${LIGHT_BEAM_RGB}, 0.92)`);
      g.addColorStop(0.25, `rgba(${LIGHT_BEAM_RGB}, 0.62)`);
      g.addColorStop(1, `rgba(${LIGHT_BEAM_RGB}, 0)`);
      ctx.beginPath(); q.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath();
      ctx.fillStyle = g; ctx.fill();
      const w = b.width / 2, hh = (b.lens ?? STRIP_HEIGHT) / 2;
      profile(b.apex.t - w, b.apex.t + w, b.apex.h - hh, b.apex.h + hh, true, LIGHT_FIXTURE_COLOR);
    }
  }
  // A fibre kit's arcs, over the ceiling: dashed.
  ctx.save();
  ctx.strokeStyle = LIGHT_AMBER; ctx.lineWidth = 1; ctx.setLineDash([5, 4]);
  for (const a of marks?.lights === false ? [] : sectionFibreArcs(sec, instances)) {
    ctx.beginPath();
    a.pts.map((p) => vp.worldToScreen(base(p.t, p.h))).forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.stroke();
  }
  ctx.restore();

  // The section title (A–A), placed in the view frame so it follows the drawing
  // when it is moved or rotated.
  const titleAt = label ? sectionTitlePos(sec, len, ext) : null;
  if (titleAt) {
    const box = paintViewTitle(ctx, vp.worldToScreen(base(titleAt.t, titleAt.h)), `${label}–${label}`, col, marks?.titleSelected);
    marks?.onTitle?.(box);
  }

  // When selected, outline the drawing's bounding box, show a rotate handle, and
  // square handles at the two line endpoints for reshaping the cut.
  if (selected) {
    const c = extentQuad(view, ext).map((p) => vp.worldToScreen(p));
    ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    ctx.beginPath();
    c.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath(); ctx.stroke();
    ctx.setLineDash([]);
    const topMid = vp.worldToScreen(base((ext.t0 + ext.t1) / 2, ext.h1));
    const rot = rotateHandleAt(vp, view, ext);
    ctx.beginPath(); ctx.moveTo(topMid.x, topMid.y); ctx.lineTo(rot.x, rot.y); ctx.stroke();
    ctx.beginPath(); ctx.arc(rot.x, rot.y, 5, 0, Math.PI * 2);
    ctx.fillStyle = '#fff'; ctx.fill(); ctx.strokeStyle = col; ctx.lineWidth = 1.6; ctx.stroke();
    drawGrips(ctx, [A, B]); // draggable endpoints of the cut line
  } else if (marks?.drilled) {
    // Drilled in: the view's own frame, dashed, so its edge — where a click goes
    // back to the section — shows.
    const c = extentQuad(view, ext).map((p) => vp.worldToScreen(p));
    ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    ctx.beginPath();
    c.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath(); ctx.stroke();
    ctx.setLineDash([]);
  }
  // Each selected object's box round its own pieces in this view.
  const pad = SECTION_BOX_PAD_PX / (vp.scale || 1);
  for (const b of boxed.boxes) {
    if (!b) continue;
    const c = sectionBoxRing(sec, b, pad).map((p) => vp.worldToScreen(p));
    ctx.strokeStyle = SECTION_SELECT_COLOR; ctx.lineWidth = 1.5; ctx.setLineDash([5, 3]);
    ctx.beginPath();
    c.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
    ctx.closePath(); ctx.stroke();
    ctx.setLineDash([]);
  }
  ctx.restore();
}

/** Section title (A–A) centred below the drawing. */
/** A walled room whose section should show walls + benches, not a solid box. */
function isRoomInstance(inst: ProductInstance): boolean {
  return traitsOf(inst.category).room || !!inst.autoBenches || (!!inst.polygon && inst.polygon.length >= 3);
}

/** Interior (clear) footprint of a room as a world polygon. */
function innerFootprint(inst: ProductInstance): Vec2[] {
  const inner = polyInner(inst);
  if (inner && inner.length >= 3) return inner.map((p) => instanceToWorld(inst, p));
  const wt = wallThicknessOf(inst);
  const hw = inst.width / 2 - wt, hd = inst.depth / 2 - wt;
  if (hw <= 0 || hd <= 0) return [];
  return [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }].map((p) => instanceToWorld(inst, p));
}

type ProfileFn = (t0: number, t1: number, h0: number, h1: number, filled: boolean, color: string) => void;
/** A point of a generated section in its own frame: `t` along the cut, `h` up.
 *  `s`: a SAMPLE, not a vertex (never a snap point); `c`: the chord to the next
 *  point is part of a curve (its midpoint is not a snap point); `x`: the edge to
 *  the next point is a CLIP edge — where a nearer face of the same object takes
 *  over (`occludeFaces`) —, never stroked, no DXF line, no snap run; `r`: it
 *  REPEATS a stretch of an edge its call draws whole (a mask's, see
 *  `occludeFaces`), stroked and written where that one is hidden, no snap run. */
export interface SecPt { t: number; h: number; s?: boolean; c?: boolean; x?: boolean; r?: boolean }
/** How a `poly` is painted: `cut` a solid cut face, `cut-hatched` a hatched one,
 *  `band` a flat fill of the given opacity, `elev` geometry beyond the plane,
 *  `roof` a roof piece beyond it (opaque, seams hidden), `glass` / `window` /
 *  `outline` as their quads. */
export type PolyStyle =
  | 'band'
  | 'cut'
  | 'cut-hatched'
  | 'elev'
  | 'glass'
  | 'outline'
  | 'roof'
  | 'window';
/** What a section's draw calls show: the drawing object — a room, a part, a
 *  drawn wall, a shape — and, for a room's feature, which one (a bench's band
 *  with its frame, a bench support's trestles, a pane, an opening, a door, a
 *  partition, a heater wall; a drawn wall's door, pane or opening, by the wall
 *  drawn over it). A feature with no id yet (one minted on demand) is its
 *  object's. */
export interface SecSrc { id: string; part?: SecPart }
/** A room's feature a section piece shows: `fixture` is a backrest or a light
 *  (`ProductInstance.backrests` / `lights`), `support` a bench support's
 *  trestle — `bench` the bench it stands under (none under a rounded corner's
 *  piece), whose box and pick take it too (`srcMatches`, roomFeatures.ts
 *  `partPick`). roomFeatures.ts maps each kind to the selection's. */
export interface SecPart {
  kind:
    | 'bench'
    | 'door'
    | 'fixture'
    | 'heaterWall'
    | 'opening'
    | 'pane'
    | 'partition'
    | 'support'
    | 'wallFeature';
  id: string;
  bench?: string;
}
/** The source of an object's feature, or the object's own when it has no id. */
export function srcOf(id: string, kind?: SecPart['kind'], partId?: string): SecSrc {
  return kind && partId ? { id, part: { kind, id: partId } } : { id };
}
/** The source of a bench support's trestle, under bench `bench` (none under a
 *  rounded corner's piece or on a standalone bench). */
export function supportSrc(id: string, support: string, bench?: string): SecSrc {
  return { id, part: { kind: 'support', id: support, ...(bench ? { bench } : {}) } };
}
export interface SecDraw {
  /** The source of the calls that follow (`SecSrc`) — set by every emitter
   *  before it draws, carried through the depth replay, the recorded events
   *  and the self-occlusion. */
  src: (s: SecSrc | null) => void;
  profile: ProfileFn;
  band: (t0: number, t1: number, h0: number, h1: number, color: string, alpha: number) => void;
  /** Geometry seen BEYOND the cut plane, in elevation. Drawn OPAQUE (a lightened
   *  tone plus a dashed outline) so that, combined with the farthest-first draw
   *  order, nearer geometry genuinely hides what stands behind it. A translucent
   *  fill here would let everything show through and the section would read flat. */
  elev: (t0: number, t1: number, h0: number, h1: number, color: string) => void;
  outline: (t0: number, t1: number, h0: number, h1: number, color: string) => void;
  vedge: (t: number, h0: number, h1: number, color: string) => void;
  glass: (t0: number, t1: number, h0: number, h1: number, color?: string) => void;
  window: (t0: number, t1: number, h0: number, h1: number, color: string) => void;
  /** A closed polygon (a shaped ceiling's pieces, and what stands under it). */
  poly: (pts: readonly SecPt[], style: PolyStyle, color: string, alpha?: number) => void;
  /** An open polyline (a roof's edges and creases). */
  line: (pts: readonly SecPt[], color: string, dashed: boolean) => void;
}

/** How far a polygon's NEAREST point stands in front of the cut plane, along the
 *  view direction. Geometry the plane cuts reaches <= 0, so it sorts to the front. */
function nearDepthOf(sec: Pick<SectionLine, 'a' | 'b' | 'side'>, poly: Vec2[]): number {
  const { n } = sectionFrame(sec);
  let mn = Infinity;
  for (const p of poly) mn = Math.min(mn, (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y);
  return mn;
}

/** Depth at the cut plane itself — cut faces are frontmost and paint last. */
const CUT_DEPTH = 0;

/** How opaque a part model's water is where the plane cuts it, and beyond it. */
const WATER_CUT = 0.5;
const WATER_SEEN = 0.3;

/** Just behind the cut plane — see `beyondDepthOf`. */
const BEYOND_DEPTH = 1e-6;

/** Depth for what the plane cuts AND stands beyond it: its nearest point is ON
 *  the plane, and tagged there it would tie with every other object's cut face —
 *  painting over the ones emitted before it. Strictly behind them all instead. */
function beyondDepthOf(sec: Pick<SectionLine, 'a' | 'b' | 'side'>, poly: Vec2[]): number {
  return Math.max(nearDepthOf(sec, poly), BEYOND_DEPTH);
}

/** Where the calls tagged with a depth stand in plan: a footprint's ring (or
 *  rings) in the world — `wall` for a drawn wall's piece —, or, for the pieces
 *  of one of its object's FACES, that face's segment (`SecFace['ends']`) and,
 *  for a wall's face, its wall's inner line (`inner`, the same ends moved
 *  through the wall's thickness). */
export type SecPlan = { ring: readonly Vec2[]; wall?: true } | { rings: readonly (readonly Vec2[])[] } | { face: SecFace['ends']; inner?: SecFace['ends'] };

export interface DepthOrderedDraw extends SecDraw {
  /** Depth to tag subsequent draw calls with, and where they stand in plan. */
  at(depth: number, plan?: SecPlan): void;
  /** The object the calls that follow belong to (a number per object). */
  of(obj: number): void;
  /** Replay every recorded call in paint order (`paintOrder`), or — not
   *  `ordered` — farthest tag first, then clear. */
  flush(): void;
}

/** One `OrderKey` for every flush: its buffer is reused. */
const ORDER_KEY = new OrderKey();

/** The calls of one plan as `depthOrdered` records them: the plan (`src`) and,
 *  once its order is asked for, where it stands in `ORDER_KEY` (`plan`). */
interface SecGroup extends PaintGroup<number> { src: SecPlan }

/** A plan as `ORDER_KEY` holds it from `at` on (a code, then (t, d) pairs):
 *  a face's segment (−1), with its wall's inner line (−2: the segment, the
 *  wall as a ring and the inner line — `PlanRings`); one ring of n points
 *  (n); m rings (−3 − m), each its n, then its points. */
function keyPlan(at: number): PlanRings {
  const v = ORDER_KEY.v, c = v[at];
  if (c === -1) return [v.subarray(at + 1, at + 5)];
  if (c === -2) return [v.subarray(at + 1, at + 5), Float64Array.of(v[at + 1], v[at + 2], v[at + 3], v[at + 4], v[at + 7], v[at + 8], v[at + 5], v[at + 6]), v.subarray(at + 5, at + 9)];
  if (c >= 0) return [v.subarray(at + 1, at + 1 + 2 * c)];
  const out: Float64Array[] = [];
  for (let r = 0, k = at + 1; r < -3 - c; r++) { const m = v[k]; out.push(v.subarray(k + 1, k + 1 + 2 * m)); k += 1 + 2 * m; }
  return out;
}

/**
 * Records each draw call with the depth set by `at()` and replays them
 * farthest-first, so nearer geometry paints over — and therefore hides — what
 * lies behind it. Recording per CALL (rather than per object) is what lets one
 * room's own pieces interleave correctly with other objects: a heater standing
 * between two benches is occluded by the nearer bench and occludes the farther.
 * Where an object's face or a drawn wall's piece and another object's call
 * share area in the view, their plans decide which is nearer over the columns
 * they share (`paintOrder`): one depth per call cannot. That order is kept per
 * section under a key of everything it reads (`rememberedOrder`), so the
 * replays of one frame work it out once; a collector whose result does not
 * depend on the order (`ordered` false: the extent, the snap vertices) takes
 * the tags' and pays nothing for it.
 */
function depthOrdered(api: SecDraw, sec: SectionLine, ordered = true): DepthOrderedDraw {
  let q: PaintCall[] = [], groups: SecGroup[] = [];
  let cur = CUT_DEPTH, g = -1, obj = -1, seq = 0, last: SecPlan | undefined, first = -1, special = false, many = false;
  let src: SecSrc | null = null;
  const rec = (k: SecKind, a: unknown[]): void => {
    q.push(src ? { k, a, d: cur, i: seq++, g, src } : { k, a, d: cur, i: seq++, g });
    if (g >= 0) { const gr = groups[g]; if (cur < gr.z0) gr.z0 = cur; if (cur > gr.z1) gr.z1 = cur; }
  };
  const { u, n } = sectionFrame(sec);
  const ax = sec.a.x, ay = sec.a.y, ux = u.x, uy = u.y, nx = n.x, ny = n.y;
  /** Ring r into the key's numbers v from k as its n and its (t, d) pairs,
   *  widening the group's span: where the next starts. */
  const putRing = (v: Float64Array, k: number, r: readonly Vec2[], gr: SecGroup): number => {
    v[k++] = r.length;
    for (let m = 0; m < r.length; m++) {
      const x = r[m].x - ax, y = r[m].y - ay, t = x * ux + y * uy, d = x * nx + y * ny;
      v[k++] = t; v[k++] = d;
      if (t < gr.t0) gr.t0 = t;
      if (t > gr.t1) gr.t1 = t;
      if (d < gr.d0) gr.d0 = d;
      if (d > gr.d1) gr.d1 = d;
    }
    return k;
  };
  /** Everything the order reads, as `ORDER_KEY`: the section, each group's
   *  object, flags and plan — in the section's frame, which is all the order
   *  reads of it, the group's `plan` its place and a footprint's span found on
   *  the way —, and the calls (`OrderKey.call`). */
  const orderKey = (): OrderKey => {
    const key = ORDER_KEY;
    key.n = 0;
    key.put(sec.a.x); key.put(sec.a.y); key.put(sec.b.x); key.put(sec.b.y); key.put(sec.side); key.put(groups.length); key.put(q.length);
    for (const gr of groups) {
      const p = gr.src;
      if ('face' in p) {
        const v = key.room(p.inner ? 11 : 7), [a, b] = p.face;
        let k = key.n;
        v[k++] = gr.o; v[k++] = 1; gr.plan = k; v[k++] = p.inner ? -2 : -1;
        v[k++] = a.t; v[k++] = a.d; v[k++] = b.t; v[k++] = b.d;
        if (p.inner) { const [c, d] = p.inner; v[k++] = c.t; v[k++] = c.d; v[k++] = d.t; v[k++] = d.d; }
        key.n = k;
        continue;
      }
      gr.t0 = gr.d0 = Infinity; gr.t1 = gr.d1 = -Infinity;
      if ('ring' in p) {
        const v = key.room(3 + 2 * p.ring.length);
        let k = key.n;
        v[k++] = gr.o; v[k++] = gr.wall ? 2 : 0; gr.plan = k;
        key.n = putRing(v, k, p.ring, gr);
        continue;
      }
      let m = 3;
      for (const r of p.rings) m += 1 + 2 * r.length;
      const v = key.room(m);
      let k = key.n;
      v[k++] = gr.o; v[k++] = 0; gr.plan = k; v[k++] = -3 - p.rings.length;
      for (const r of p.rings) k = putRing(v, k, r, gr);
      key.n = k;
    }
    for (const c of q) key.call(c);
    return key;
  };
  return {
    at(depth, plan) {
      cur = depth;
      if (!ordered) return;
      if (plan === last && g >= 0) return; // a face's pieces come one after another, with its plan
      if (!plan || obj < 0) { g = -1; last = undefined; return; }
      let gr: SecGroup;
      if ('face' in plan) {
        const [a, b] = plan.face, [c, d] = plan.inner ?? plan.face;
        gr = { o: obj, face: true, wall: false, src: plan, plan: -1, t0: Math.min(a.t, b.t), t1: Math.max(a.t, b.t), d0: Math.min(a.d, b.d, c.d, d.d), d1: Math.max(a.d, b.d, c.d, d.d), z0: Infinity, z1: -Infinity };
        special = true;
      } else {
        const wall = 'ring' in plan && !!plan.wall;
        gr = { o: obj, face: false, wall, src: plan, plan: -1, t0: 0, t1: 0, d0: 0, d1: 0, z0: Infinity, z1: -Infinity };
        if (wall) special = true;
      }
      g = groups.push(gr) - 1;
      last = plan;
      if (first < 0) first = obj; else if (obj !== first) many = true;
    },
    src(s) { src = s; },
    of(o) { obj = o; g = -1; last = undefined; },
    flush() {
      // Each call after its own source.
      let shown: SecSrc | null | undefined;
      const replay = ({ k, a, src: own }: PaintCall): void => {
        const s = own ?? null;
        if (s !== shown) { api.src(s); shown = s; }
        (api[k] as (...a: unknown[]) => void)(...a);
      };
      // Farthest first; ties keep emission order so a cut face still lands
      // over its own elevation projection — but where plans say otherwise
      // (`paintOrder`; nothing to decide without a face or a wall and two objects).
      if (!ordered || !special || !many) q.sort(byTag).forEach(replay);
      else {
        const key = orderKey(), kept = rememberedOrder(sec.id, key);
        if (kept) for (let k = 0; k < kept.length; k++) replay(q[kept[k]]);
        else {
          const order = paintOrder(q, groups, keyPlan); // sorts q
          const ids = order ? order.map((k) => q[k].i) : q.map((c) => c.i);
          rememberOrder(sec.id, key, ids);
          for (let m = 0; m < ids.length; m++) replay(order ? q[order[m]] : q[m]);
        }
      }
      q = []; groups = []; g = -1; last = undefined; seq = 0; first = -1; special = many = false;
    },
    profile: (...a) => rec('profile', a),
    band: (...a) => rec('band', a),
    elev: (...a) => rec('elev', a),
    outline: (...a) => rec('outline', a),
    vedge: (...a) => rec('vedge', a),
    glass: (...a) => rec('glass', a),
    window: (...a) => rec('window', a),
    poly: (pts, ...rest) => rec('poly', [pts.map((p) => ({ ...p })), ...rest]),
    line: (pts, ...rest) => rec('line', [pts.map((p) => ({ ...p })), ...rest]),
  };
}

/** A drawing API that lifts every height by `dz` mm — used to raise a room off
 *  the floor by its height-from-ground, so the section shows it floating. */
function raiseApi(api: SecDraw, dz: number): SecDraw {
  if (dz === 0) return api;
  return {
    src: (s) => api.src(s),
    profile: (t0, t1, h0, h1, filled, color) => api.profile(t0, t1, h0 + dz, h1 + dz, filled, color),
    band: (t0, t1, h0, h1, color, alpha) => api.band(t0, t1, h0 + dz, h1 + dz, color, alpha),
    elev: (t0, t1, h0, h1, color) => api.elev(t0, t1, h0 + dz, h1 + dz, color),
    outline: (t0, t1, h0, h1, color) => api.outline(t0, t1, h0 + dz, h1 + dz, color),
    vedge: (t, h0, h1, color) => api.vedge(t, h0 + dz, h1 + dz, color),
    glass: (t0, t1, h0, h1, color) => api.glass(t0, t1, h0 + dz, h1 + dz, color),
    window: (t0, t1, h0, h1, color) => api.window(t0, t1, h0 + dz, h1 + dz, color),
    poly: (pts, style, color, alpha) => api.poly(pts.map((p) => ({ ...p, h: p.h + dz })), style, color, alpha),
    line: (pts, color, dashed) => api.line(pts.map((p) => ({ ...p, h: p.h + dz })), color, dashed),
  };
}

/** A hole punched through a cut wall: its chord span [t0,t1] and vertical extent
 *  [sill,arch]. Glass draws a translucent pane; a gap (door/opening) stays empty. */
interface WallHole { t0: number; t1: number; sill: number; arch: number; kind: 'glass' | 'gap' | 'window'; color?: string; src: SecSrc }

/** A room's panes and its outer doors' glass segments, each with its source —
 *  and, for one a glazing builds (roomGlazing.ts), the glazing's height: its run
 *  is a hole in the wall up to there, the glazing drawn apart (`glazingCalls`). */
function glazingOf(inst: ProductInstance): Array<{ seg: GlassSegment; src: SecSrc; door: boolean; glazedH: number | null }> {
  const hOf = (g: { input: { H: number } } | null): number | null => (g ? g.input.H : null);
  return [
    ...(inst.glass || []).map((seg) => ({ seg, src: srcOf(inst.id, 'pane', seg.id), door: false, glazedH: hOf(glazingOfPane(inst, seg.id)) })),
    ...doorGlassSpans(inst).map(({ seg, door }) => ({ seg, src: srcOf(inst.id, 'door', door.id), door: true, glazedH: hOf(roomDoor(inst, door)?.glazing ?? null) })),
  ];
}

/** The doors/glass/openings the section line passes through, as chord-space holes. */
function cutWallHoles(inst: ProductInstance, sec: SectionLine): WallHole[] {
  const holes: WallHole[] = [];
  const addGlass = (seg: GlassSegment, kind: 'glass' | 'gap', src: SecSrc, glazedH: number | null): void => {
    const fp = glassPolysLocal(inst, seg).flat().map((p) => instanceToWorld(inst, p));
    const ch = segmentPolyChord(sec.a, sec.b, fp);
    // A pane cut by the section is drawn from its OWN base to its OWN top and in
    // its own colour; a doorway is a walk-through gap from the floor up to the
    // door's top (its segment carries the door's height, so `glassPaneTop` of it
    // is `doorTop`; it has no `heightFromGround`, so its base is 0).
    if (!ch) return;
    if (glazedH != null) holes.push({ t0: ch[0], t1: ch[1], sill: 0, arch: glazedH, kind: 'gap', src });
    else holes.push({ t0: ch[0], t1: ch[1], sill: kind === 'glass' ? glassPaneBase(seg) : 0, arch: glassPaneTop(inst, seg), kind, color: seg.color, src });
  };
  // Doorways are walk-through gaps, not panes; so is a glazing's run.
  for (const g of glazingOf(inst)) addGlass(g.seg, g.door ? 'gap' : 'glass', g.src, g.glazedH);
  for (const o of inst.openings || []) {
    const fp = openingHoleBand(inst, o).map((p) => instanceToWorld(inst, p));
    const ch = segmentPolyChord(sec.a, sec.b, fp);
    // A part's hole (`roomWallCuts`: a display set into the wall) is a plain gap.
    if (ch) holes.push({ t0: ch[0], t1: ch[1], sill: openingSill(o), arch: openingArchitrave(inst, o), kind: isPartCut(o) ? 'gap' : 'window', src: openingSrc(inst, o) });
  }
  return holes;
}

/** What an opening shows in a section: the room's opening, or — a part's hole
 *  (`roomWallCuts`) — the part set into it. */
function openingSrc(inst: ProductInstance, o: GlassSegment): SecSrc {
  return isPartCut(o) ? srcOf(o.id!.slice(PART_CUT_PREFIX.length)) : srcOf(inst.id, 'opening', o.id);
}

/** Draw one cut wall over chord span [wa,wb] as a solid band, punching out any
 *  door/glass/opening holes (piers left/right, sill below, lintel above). The
 *  wall is the room's (`room`), a pane or an opening its own. */
function drawCutWall(api: SecDraw, wa: number, wb: number, wallH: number, wallCol: string, holes: WallHole[], room: SecSrc): void {
  const clipped = holes
    .map((h) => ({ ...h, t0: Math.max(wa, Math.min(wb, h.t0)), t1: Math.max(wa, Math.min(wb, h.t1)), arch: Math.min(h.arch, wallH) }))
    .filter((h) => h.t1 - h.t0 > 1e-3)
    .sort((a, b) => a.t0 - b.t0);
  if (!clipped.length) { api.profile(wa, wb, 0, wallH, true, wallCol); return; }
  const rect = (t0: number, t1: number, h0: number, h1: number): SecPt[] => [{ t: t0, h: h0 }, { t: t1, h: h0 }, { t: t1, h: h1 }, { t: t0, h: h1 }];
  const solid: SecPt[][] = [];
  const panes: Array<() => void> = [];
  let cur = wa;
  for (const h of clipped) {
    if (h.t0 > cur + 1e-3) solid.push(rect(cur, h.t0, 0, wallH)); // pier before the hole
    const s0 = Math.max(cur, h.t0), s1 = Math.max(s0, h.t1);
    if (s1 > s0 + 1e-3) {
      if (h.sill > 0) solid.push(rect(s0, s1, 0, h.sill)); // solid below the sill
      if (h.arch < wallH) solid.push(rect(s0, s1, h.arch, wallH)); // lintel above
      if (h.kind !== 'gap') {
        panes.push(() => {
          api.src(h.src);
          if (h.kind === 'glass') api.glass(s0, s1, h.sill, h.arch, h.color); // same colour + line thickness as the plan
          else api.window(s0, s1, h.sill, h.arch, wallCol); // see-through opening + frame
          api.src(room);
        });
      }
    }
    cur = Math.max(cur, h.t1);
  }
  if (cur < wb - 1e-3) solid.push(rect(cur, wb, 0, wallH));
  for (const r of seamless(solid)) emitPoly(api, r, 'cut', wallCol);
  for (const p of panes) p();
}

/** Pieces that tile ONE solid, each edge (or stretch of one) a piece shares
 *  with another flagged a clip edge (`x`), so they fill and stroke as one
 *  outline: a pane is a few pixels thick, and the lintel over it, stroked on
 *  every side, read as a dark strip instead of the wall it is. */
function seamless(pieces: readonly SecPt[][]): SecPt[][] {
  const EPS = 1e-3;
  return pieces.map((ring, k) => {
    const others = pieces.filter((_, j) => j !== k);
    const out: SecPt[] = [];
    ring.forEach((p, i) => {
      const q = ring[(i + 1) % ring.length];
      const dt = q.t - p.t, dh = q.h - p.h, L2 = dt * dt + dh * dh;
      if (L2 < 1e-12) return;
      const L = Math.sqrt(L2);
      const cuts = [0, 1];
      for (const o of others) for (const v of o) {
        const u = ((v.t - p.t) * dt + (v.h - p.h) * dh) / L2;
        if (u > 1e-9 && u < 1 - 1e-9 && Math.abs((v.t - p.t) * dh - (v.h - p.h) * dt) / L < 1e-6) cuts.push(u);
      }
      cuts.sort((a, b) => a - b);
      for (let c = 0; c + 1 < cuts.length; c++) {
        const u0 = cuts[c], u1 = cuts[c + 1];
        if (u1 - u0 < 1e-9) continue;
        const m = { t: p.t + dt * (u0 + u1) / 2, h: p.h + dh * (u0 + u1) / 2 };
        const nt = (-dh / L) * EPS, nh = (dt / L) * EPS;
        const outward = insideRing({ t: m.t + nt, h: m.h + nh }, ring) ? { t: m.t - nt, h: m.h - nh } : { t: m.t + nt, h: m.h + nh };
        const shared = others.some((o) => insideRing(outward, o));
        const at: SecPt = c === 0 ? { ...p } : { t: p.t + dt * u0, h: p.h + dh * u0 };
        if (shared) at.x = true; else delete at.x;
        out.push(at);
      }
    });
    return out;
  });
}

/** A room bench as a world polygon, its seat height and colour — resolved from
 *  the polygonal-room bands or the rectangular-room tier boxes (+ inner walls),
 *  so the section shows benches for standard and polygonal rooms alike. */
// `holes`: rings cut clean through a bench piece by what stands in its room
// (benchSolids.ts `carvedSeat`); a piece carved apart is already several pieces.
// `underCap` (a shaped ceiling only): the band rises to the ceiling's underside,
// capped at this height — its top under a ceiling out of reach (Infinity for a
// partition with no height of its own, `innerWallTopAt(iw, Infinity)`), so
// min(cap, underside) is exactly its top under the real one: a partition with no
// height follows the ceiling, anything taller is cut by it.
interface SecBench { poly: Vec2[]; holes?: Vec2[][]; base: number; height: number; color: string; underCap?: number; sweep?: SecSweep; src: SecSrc }
/** A steam bench's profile swept along it (world plan): `quad` for a straight
 *  band (`[backStart, backEnd, frontEnd, frontStart]`), whose cut is the profile;
 *  a corner piece has none and is cut through its swept faces. */
interface SecSweep { ring: BenchPt[]; width: number; pathAt: (x: number) => Vec2[]; quad?: Vec2[]; faces?: SweepFace[] }
function roomBenchBands(inst: ProductInstance, shaped = false): SecBench[] {
  const src = { benchTierHeights: inst.benchTierHeights };
  const tierCols = inst.category === 'steamroom' ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS;
  // One piece per slat (the gaps between them open, the last one the front
  // board), the plan's own slat quads, then the frame, trestles and skirting;
  // rounded-corner pieces sit in the same height band as the tier they join, and
  // a partition's benches are built the same way.
  const benches = roomBenchSolids(inst, src).map((b) => {
    const own = b.kind === 'band' ? b.color ?? benchColorOf(inst, b) : b.color ?? tierCols[b.level];
    return {
    poly: b.poly.map((p) => instanceToWorld(inst, p)),
    base: b.base,
    height: b.top,
    ...(b.holes ? { holes: b.holes.map((h) => h.map((p) => instanceToWorld(inst, p))) } : {}),
    color: b.part === 'frame' || b.part === 'support' ? structureColor(own) : own,
    sweep: b.sweep ? benchSweep(b.sweep.profile, (x) => b.sweep!.pathAt(x).map((p) => instanceToWorld(inst, p)), b.sweep.quad?.map((p) => instanceToWorld(inst, p))) : undefined,
    // A band's slats and frame are its bench, a support's trestle the support
    // (under its bench); a rounded corner's piece is the room's (as in the
    // plan and in 3D).
    src: b.part === 'support' && b.support ? supportSrc(inst.id, b.support, b.kind === 'band' ? b.id : undefined)
      : b.kind === 'band' ? srcOf(inst.id, 'bench', b.id) : srcOf(inst.id),
    };
  });
  // Sculpted benches: their faces, seen and cut as a swept bench's corner piece
  // is (no straight band: the cut is read off the faces).
  const turn = (inst.rotation * Math.PI) / 180, tc = Math.cos(turn), ts = Math.sin(turn);
  const rotateNormal = (n: Vec3): Vec3 => ({ x: tc * n.x - ts * n.y, y: ts * n.x + tc * n.y, z: n.z });
  const sculpted = sculptedBenchesOf(inst).flatMap((sb): SecBench[] => {
    const lay = sculptLayout(inst, sb);
    if (!lay) return [];
    const W = (p: { x: number; y: number }): Vec2 => instanceToWorld(inst, p);
    const faces = lay.faces.map((f) => ({ ...f, pts: f.pts.map((p) => ({ ...W(p), z: p.z })), normal: rotateNormal(f.normal) }));
    const span = sculptSpan(lay);
    const src = srcOf(inst.id, 'fixture', sb.id), timber = structureColor(sculptColor(inst, sb));
    // Its trestles, as a bench's are drawn (sculptTrestles.ts).
    const trestles = sculptTrestles(inst, sb).flatMap((t) => t.solids.map((b): SecBench => ({
      poly: b.poly.map(W), base: b.base, height: b.top, color: timber,
      sweep: b.sweep ? benchSweep(b.sweep.profile, (x) => b.sweep!.pathAt(x).map(W), b.sweep.quad?.map(W)) : undefined,
      src,
    })));
    return [{
      poly: lay.outline.map(W), base: span.z0, height: span.z1, color: sculptColor(inst, sb),
      sweep: { ring: [], width: lay.extent.out[1], pathAt: () => [], faces },
      src,
    }, ...trestles];
  });
  // Hidden heater walls: same shape as a bench block (world polygon + height
  // band + colour), just floating at their own height above the floor.
  const heaters = heaterWallBands(inst).map((hb) => ({
    poly: hb.poly.map((p) => instanceToWorld(inst, p)),
    base: heaterWallBase(hb.wall),
    height: heaterWallTop(hb.wall),
    color: hb.wall.color,
    ...(shaped ? { underCap: heaterWallTop(hb.wall) } : {}),
    src: srcOf(inst.id, 'heaterWall', hb.wall.id),
  }));
  // Interior partitions: wall-like blocks (doors and panes cut out of the fill),
  // standing in their own height band; over a door its header, below and above
  // a pane its own band — the very pieces 3D builds (roomFeaturePieces.ts).
  const featureBands = roomFeaturePieces(inst).filter((p) => p.role === 'partitionBand');
  const parts = (inst.innerWalls || []).flatMap((iw): SecBench[] => {
    if (iw.edge == null) return [];
    const b0 = innerWallBase(iw), b1 = innerWallTop(inst, iw), cap = innerWallTopAt(iw, Infinity);
    const color = iw.color ?? wallColorOf(inst), part = srcOf(inst.id, 'partition', iw.id);
    const band = (q: Vec2[], base: number, top: number, under: number): SecBench => ({
      poly: q.map((p) => instanceToWorld(inst, p)), base, height: top, color, ...(shaped ? { underCap: under } : {}), src: part,
    });
    const out = polyInnerWallFill(inst, iw).filter((q) => q.length >= 3).map((q) => band(q, b0, b1, cap));
    // A band's top is its cap under a shaped ceiling (min(cap, underside) there).
    for (const p of featureBands) if (p.stuff.kind === 'partition' && p.stuff.iw === iw) out.push(band(p.ring, p.z0, shaped ? Math.min(p.z1, b1) : p.z1, p.z1));
    // Under a shaped ceiling a band rises to min(cap, underside), where an empty
    // one draws nothing; a flat one is dropped here.
    return out.filter((b) => (shaped ? b.underCap! - b.base : b.height - b.base) > 1e-6);
  });
  // Backrests: each board and spacer a block in its own height band.
  const backrests = backrestsOf(inst).flatMap((br) => {
    const col = backrestColor(inst, br);
    return backrestSolids(inst, br).map((b): SecBench => ({
      poly: b.poly.map((p) => instanceToWorld(inst, p)),
      base: b.base,
      height: b.top,
      color: b.part === 'spacer' ? structureColor(col) : col,
      src: srcOf(inst.id, 'fixture', br.id),
    }));
  });
  // The floor grille: each batten and top slat a block (floorGrille.ts).
  const grille = grilleLayout(inst), grilleCol = grilleColor(inst);
  const grillePieces = grille ? [...grille.battens, ...grille.slats].map((p): SecBench => ({
    poly: p.ring.outer.map((q) => instanceToWorld(inst, q)),
    base: p.z0,
    height: p.z1,
    color: p.kind === 'batten' ? structureColor(grilleCol) : grilleCol,
    src: srcOf(inst.id),
  })) : [];
  // Strip lights: a small block along each strip (lights.ts).
  const strips = stripSolids(inst).map((b): SecBench => ({
    poly: b.poly.map((p) => instanceToWorld(inst, p)),
    base: b.base,
    height: b.top,
    color: b.part === 'can' ? SPOT_BODY_COLOR : LIGHT_FIXTURE_COLOR,
    src: srcOf(inst.id, 'fixture', b.id),
  }));
  // Spots and fibre ends: a spot's can in the ceiling and its lens under it.
  const spots = spotSolids(inst).map((b): SecBench => ({
    poly: b.poly.map((p) => instanceToWorld(inst, p)),
    base: b.base,
    height: b.top,
    color: b.part === 'can' ? SPOT_BODY_COLOR : LIGHT_FIXTURE_COLOR,
    src: srcOf(inst.id, 'fixture', b.id),
  }));
  // A starry sky: each fibre end a tick under the ceiling.
  const fibres = skySolids(inst).map((b): SecBench => ({
    poly: b.poly.map((p) => instanceToWorld(inst, p)),
    base: b.base,
    height: b.top,
    color: LIGHT_FIXTURE_COLOR,
    src: srcOf(inst.id, 'fixture', b.id),
  }));
  // Channels: the profile in the ceiling's groove and its light at the mouth.
  const channels = channelSolids(inst).map((b): SecBench => ({
    poly: b.poly.map((p) => instanceToWorld(inst, p)),
    base: b.base,
    height: b.top,
    color: b.part === 'can' ? SPOT_BODY_COLOR : LIGHT_FIXTURE_COLOR,
    src: srcOf(inst.id, 'fixture', b.id),
  }));
  // A cove: a flat ceiling's second layer, its ledges and valances, or a Coved
  // one's ledges, in the ceiling's colour; its strip glowing.
  const cove = coveOf(inst), cg = cove ? coveGeometry(inst) : null;
  const coveParts = cove && cg ? [
    ...cg.parts.map((b): SecBench => ({ poly: b.poly.map((p) => instanceToWorld(inst, p)), base: b.base, height: b.top, color: ceilingHex6(inst, wallColorOf(inst)), src: srcOf(inst.id, 'fixture', cove.id) })),
    ...coveStripSolids(inst).map((b): SecBench => ({ poly: b.poly.map((p) => instanceToWorld(inst, p)), base: b.base, height: b.top, color: LIGHT_FIXTURE_COLOR, src: srcOf(inst.id, 'fixture', b.id) })),
  ] : [];
  return [...grillePieces, ...benches, ...sculpted, ...heaters, ...parts, ...backrests, ...strips, ...spots, ...fibres, ...channels, ...coveParts];
}

/** A fibre kit's arcs in a section, beyond the cut: from its projector part to
 *  the fibres it feeds, each point in the view's frame (t along, h above the
 *  ground); an arc that reaches behind the cut is left out. */
export function sectionFibreArcs(sec: SectionLine, instances: ProductInstance[]): Array<{ id: string; pts: BeamPt[] }> {
  const { u, n } = sectionFrame(sec);
  const parts = new Map(instances.map((i) => [i.id, i]));
  const out: Array<{ id: string; pts: BeamPt[] }> = [];
  for (const inst of instances) {
    if (!inst.lights?.length || !isPolyRoom(inst)) continue;
    for (const kit of fibreKitsOf(inst)) {
      const part = kit.projectorId ? parts.get(kit.projectorId) : undefined;
      if (!part) continue;
      for (const arc of kitArcs(inst, projectorMiddle(inst, part), kitFibres(inst, kit))) {
        const pts = arc.map(([x, y, z]) => {
          const w = instanceToWorld(inst, { x, y }), dx = w.x - sec.a.x, dy = w.y - sec.a.y;
          return { t: dx * u.x + dy * u.y, d: dx * n.x + dy * n.y, h: z + roomBase(inst) };
        });
        if (pts.every((p) => p.d >= 0)) out.push({ id: kit.id, pts: pts.map(({ t, h }) => ({ t, h })) });
      }
    }
  }
  return out;
}

/** A light's beam in a section: where the plane passes through the light, and
 *  the part of its cone it sees. */
export interface SectionBeam {
  id: string;
  apex: BeamPt;
  fan: BeamPt[];
  cone: BeamCone;
  /** How wide the plane cuts the light (mm along the view). */
  width: number;
  /** A spot's lens height (mm), painted again over its beam; a strip's is its own. */
  lens?: number;
}

/** Where a section's plane passes through the rooms' strip lights, each with its
 *  beam: one size per kind of light (`cones`), every ray stopped by the first
 *  solid the plane cuts — a wall, a bench, its skirting, a backrest, the ceiling,
 *  never a light. A strip beyond the plane gets none: it is seen, not cut. */
export function sectionLightBeams(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], cones: BeamCones = BEAM_DEFAULTS): SectionBeam[] {
  // Every ray cast against the cut's solids: kept while the section, what it cuts
  // and the beam sizes hold. Shared: never mutate it.
  return BEAMS.get(sec.id, () => [sec, instances, walls, polygons, cones], () => beamsOf(sec, instances, walls, polygons, cones));
}
const BEAMS = contentMemo<SectionBeam[]>({ max: 64, skip: HEAVY_FIELDS });

function beamsOf(sec: SectionLine, instances: ProductInstance[], walls: Wall[], polygons: Polygon[], cones: BeamCones): SectionBeam[] {
  const { u, len } = sectionFrame(sec);
  const hits: Array<{ id: string; apex: BeamPt; axis: number; width: number; cone: BeamCone; lens?: number; fill?: BeamPt[] }> = [];
  const lights = new Set<string>();
  for (const inst of instances) {
    if (!inst.lights?.length || !isPolyRoom(inst)) continue;
    for (const l of inst.lights) lights.add(l.id);
    const o = instanceToWorld(inst, { x: 0, y: 0 });
    const along = (v: Vec2): number => { const w = instanceToWorld(inst, v); return (w.x - o.x) * u.x + (w.y - o.y) * u.y; };
    // A strip's or a cove's stretch the plane crosses: its emitter, as it shines.
    // A cove's light fills the cove — a flat ceiling's recess and the wash out of
    // its opening (`coveRecess`, square to the wall, stretched as the plane crosses
    // it), or under a Coved ceiling a strip's own spread, as far as the curve.
    const cg = coveOf(inst) ? coveGeometry(inst) : null;
    const recess = cg ? coveRecess(inst) : null;
    const rise = cg?.kind === 'springing' && cg.runs.length ? ceilingUnder(inst, { x: 0, y: 0 }).z - cg.runs[0].z : 0;
    for (const e of lightEmitters(inst, cones)) {
      if (e.kind !== 'line' || e.role === 'channel') continue;
      const a = instanceToWorld(inst, { x: e.a[0], y: e.a[1] }), b = instanceToWorld(inst, { x: e.b[0], y: e.b[1] });
      const s = segParam(sec.a, sec.b, a, b);
      if (s == null) continue;
      const d = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const across = Math.abs(((b.x - a.x) * u.y - (b.y - a.y) * u.x) / d);
      const apex = { t: s * len, h: e.a[2] }, axis = Math.atan2(e.aim[2], along({ x: e.aim[0], y: e.aim[1] }));
      if (e.role === 'strip') { hits.push({ id: e.id, apex, axis, width: STRIP_WIDTH / Math.max(0.25, across), cone: e.beam }); continue; }
      const toward = along(e.lean), k1 = 1 / (Math.sign(toward || 1) * Math.max(0.25, Math.abs(toward)));
      const fill = recess?.map((q) => ({ t: apex.t + (q.d - recess[0].d) * k1, h: q.z }));
      const reach = fill ? Math.max(...fill.map((q) => Math.hypot(q.t - apex.t, q.h - apex.h))) * 1.1 : Math.max(e.beam.length, 1.5 * rise);
      hits.push({ id: e.id, apex, axis, width: STRIP_WIDTH / Math.max(0.25, across), cone: { length: reach, angle: e.beam.angle }, ...(fill ? { fill } : {}) });
    }
    // A channel the plane crosses: its beam from the groove's mouth, square to the
    // ceiling there.
    for (const ch of channelsOf(inst)) {
      for (let k = 0; k + 1 < ch.pts.length; k++) {
        const a = instanceToWorld(inst, ch.pts[k]), b = instanceToWorld(inst, ch.pts[k + 1]);
        const s = segParam(sec.a, sec.b, a, b);
        if (s == null) continue;
        const r = segParam(a, b, sec.a, sec.b) ?? 0;
        const local = { x: ch.pts[k].x + (ch.pts[k + 1].x - ch.pts[k].x) * r, y: ch.pts[k].y + (ch.pts[k + 1].y - ch.pts[k].y) * r };
        const c = ceilingUnder(inst, local), d = Math.hypot(b.x - a.x, b.y - a.y) || 1;
        const across = Math.abs(((b.x - a.x) * u.y - (b.y - a.y) * u.x) / d);
        hits.push({ id: ch.id, apex: { t: s * len, h: c.z }, axis: Math.atan2(c.down[2], along({ x: c.down[0], y: c.down[1] })), width: channelFigures(ch).width / Math.max(0.25, across), cone: lightBeam(ch, cones)!, lens: 4 });
      }
    }
    // A spot the plane passes through: its cone along its axis, as far as the view
    // sees it (a fibre end has none).
    for (const sp of spotsOf(inst)) {
      if (sp.fibre) continue;
      const m = spotMount(inst, sp), c = instanceToWorld(inst, sp.at);
      const t = (c.x - sec.a.x) * u.x + (c.y - sec.a.y) * u.y;
      const off = Math.abs((c.x - sec.a.x) * u.y - (c.y - sec.a.y) * u.x);
      if (t < 0 || t > len || off > m.diameter / 2) continue;
      hits.push({ id: sp.id, apex: { t, h: m.z - SPOT_LENS_DROP }, axis: Math.atan2(m.axis[2], along({ x: m.axis[0], y: m.axis[1] })), width: 2 * Math.sqrt(Math.max(0, (m.diameter / 2) ** 2 - off * off)), cone: lightBeam(sp, cones)!, lens: 6 });
    }
  }
  if (!hits.length) return [];
  const solids = sectionPieces(sec, instances, walls, polygons)
    .filter((pc) => (pc.kind === 'profile' || (pc.kind === 'poly' && (pc.style === 'cut' || pc.style === 'cut-hatched')))
      && !(pc.src?.part?.kind === 'fixture' && lights.has(pc.src.part.id)))
    .map((pc) => pc.pts);
  solids.push([{ t: -1e7, h: 0 }, { t: 1e7, h: 0 }]); // the floor
  // A light set inside what the plane cuts — an A1 strip in a bench's front rail
  // where the front board hangs no lower than the frame — shines out of it: only
  // what is around it stops its beam.
  const around = (apex: BeamPt): BeamPt[][] => solids.filter((ring) => !insideRing(apex, ring));
  return hits.map((h) => ({ id: h.id, apex: h.apex, width: h.width, cone: h.cone, fan: h.fill ?? beamFan(h.apex, h.axis, h.cone, around(h.apex)), ...(h.lens ? { lens: h.lens } : {}) }));
}

/** Whether a point lies inside a closed ring (a section's t / h). */
function insideRing(p: BeamPt, ring: readonly BeamPt[]): boolean {
  if (ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j];
    if ((a.h > p.h) !== (b.h > p.h) && p.t < ((b.t - a.t) * (p.h - a.h)) / (b.h - a.h) + a.t) inside = !inside;
  }
  return inside;
}

function benchSweep(profile: BenchProfilePoint[], pathAt: (x: number) => Vec2[], quad?: Vec2[]): SecSweep {
  return { ring: profileRing(profile), width: profileExtent(profile).width, pathAt, quad };
}

/** A swept steam bench in a section: beyond the plane each face turned toward
 *  the viewer (clipped to the viewed side), at its own depth — so the seat's
 *  front hides the recess under it and a backrest stands behind the seat — and,
 *  where the plane crosses a straight band, the profile it cuts. */
function drawSweptBench(sec: SectionLine, api: SecDraw, at: (d: number, plan?: SecPlan) => void, sw: SecSweep, color: string, poly: Vec2[]): void {
  const { u, n } = sectionFrame(sec);
  const side = (p: Vec3): number => (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y;
  const faces = sw.faces ?? profileSweepFaces(sw.ring, sw.pathAt);
  const seen = (f: SweepFace): boolean => f.normal.x * n.x + f.normal.y * n.y <= -1e-6; // not turned away, nor edge on
  for (const f of faces) {
    if (!seen(f)) continue;
    // A smooth edge between two faces both seen is no line: only creases and the silhouette are drawn.
    const hidden = f.nb?.map((o, i) => o != null && !f.crease?.[i] && !!faces[o] && seen(faces[o]));
    const pts = clipFaceToViewedSide(f, side, hidden);
    if (pts.length < 3) continue;
    const plan = pts.map((p) => ({ x: p.x, y: p.y }));
    const mid = plan.reduce((s, p) => ({ x: s.x + p.x / plan.length, y: s.y + p.y / plan.length }), { x: 0, y: 0 });
    at(Math.max(beyondDepthOf(sec, plan), nearDepthOf(sec, [mid])), { ring: plan });
    api.poly(pts.map((p) => ({ t: (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y, h: p.z, ...(p.s ? { s: true } : {}), ...(p.x_ ? { x: true } : {}) })), 'elev', color);
  }
  if (!sw.quad) {
    // A corner piece: its cut read off the swept faces, round the arc.
    if (!segmentPolyChord(sec.a, sec.b, poly)) return;
    at(CUT_DEPTH);
    for (const loop of sweepPlaneCut(faces, sec.a, u, n)) api.poly(loop, 'cut', color);
    return;
  }
  const chord = segmentPolyChord(sec.a, sec.b, sw.quad);
  if (!chord) return;
  const [q0, q1, , q3] = sw.quad;
  const ex = q1.x - q0.x, ey = q1.y - q0.y, el = Math.hypot(ex, ey) || 1;
  let nx = -ey / el, ny = ex / el;
  if ((q3.x - q0.x) * nx + (q3.y - q0.y) * ny < 0) { nx = -nx; ny = -ny; }
  const D = (q3.x - q0.x) * nx + (q3.y - q0.y) * ny;
  const depthAt = (t: number): number => {
    const p = { x: sec.a.x + u.x * t, y: sec.a.y + u.y * t };
    return D > 0 ? (((p.x - q0.x) * nx + (p.y - q0.y) * ny) / D) * sw.width : 0;
  };
  const [t0, t1] = chord;
  at(CUT_DEPTH);
  for (const piece of profileCut(sw.ring, depthAt(t0), depthAt(t1))) {
    api.poly(piece.map((p) => ({ t: t0 + p.u * (t1 - t0), h: p.y, ...(p.s ? { s: true } : {}) })), 'cut', color);
  }
}

/** A face's corners on the viewed side of the plane (its arc samples kept).
 *  `hidden[i]`: edge `i → i+1` is drawn as no line, which each piece of it keeps
 *  (`x_` on the point it starts from); the plane's own edge is drawn. */
function clipFaceToViewedSide(f: SweepFace, side: (p: Vec3) => number, hidden?: readonly boolean[]): Array<Vec3 & { s?: boolean; x_?: boolean }> {
  const pts = f.pts.map((p, i) => ({ ...p, s: !!f.s?.[i], x_: !!hidden?.[i] }));
  const out: Array<Vec3 & { s?: boolean; x_?: boolean }> = [];
  for (let i = 0; i < pts.length; i++) {
    const cur = pts[i], prev = pts[(i + pts.length - 1) % pts.length];
    const dc = side(cur), dp = side(prev);
    const cross = (x_: boolean): Vec3 & { x_: boolean } => { const k = dp / (dp - dc); return { x: prev.x + (cur.x - prev.x) * k, y: prev.y + (cur.y - prev.y) * k, z: prev.z + (cur.z - prev.z) * k, x_ }; };
    if (dc >= 0) { if (dp < 0) out.push(cross(prev.x_)); out.push(cur); } else if (dp >= 0) out.push(cross(false));
  }
  return out;
}

/** Project a world polygon onto the section line's length axis: [tMin,tMax] as
 *  distances (mm) from `a`. Used to place geometry beyond the cut in elevation. */
function projectSpan(sec: SectionLine, poly: Vec2[]): [number, number] {
  const { u } = sectionFrame(sec);
  let mn = Infinity, mx = -Infinity;
  for (const p of poly) {
    const t = (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y;
    mn = Math.min(mn, t); mx = Math.max(mx, t);
  }
  return [mn, mx];
}

/** Whether a polygon sits on the side the section looks toward (the `+n`
 *  half-plane). Flipping `sec.side` flips `n`, so it flips what gets projected —
 *  you see what's in front of the cut in the chosen view direction. */
function viewedSide(sec: SectionLine, poly: Vec2[]): boolean {
  const { n } = sectionFrame(sec);
  let cx = 0, cy = 0;
  for (const p of poly) { cx += p.x; cy += p.y; }
  cx /= poly.length; cy /= poly.length;
  return (cx - sec.a.x) * n.x + (cy - sec.a.y) * n.y > 0;
}

/** Clip a convex polygon to the half-plane the section looks toward (`+n`), so
 *  the part of a cut object that extends behind the plane can be projected. */
function clipToViewedSide(sec: SectionLine, poly: Vec2[]): Vec2[] {
  const { n } = sectionFrame(sec);
  const side = (p: Vec2): number => (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y;
  const out: Vec2[] = [];
  for (let i = 0; i < poly.length; i++) {
    const cur = poly[i], prev = poly[(i + poly.length - 1) % poly.length];
    const dc = side(cur), dp = side(prev);
    if (dc >= 0) {
      if (dp < 0) { const t = dp / (dp - dc); out.push({ x: prev.x + (cur.x - prev.x) * t, y: prev.y + (cur.y - prev.y) * t }); }
      out.push(cur);
    } else if (dp >= 0) {
      const t = dp / (dp - dc); out.push({ x: prev.x + (cur.x - prev.x) * t, y: prev.y + (cur.y - prev.y) * t });
    }
  }
  return out;
}

/** Fill a projected wall face (chord span [t0,t1] up to height H) as a SOLID
 *  opaque poché — a wall seen from outside reads as solid material, never
 *  see-through — but leave each opening rectangle CLEAR so its glazing (or a
 *  window void) shows through. Mirrors the cut-wall pier/sill/lintel breakdown. */
function fillElevWall(api: SecDraw, t0: number, t1: number, H: number, wallCol: string, holes: Array<{ a: number; b: number; sill: number; arch: number }>): void {
  const A = 1;
  const clipped = holes
    .map((h) => ({ a: Math.max(t0, Math.min(t1, h.a)), b: Math.max(t0, Math.min(t1, h.b)), sill: h.sill, arch: Math.min(h.arch, H) }))
    .filter((h) => h.b - h.a > 1e-3)
    .sort((x, y) => x.a - y.a);
  let cur = t0;
  for (const h of clipped) {
    if (h.a > cur + 1e-3) api.band(cur, h.a, 0, H, wallCol, A); // pier before the opening
    const s0 = Math.max(cur, h.a), s1 = Math.max(s0, h.b);
    if (s1 > s0 + 1e-3) {
      if (h.sill > 0) api.band(s0, s1, 0, h.sill, wallCol, A); // below the sill
      if (h.arch < H) api.band(s0, s1, h.arch, H, wallCol, A); // lintel above
      // [s0,s1] × [sill,arch] stays clear — the see-through window.
    }
    cur = Math.max(cur, h.b);
  }
  if (cur < t1 - 1e-3) api.band(cur, t1, 0, H, wallCol, A);
}

/** An outline's faces with the in-line runs joined: a wall split at its panes'
 *  jambs (each pane owns a segment) is still ONE wall face, so its elevation has
 *  no break above or below the glass at a jamb. */
function wallFaces(fp: readonly Vec2[]): Vec2[][] {
  const n = fp.length;
  const inLine = (i: number): boolean => {
    const a = fp[(i - 1 + n) % n], b = fp[i], c = fp[(i + 1) % n];
    const u = { x: b.x - a.x, y: b.y - a.y }, v = { x: c.x - b.x, y: c.y - b.y };
    const lu = Math.hypot(u.x, u.y), lv = Math.hypot(v.x, v.y);
    if (lu < 1e-9 || lv < 1e-9) return true;
    return Math.abs(u.x * v.y - u.y * v.x) < 1e-9 * lu * lv && u.x * v.x + u.y * v.y > 0;
  };
  const start = fp.findIndex((_, i) => !inLine(i));
  if (start < 0) return [];
  const faces: Vec2[][] = [];
  let a = start;
  for (let k = 1; k <= n; k++) {
    const i = (start + k) % n;
    if (!inLine(i) || i === start) { faces.push([fp[a], fp[i]]); a = i; }
  }
  return faces;
}

/** The STAND-INS of a room's reflex corners seen from `sec`. There the inner
 *  face is the longer one: past the columns its outer faces span, the corner's
 *  wall block reaches on to the inner corner. Seen past a face turned toward
 *  the view and its neighbour turned away, no face spans those columns — a ray
 *  there crosses the block between two inner faces — and through a hole in a
 *  face in front, or over a lower wall, what stood behind showed through it; in
 *  any other view a pane or a door ending at the corner opened its face where
 *  the block behind it is wall. Each corner's stand-in is a face of its own
 *  across those columns: the segment from the outer face's point farthest along
 *  the cut to the inner face's — a sharp corner's mitre, the edge its two walls'
 *  bodies share, a rounded one's radius —, inside the wall, so all past it is
 *  behind wall. None where the plan draws no wall at the corner (glass or a
 *  door on both sides of it). */
function reflexStandIns(inst: ProductInstance, sec: SectionLine): Vec2[][] {
  const poly = inst.polygon, w = wallThicknessOf(inst);
  if (!poly || poly.length < 3 || !(w > 0)) return [];
  const n = poly.length;
  const outer = resolvedOutlinePaths(inst, 0), inner = resolvedOutlinePaths(inst, w);
  if (outer.length !== n || inner.length !== n) return [];
  const sgn = polygonArea(poly) > 0 ? 1 : -1;
  const { u } = sectionFrame(sec);
  const tOf = (p: Vec2): number => (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y;
  let solid: Vec2[][] | null = null;
  const out: Vec2[][] = [];
  for (let v = 0; v < n; v++) {
    const a = poly[(v - 1 + n) % n], b = poly[v], c = poly[(v + 1) % n];
    const ux = b.x - a.x, uy = b.y - a.y, vx = c.x - b.x, vy = c.y - b.y;
    if (sgn * (ux * vy - uy * vx) >= -1e-9 * Math.hypot(ux, uy) * Math.hypot(vx, vy)) continue; // convex, or in line
    solid ??= polyWall(inst).fill;
    if (!solid.some((q) => q.some((p) => inner[v].some((r) => Math.hypot(p.x - r.x, p.y - r.y) < 1e-6)))) continue;
    const o = outer[v].map((p) => instanceToWorld(inst, p)), i = inner[v].map((p) => instanceToWorld(inst, p));
    const to = o.map(tOf), ti = i.map(tOf);
    const ohi = Math.max(...to), olo = Math.min(...to), ihi = Math.max(...ti), ilo = Math.min(...ti);
    if (ihi > ohi + 1e-6) out.push([o[to.indexOf(ohi)], i[ti.indexOf(ihi)]]);
    if (ilo < olo - 1e-6) out.push([o[to.indexOf(olo)], i[ti.indexOf(ilo)]]);
  }
  return out;
}

/** The STAND-INS of a room's convex corners where a pane, an opening or a door
 *  ends on ONE side: the plan cuts that wall's band there and keeps the other's,
 *  mitred, so the corner's block is that wall's half up to the mitre — the
 *  segment from the outer corner to the inner one, a face of its own once its
 *  neighbour is a hole. Where its columns run past the solid wall's outer face
 *  — on the hole's side of the corner — no face of that wall spans them: seen
 *  through the hole the mitre (or, from inside, the solid wall's inner face) is
 *  wall, and the hole's face alone opened it. So it stands in there, as a
 *  reflex corner's does. None where both sides are holes (the plan draws no
 *  wall at the corner) or neither is, and none where the solid face's own
 *  columns cover the mitre's — there that face hides or shows the same wall. */
function mitreStandIns(inst: ProductInstance, sec: SectionLine): Vec2[][] {
  const c = holeCorners(inst);
  if (!c || !(wallThicknessOf(inst) > 0)) return [];
  const { edges, n, ends, starts } = c;
  const outer = resolvedOutlinePaths(inst, 0);
  const sgn = polygonArea(inst.polygon!) > 0 ? 1 : -1;
  const { u } = sectionFrame(sec);
  const tOf = (p: Vec2): number => (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y;
  const out: Vec2[][] = [];
  for (let v = 0; v < n; v++) {
    const pe = edges[(v - 1 + n) % n], ne = edges[v];
    if (sgn * (pe.dir.x * ne.dir.y - pe.dir.y * ne.dir.x) <= 1e-9) continue; // reflex, or in line
    if ((outer[v]?.length ?? 1) > 1) continue; // a rounded corner: no feature reaches it
    if (ends(v) === starts(v)) continue;
    const V = instanceToWorld(inst, ne.a), I = instanceToWorld(inst, ne.ia);
    const along = ends(v) ? ne.dir : { x: -pe.dir.x, y: -pe.dir.y }; // the solid wall's face, from the corner
    const S = instanceToWorld(inst, { x: ne.a.x + along.x, y: ne.a.y + along.y });
    const dI = tOf(I) - tOf(V), dS = tOf(S) - tOf(V);
    if (Math.abs(dI) > 1e-6 && dI * dS < 0) out.push([V, I]);
  }
  return out;
}

/** Which of a room's corners a hole in its outline reaches (a pane, an opening
 *  or a door whose span runs to its segment's end): `ends(v)` on the segment
 *  before vertex `v`, `starts(v)` on the one after it. A jamb is no corner — its
 *  feature always reaches it — so the caller tells the two apart. */
function holeCorners(inst: ProductInstance): { edges: ReturnType<typeof polyEdges>; n: number; ends: (v: number) => boolean; starts: (v: number) => boolean } | null {
  const poly = inst.polygon;
  if (!poly || poly.length < 3) return null;
  const edges = polyEdges(inst), n = edges.length;
  if (n !== poly.length) return null;
  const spans: Array<{ edge: number; s: number; t: number }> = [];
  for (const g of [...(inst.glass ?? []), ...(inst.openings ?? [])]) {
    if (g.innerWallId == null && g.edge != null && !g.pocket) spans.push({ edge: g.edge, s: Math.min(g.start, g.end), t: Math.max(g.start, g.end) });
  }
  for (const d of outerDoorSpans(inst)) spans.push({ edge: d.edge, s: d.start, t: d.end });
  if (!spans.length) return null;
  const ends = (v: number): boolean => { const k = (v - 1 + n) % n; return spans.some((sp) => sp.edge === k && sp.t >= edges[k].len - 1e-3); };
  const starts = (v: number): boolean => spans.some((sp) => sp.edge === v && sp.s <= 1e-3);
  return { edges, n, ends, starts };
}

/** Every corner's stand-in seen from `sec`: the reflex corners' and the mitres
 *  a hole at a convex corner bares. */
const cornerStandIns = (inst: ProductInstance, sec: SectionLine): Vec2[][] => [...reflexStandIns(inst, sec), ...mitreStandIns(inst, sec)];

/** A room the section faces but does NOT cut: draw its exterior in elevation —
 *  the outer wall silhouette (full external height) plus its glazed openings
 *  (glass panes + door) and any wall openings, projected onto the section plane
 *  — and what stands in it (`drawRoomContents`): its solid walls hide that, its
 *  glass and openings show it, as in 3D. */
function drawRoomElevation(inst: ProductInstance, sec: SectionLine, api: SecDraw, len: number, depth?: Pick<DepthOrderedDraw, 'at'>, fp = instanceFootprint(inst)): void {
  const ev = flatElevationEvents(inst, sec, len, fp);
  replayEvents(ev, api, depth);
  if (ev.length) drawRoomContents(inst, sec, api, (d, p) => depth?.at(d, p), len);
}

/** Replay recorded events into `api`: each depth and its plan into `depth`,
 *  each call after its own source. */
function replayEvents(ev: readonly ShapedEvent[], api: SecDraw, depth?: Pick<DepthOrderedDraw, 'at'>): void {
  for (const e of ev) {
    if ('d' in e) depth?.at(e.d, e.p);
    else replayCall(e, api);
  }
}

function replayCall(c: SecCall, api: SecDraw): void {
  if (c.src) api.src(c.src);
  (api[c.k] as (...a: unknown[]) => void)(...c.a);
}

/** A wall face seen in elevation, recorded: `away`, its outward normal along
 *  the view — a corner's stand-in (`cornerStandIns`) counts as turned
 *  away —, and the inner line of its wall (`innerEnds`; none for a stand-in or
 *  a wall of no thickness). */
export interface ElevFace extends SecFace { away: boolean; inner?: SecFace['ends'] }

/** A segment's two ends as (t along the cut, depth in front of it). */
function faceEnds(sec: SectionLine, [p, q]: readonly Vec2[]): SecFace['ends'] {
  const { u, n } = sectionFrame(sec);
  const at = (w: Vec2): { t: number; d: number } => ({ t: (w.x - sec.a.x) * u.x + (w.y - sec.a.y) * u.y, d: (w.x - sec.a.x) * n.x + (w.y - sec.a.y) * n.y });
  return [at(p), at(q)];
}

/** A wall face's inner line: its segment moved into the room (`sgn`: the
 *  outline's winding) through the wall's thickness w, as `faceEnds` — with the
 *  segment, the wall the face is ordered by against other objects (`paintOrder`). */
function innerEnds(sec: SectionLine, [p, q]: readonly Vec2[], sgn: number, w: number): SecFace['ends'] | undefined {
  const dx = q.x - p.x, dy = q.y - p.y, L = Math.hypot(dx, dy);
  if (!(w > 0) || !(L > 0)) return undefined;
  const ix = (-dy / L) * sgn * w, iy = (dx / L) * sgn * w;
  return faceEnds(sec, [{ x: p.x + ix, y: p.y + iy }, { x: q.x + ix, y: q.y + iy }]);
}

/** A face's plan (`SecPlan`): its segment, and its wall's inner line. */
const facePlan = (f: SecFace & { inner?: SecFace['ends'] }): SecPlan => (f.inner ? { face: f.ends, inner: f.inner } : { face: f.ends });

/** A `SecDraw` that records its calls into `out` (point lists copied), each
 *  with the source set before it. */
function recorder(out: ShapedEvent[]): SecDraw {
  let src: SecSrc | null = null;
  const rec = (k: SecKind) => (...a: unknown[]): void => {
    for (let i = 0; i < a.length; i++) if (Array.isArray(a[i])) a[i] = (a[i] as SecPt[]).map((p) => ({ ...p }));
    out.push(src ? { k, a, src } : { k, a });
  };
  return {
    src: (s) => { src = s; },
    profile: rec('profile'), band: rec('band'), elev: rec('elev'), outline: rec('outline'), vedge: rec('vedge'),
    glass: rec('glass'), window: rec('window'), poly: rec('poly'), line: rec('line'),
  };
}

/** All a flat room's elevation is built from, seen from one section: its
 *  footprint in the world and the columns it spans, its height, its walls'
 *  colour and thickness, a ceiling of its own colour's thickness and colour
 *  (null: none shows), its openings' and glazing's holes — each with its sill,
 *  arch, colour and the line of the face that holds it — and its corners'
 *  stand-ins (`cornerStandIns`). */
interface FlatElevation {
  room: SecSrc; fp: Vec2[]; t0: number; t1: number; H: number; wallCol: string; wallT: number; slabT: number; slabCol: string | null;
  openHoles: ElevHole[]; glassHoles: Array<ElevHole & { color?: string; glazed?: true }>; standIns: Vec2[][];
}

function flatElevationInput(inst: ProductInstance, sec: SectionLine, len: number, fp = instanceFootprint(inst)): FlatElevation | null {
  if (fp.length < 3 || !viewedSide(sec, fp)) return null; // behind the view direction
  const [pmn, pmx] = projectSpan(sec, fp);
  const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
  if (t1 - t0 <= 1e-3) return null;
  const H = externalHeight(inst), wallCol = wallColorOf(inst);
  // Openings, panes and the door are holes in the solid wall, so the glass reads
  // through the poché instead of sitting as a tint on top of it — each in the
  // face that holds it only.
  const { openHoles, glassHoles } = elevationHoles(inst, sec, t0, t1);
  const slabT = Math.min(H, ceilingThicknessOf(inst));
  const slabCol = hasCeiling(inst) && slabT > 0 && ownCeilingColor(inst) ? ceilingHex6(inst, wallCol) : null;
  return { room: srcOf(inst.id), fp, t0, t1, H, wallCol, wallT: wallThicknessOf(inst), slabT, slabCol, openHoles, glassHoles, standIns: cornerStandIns(inst, sec) };
}

/** The flat room's wall faces in elevation, each with its calls, in the order
 *  they are drawn. PER WALL FACE, each at its own depth: a room's outline is a
 *  ribbon of faces standing at different distances, and one band carrying the
 *  whole footprint's nearest depth painted the far faces at the near face's
 *  depth across the full width — so an object standing in the notch of an L, in
 *  front of the far arm and beside the near leg, was wrongly buried. The faces
 *  turned AWAY from the viewer come first, as in `shapedElevation`, then the
 *  corners' stand-ins (`cornerStandIns`, no hole: a feature is its own face's); what each
 *  face shows past the faces in front of it is `occludeFaces`'. */
function flatElevationFaces(x: FlatElevation, sec: SectionLine): ElevFace[] {
  const { room, fp, t0, t1, H, wallCol, wallT, slabT, slabCol, openHoles, glassHoles, standIns } = x;
  const within = (a: number, b: number, s0: number, s1: number): boolean => a < s1 - 1e-3 && b > s0 + 1e-3;
  const { n } = sectionFrame(sec);
  const sgn = polygonArea(fp) > 0 ? 1 : -1;
  const away = ([p, q]: Vec2[]): boolean => sgn * ((q.y - p.y) * n.x - (q.x - p.x) * n.y) > 0; // the outward normal along the view
  const faces = wallFaces(fp);
  const out: ElevFace[] = [];
  for (const face of [...faces.filter(away), ...standIns, ...faces.filter((f) => !away(f))]) {
    const own = !standIns.includes(face);
    if (!viewedSide(sec, face)) continue; // this face is behind the plane
    const [fmn, fmx] = projectSpan(sec, face);
    const s0 = Math.max(t0, fmn), s1 = Math.min(t1, fmx);
    if (s1 - s0 <= 1e-3) continue; // edge-on to the view: nothing to fill
    const calls: SecCall[] = [];
    const api = recorder(calls);
    api.src(room);
    const faceOpen = own ? holesOnFace(face, openHoles) : [], faceGlass = own ? holesOnFace(face, glassHoles) : [];
    const holes = [...faceOpen, ...faceGlass];
    if (slabCol) {
      // A ceiling of its own colour shows its edge above the walls, as in 3D —
      // over a stand-in it is the slab's inside, no edge.
      fillElevWall(api, s0, s1, H - slabT, wallCol, holes);
      if (own) api.band(s0, s1, H - slabT, H, slabCol, 1);
    } else fillElevWall(api, s0, s1, H, wallCol, holes); // solid wall, openings clear
    api.outline(s0, s1, 0, own || !slabCol ? H : H - slabT, wallCol); // this face's silhouette
    for (const g of faceGlass) {
      if (!g.glazed && within(g.a, g.b, s0, s1)) { api.src(g.src); api.glass(Math.max(g.a, s0), Math.min(g.b, s1), g.sill, g.arch, g.color); }
    }
    // Each solid-wall opening is drawn as a window frame you can see through.
    for (const h of faceOpen) {
      if (within(h.a, h.b, s0, s1)) { api.src(h.src); api.window(Math.max(h.a, s0), Math.min(h.b, s1), h.sill, Math.min(h.arch, H), wallCol); }
    }
    out.push({ ends: faceEnds(sec, face), s0, s1, d: nearDepthOf(sec, face), calls, open: glazedRuns(faceGlass, s0, s1), away: !own || away(face), inner: own ? innerEnds(sec, face, sgn, wallT) : undefined });
  }
  return out;
}

/** The calls of a flat room in elevation, recorded once per section and room
 *  (`cachedEvents`) — a section replays its emitter several times per
 *  mousemove, and each replay would clip the faces again —, keyed on the line
 *  and the fields of the room a section reads, as a shaped room's are. */
function flatElevationEvents(inst: ProductInstance, sec: SectionLine, len: number, fp?: Vec2[]): ShapedEvent[] {
  roomDoors(inst); // a legacy door is moved into `doors` when first read: before the key, not during the build
  return cachedEvents(`flat:${sec.id}:${inst.id}`, [sec.a.x, sec.a.y, sec.b.x, sec.b.y, sec.side, len], inst, (ev) => {
    const x = flatElevationInput(inst, sec, len, fp);
    if (x) pushShown(ev, flatElevationFaces(x, sec));
  });
}

/** What `occludeFaces` shows of `faces`, as recorded events: each call after
 *  its depth and its face's plan (`facePlan`). */
function pushShown(ev: ShapedEvent[], faces: readonly (SecFace & { inner?: SecFace['ends'] })[]): void {
  occludeFaces(faces).forEach((shown: readonly SecShown[], i) => {
    const p = facePlan(faces[i]);
    for (const s of shown) ev.push({ d: s.d, p }, s.call.src ? { k: s.call.k, a: s.call.a, src: s.call.src } : { k: s.call.k, a: s.call.a });
  });
}

/** Architectural cross-section of a walled room: the two cut walls (with door /
 *  glass / opening holes), the ceiling band, the walls seen from inside, each cut
 *  bench as a solid step, and benches beyond the cut projected in elevation —
 *  all in the room's own wall / bench colours. */
function drawRoomSection(inst: ProductInstance, sec: SectionLine, api: SecDraw, len = Infinity, depth?: Pick<DepthOrderedDraw, 'at'>): void {
  // `depth` tags each piece below with its own distance from the cut plane, so a
  // room's benches / heater walls interleave correctly with the heaters and other
  // objects standing between them. Without it every piece keeps the caller's depth.
  const at = (d: number, plan?: SecPlan): void => depth?.at(d, plan);
  // One interval per piece of the room the plane crosses: a cut across both arms
  // of an L or a U is two rooms' worth of walls and ceiling, with nothing over
  // the void between them.
  if (!isFlatCeiling(inst)) { replayShaped(inst, sec, api, len, depth); return; }
  const fp = instanceFootprint(inst);
  const outerIv = segmentPolyIntervals(sec.a, sec.b, fp);
  if (!outerIv.length) { drawRoomElevation(inst, sec, api, len, depth, fp); return; } // not cut → exterior view
  const room = srcOf(inst.id);
  api.src(room);
  const H = externalHeight(inst);
  const wallH = interiorHeight(inst); // walls rise to the underside of the ceiling
  const wallCol = wallColorOf(inst);
  const inner = innerFootprint(inst);
  const innerIv = inner.length >= 3 ? segmentPolyIntervals(sec.a, sec.b, inner) : [];
  const holes = cutWallHoles(inst, sec);
  at(CUT_DEPTH);
  // The cut walls: what each outer interval keeps once the interior is taken out.
  for (const [o0, o1] of outerIv) {
    for (const [w0, w1] of subtractSpans(o0, o1, innerIv)) drawCutWall(api, w0, w1, wallH, wallCol, holes, room);
  }
  at(CUT_DEPTH);
  // The ceiling band (solid, on the walls); a 0 mm ceiling does not exist, so it
  // draws nothing — not a zero-height slab line at the wall tops.
  if (H - wallH >= 1e-6) for (const [o0, o1] of outerIv) api.profile(o0, o1, wallH, H, true, ceilingHex6(inst, wallCol));
  // What stands beyond the cut is clipped to the interior it stands in.
  const spans = innerIv.length ? innerIv : outerIv;
  const clipSpans = (a: number, b: number): Array<[number, number]> => spans
    .map(([s0, s1]): [number, number] => [Math.max(s0, a), Math.min(s1, b)])
    .filter(([s0, s1]) => s1 - s0 > 1e-3);
  drawBackWalls(inst, sec, api, at, clipSpans, wallH);
  api.src(room);
  // Vertical room corners on the viewed side, projected as dashed floor-to-ceiling
  // edges — a depth cue for the room's shape beyond the cut plane. Corners the cut
  // passes through (near the cut plane) are already part of the cut walls.
  const seen: number[] = [];
  for (const corner of inner) {
    if (!viewedSide(sec, [corner])) continue;
    const t = projectSpan(sec, [corner])[0];
    if (!spans.some(([s0, s1]) => t >= s0 - 0.5 && t <= s1 + 0.5)) continue;
    if (seen.some((s) => Math.abs(s - t) < 1)) continue; // one line per corner
    seen.push(t);
    at(nearDepthOf(sec, [corner]), { ring: [corner] });
    api.vedge(t, 0, wallH, wallCol);
  }
  // Glass walls the section looks toward (panes in front of the cut, roughly
  // parallel to it) are projected in elevation — outlined + light-blue fill, the
  // same look as the plan — so a glazed wall reads as glass, not a blank
  // elevation. Panes the section actually cuts are already drawn in the cut wall
  // above, so skip those. Clipped to the interior span so the perpendicular side
  // walls occlude the glass ends. The door is glass too, so it projects as well.
  for (const { seg, src, glazedH } of glazingOf(inst)) {
    if (glazedH != null) continue;
    const rings = glassPolysLocal(inst, seg).map((q) => q.map((p) => instanceToWorld(inst, p)));
    const fp = rings.flat();
    if (fp.length < 3) continue;
    if (segmentPolyChord(sec.a, sec.b, fp)) continue; // cut by the plane → drawn as a cut pane
    if (!viewedSide(sec, fp)) continue; // behind the view direction
    const [pmn, pmx] = projectSpan(sec, fp);
    const pieces = clipSpans(pmn, pmx);
    if (!pieces.length) continue;
    at(nearDepthOf(sec, fp), { rings });
    api.src(src);
    // From the pane's own base: a raised pane does not reach the floor.
    for (const [t0, t1] of pieces) api.glass(t0, t1, glassPaneBase(seg), glassPaneTop(inst, seg), seg.color);
  }
  api.src(room);
  drawRoomInterior(inst, sec, api, at, clipSpans);
  api.src(room);
}

/** How far toward white the walls a cut room is seen into are drawn
 *  (`lightenHex`), so they read apart from the walls the plane cuts. */
const BACK_WALL_TONE = 0.25;

/** The walls a cut room is seen into, from inside: the inner surface of each
 *  face turned AWAY from the viewer, beyond the plane, opaque and a tone lighter
 *  than the cut walls (`BACK_WALL_TONE`), each at its own depth with its face's
 *  plan — so what stands in the room paints over it and what stands behind the
 *  room is hidden by it —, its panes' and openings' holes left clear (the glass
 *  beyond the cut is drawn into them, an opening framed). Clipped to the
 *  interior spans. `x`: a shaped room's, up to its underside; else up to `wallH`. */
function drawBackWalls(inst: ProductInstance, sec: SectionLine, api: SecDraw, at: (d: number, plan?: SecPlan) => void, clipSpans: (a: number, b: number) => Array<[number, number]>, wallH: number, x?: ShapedCtx): void {
  const fp = instanceFootprint(inst);
  const { n } = sectionFrame(sec);
  const sgn = polygonArea(fp) > 0 ? 1 : -1;
  const w = wallThicknessOf(inst);
  const wallCol = wallColorOf(inst), col = lightenHex(wallCol, BACK_WALL_TONE);
  const [pmn, pmx] = projectSpan(sec, fp);
  const { openHoles, glassHoles } = elevationHoles(inst, sec, pmn, pmx);
  for (const face of wallFaces(fp)) {
    const [p, q] = face;
    const dx = q.x - p.x, dy = q.y - p.y, L = Math.hypot(dx, dy);
    if (L < 1e-9 || sgn * (dy * n.x - dx * n.y) <= 0) continue; // its inside faces away from the viewer
    const seg = clipSegViewed(sec, p, q);
    if (!seg) continue;
    const ix = (-dy / L) * sgn * w, iy = (dx / L) * sgn * w;
    const inner: [Vec2, Vec2] = [{ x: seg[0].x + ix, y: seg[0].y + iy }, { x: seg[1].x + ix, y: seg[1].y + iy }];
    const [a, b] = projectSpan(sec, inner);
    const pieces = clipSpans(a, b);
    if (!pieces.length) continue;
    const holes = [...holesOnFace(face, openHoles), ...holesOnFace(face, glassHoles)];
    const ends = faceEnds(sec, seg), innerE = innerEnds(sec, seg, sgn, w);
    at(beyondDepthOf(sec, inner), innerE ? { face: ends, inner: innerE } : { face: ends });
    api.src(srcOf(inst.id));
    const wtop: Prof = x ? x.along(inner[0], inner[1], 'under') : wallH;
    for (const [s0, s1] of pieces) {
      if (x) fillElevWallShaped(api, s0, s1, wtop, col, holes);
      else fillElevWall(api, s0, s1, wallH, col, holes);
      for (const h of holesOnFace(face, openHoles)) {
        const h0 = Math.max(h.a, s0), h1 = Math.min(h.b, s1);
        if (h1 - h0 <= 1e-3) continue;
        api.src(h.src);
        if (x) for (const r of bandBetween(h0, h1, h.sill, profMin(h.arch, wtop, h0, h1))) emitPoly(api, r, 'window', wallCol);
        else api.window(h0, h1, h.sill, Math.min(h.arch, wallH), wallCol);
      }
    }
  }
}

/** A room's benches, heater walls and partitions: each cut where the plane
 *  crosses it, each part beyond the plane in elevation, at its own depth and
 *  clipped to `clipSpans` — in a cut room and, seen from outside, in a room's
 *  elevation, where its walls hide them but its glass and openings do not.
 *  `x`: a shaped room's, whose partitions and heater walls rise to its underside. */
function drawRoomInterior(inst: ProductInstance, sec: SectionLine, api: SecDraw, at: (d: number, plan?: SecPlan) => void, clipSpans: (a: number, b: number) => Array<[number, number]>, x?: ShapedCtx): void {
  for (const bnd of roomBenchBands(inst, !!x)) {
    api.src(bnd.src);
    if (bnd.sweep) {
      drawSweptBench(sec, api, at, bnd.sweep, bnd.color, bnd.poly);
      continue;
    }
    // Project the portion on the viewed side (a bench beyond the cut, or the part
    // of a partially-cut bench that recedes behind the plane) as a dashed
    // elevation — drawn first so the solid cut face reads over it.
    const vpoly = clipToViewedSide(sec, bnd.poly);
    if (vpoly.length >= 3) {
      const [pmn, pmx] = projectSpan(sec, vpoly);
      const pieces = clipSpans(pmn, pmx);
      if (pieces.length) {
        at(beyondDepthOf(sec, vpoly), { ring: vpoly }); // a partly cut band: strictly behind every cut face
        if (x && bnd.underCap != null) underBand(x, api, bnd.poly.map((p) => toInstanceLocal(inst, p)), bnd.base, bnd.underCap, pieces, 'elev', bnd.color);
        else for (const [t0, t1] of pieces) api.elev(t0, t1, bnd.base, bnd.height, bnd.color);
      }
    }
    // The cut face at the section plane (solid): every stretch of a carved,
    // perhaps concave, piece the plane crosses, less its holes.
    if (x && bnd.underCap != null) {
      const bc = segmentPolyChord(sec.a, sec.b, bnd.poly);
      if (bc) {
        at(CUT_DEPTH);
        for (const r of bandBetween(bc[0], bc[1], bnd.base, profMin(bnd.underCap, x.chord(bc[0], bc[1], 'under'), bc[0], bc[1]))) emitPoly(api, r, 'cut', bnd.color);
      }
      continue;
    }
    const holes = (bnd.holes ?? []).flatMap((h) => segmentPolyIntervals(sec.a, sec.b, h));
    const cut = segmentPolyIntervals(sec.a, sec.b, bnd.poly).flatMap(([t0, t1]) => subtractSpans(t0, t1, holes));
    if (cut.length) at(CUT_DEPTH);
    for (const [s0, s1] of cut) api.profile(s0, s1, bnd.base, bnd.height, true, bnd.color);
  }
}

/** A room seen from outside: what stands in it, beyond its walls — over the
 *  columns its footprint spans — and the panes of its partitions (its outer
 *  walls' are their faces' holes), so its glass shows what is behind it. */
function drawRoomContents(inst: ProductInstance, sec: SectionLine, api: SecDraw, at: (d: number, plan?: SecPlan) => void, len: number, x?: ShapedCtx): void {
  const fp = instanceFootprint(inst);
  const [pmn, pmx] = projectSpan(sec, fp);
  const lo = Math.max(0, pmn), hi = Math.min(len, pmx);
  // Only through a hole in a face turned toward the viewer: a closed room's
  // walls hide all of it, and it adds nothing to paint, snap to or export.
  const { openHoles, glassHoles } = elevationHoles(inst, sec, lo, hi);
  const { n } = sectionFrame(sec);
  const sgn = polygonArea(fp) > 0 ? 1 : -1;
  const toward = ([p, q]: Vec2[]): boolean => sgn * ((q.y - p.y) * n.x - (q.x - p.x) * n.y) <= 0;
  if (!wallFaces(fp).some((f) => toward(f) && viewedSide(sec, f) && holesOnFace(f, [...openHoles, ...glassHoles]).length)) return;
  const clipSpans = (a: number, b: number): Array<[number, number]> => {
    const s0 = Math.max(lo, a), s1 = Math.min(hi, b);
    return s1 - s0 > 1e-3 ? [[s0, s1]] : [];
  };
  for (const { seg, src } of glazingOf(inst)) {
    if (!seg.innerWallId) continue;
    const polys = glassPolysLocal(inst, seg).filter((q) => q.length >= 3);
    for (const q of polys) {
      const w = q.map((p) => instanceToWorld(inst, p));
      if (!viewedSide(sec, w)) continue;
      const [a, b] = projectSpan(sec, w);
      const pieces = clipSpans(a, b);
      if (!pieces.length) continue;
      at(nearDepthOf(sec, w), { ring: w });
      api.src(src);
      if (x) underBand(x, api, q, glassPaneBase(seg), glassPaneTop(inst, seg), pieces, 'glass', seg.color ?? '');
      else for (const [t0, t1] of pieces) api.glass(t0, t1, glassPaneBase(seg), glassPaneTop(inst, seg), seg.color);
    }
  }
  api.src(srcOf(inst.id));
  drawRoomInterior(inst, sec, api, at, clipSpans, x);
  api.src(srcOf(inst.id));
}

// ---- Shaped ceilings ----------------------------------------------------------
// A room whose ceiling has a shape (docs/claude/ceilings.md, *Shaped ceilings*): its walls rise
// to the ceiling's underside, the shell is cut where the plane crosses it, and
// the roof standing beyond the plane is drawn piece by piece — each piece CONVEX
// and tagged at its own depth, so it never spans a range of depths.

/** A profile in the section frame: points along t (sorted), or a constant height. */
type Prof = readonly SecPt[] | number;

function profAt(p: Prof, t: number): number {
  if (typeof p === 'number') return p;
  if (!p.length) return 0;
  if (t <= p[0].t) return p[0].h;
  for (let i = 0; i + 1 < p.length; i++) {
    const a = p[i], b = p[i + 1];
    if (t <= b.t) return b.t - a.t < 1e-12 ? b.h : a.h + ((b.h - a.h) * (t - a.t)) / (b.t - a.t);
  }
  return p[p.length - 1].h;
}
const profBreaks = (p: Prof, t0: number, t1: number): number[] => (typeof p === 'number' ? [] : p.map((q) => q.t).filter((t) => t > t0 + 1e-9 && t < t1 - 1e-9));
const profSample = (p: Prof, t: number): boolean => typeof p !== 'number' && p.some((q) => q.s && Math.abs(q.t - t) < 1e-9);
/** Whether the profile's chord over (ta, tb) is part of a curve. */
function profCurve(p: Prof, ta: number, tb: number): boolean {
  if (typeof p === 'number') return false;
  const m = (ta + tb) / 2;
  for (let i = 0; i + 1 < p.length; i++) if (m >= p[i].t && m <= p[i + 1].t) return !!p[i].c;
  return false;
}

/** Drop consecutive points within 1e-6 (a clip through a vertex repeats it). */
function cleanPts(pts: SecPt[], closed: boolean): SecPt[] {
  const out: SecPt[] = [];
  for (const p of pts) {
    if (!Number.isFinite(p.t) || !Number.isFinite(p.h)) continue;
    const q = out[out.length - 1];
    // One point: a vertex if either was, and the run after it is the second's.
    if (q && Math.hypot(p.t - q.t, p.h - q.h) < 1e-6) { if (!p.s) q.s = undefined; q.c = p.c; q.x = p.x; q.r = p.r; continue; }
    out.push(p);
  }
  while (closed && out.length > 1 && Math.hypot(out[0].t - out[out.length - 1].t, out[0].h - out[out.length - 1].h) < 1e-6) { if (!out.pop()!.s) out[0].s = undefined; }
  for (const p of out) { if (!p.s) delete p.s; if (!p.c) delete p.c; if (!p.x) delete p.x; if (!p.r) delete p.r; }
  return out;
}

function areaTH(pts: readonly SecPt[]): number {
  let a = 0;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) a += pts[j].t * pts[i].h - pts[i].t * pts[j].h;
  return Math.abs(a / 2);
}

/** A convex ring (x along the view, y up) clipped to the view's length,
 *  `t0` ≤ x ≤ `t1`. */
function clipToSpan(ring: Vec2[], t0: number, t1: number): Vec2[] {
  const clip = (pts: Vec2[], keep: (p: Vec2) => number): Vec2[] => {
    const out: Vec2[] = [];
    pts.forEach((p, i) => {
      const q = pts[(i + 1) % pts.length], kp = keep(p), kq = keep(q);
      if (kp >= 0) out.push(p);
      if ((kp >= 0) !== (kq >= 0)) { const f = kp / (kp - kq); out.push({ x: p.x + (q.x - p.x) * f, y: p.y + (q.y - p.y) * f }); }
    });
    return out;
  };
  return clip(clip(ring, (p) => p.x - t0), (p) => t1 - p.x);
}

function emitPoly(api: SecDraw, pts: SecPt[], style: PolyStyle, color: string, alpha?: number): void {
  const c = cleanPts(pts, true);
  if (c.length >= 3 && areaTH(c) > 1e-6) api.poly(c, style, color, alpha);
}

/** The pointwise minimum of two profiles over [t0, t1], their crossings inserted.
 *  A break of the higher profile, under which the lower one runs straight, says
 *  nothing and goes; under a curve of the lower one it is a sample of it. */
function profMin(a: Prof, b: Prof, t0: number, t1: number): SecPt[] {
  const ts = [...new Set([t0, t1, ...profBreaks(a, t0, t1), ...profBreaks(b, t0, t1)])].sort((x, y) => x - y);
  const all: number[] = [], cross = new Set<number>();
  for (let i = 0; i < ts.length; i++) {
    all.push(ts[i]);
    if (i + 1 < ts.length) {
      const d0 = profAt(a, ts[i]) - profAt(b, ts[i]), d1 = profAt(a, ts[i + 1]) - profAt(b, ts[i + 1]);
      if ((d0 > 1e-9 && d1 < -1e-9) || (d0 < -1e-9 && d1 > 1e-9)) { const tc = ts[i] + ((ts[i + 1] - ts[i]) * d0) / (d0 - d1); all.push(tc); cross.add(tc); }
    }
  }
  const lower = (t: number): Prof => (profAt(a, t) <= profAt(b, t) ? a : b);
  const own = (p: Prof, t: number): boolean => typeof p !== 'number' && p.some((q) => Math.abs(q.t - t) < 1e-9);
  const kept: SecPt[] = [];
  all.forEach((t, i) => {
    const va = profAt(a, t), vb = profAt(b, t), src = va <= vb ? a : b;
    const pt: SecPt = { t, h: Math.min(va, vb) };
    const prev = all[i - 1], next = all[i + 1];
    const run = prev != null && next != null && !cross.has(t) ? lower((prev + t) / 2) : null;
    if (run && run === lower((t + next) / 2) && !own(run, t)) {
      if (!profCurve(run, t, t)) return;
      pt.s = true;
    } else if (profSample(src, t)) pt.s = true;
    kept.push(pt);
  });
  for (let i = 0; i + 1 < kept.length; i++) {
    const ta = kept[i].t, tb = kept[i + 1].t;
    if (profCurve(lower((ta + tb) / 2), ta, tb)) kept[i].c = true;
  }
  return kept;
}

/** The polygons between two profiles over [t0, t1], where `hi` is above `lo`:
 *  `lo` forward, `hi` back, their crossings inserted (exact between the
 *  breakpoints of planar profiles). */
function bandBetween(t0: number, t1: number, lo: Prof, hi: Prof): SecPt[][] {
  if (!(t1 - t0 > 1e-6)) return [];
  const ts = [...new Set([t0, t1, ...profBreaks(lo, t0, t1), ...profBreaks(hi, t0, t1)])].sort((x, y) => x - y);
  const d = (t: number): number => profAt(hi, t) - profAt(lo, t);
  const S: number[] = [], cross = new Set<number>();
  for (let i = 0; i < ts.length; i++) {
    S.push(ts[i]);
    if (i + 1 < ts.length) {
      const d0 = d(ts[i]), d1 = d(ts[i + 1]);
      if ((d0 > 1e-9 && d1 < -1e-9) || (d0 < -1e-9 && d1 > 1e-9)) { const tc = ts[i] + ((ts[i + 1] - ts[i]) * d0) / (d0 - d1); S.push(tc); cross.add(tc); }
    }
  }
  // A side keeps its own points (a sample stays one), its ends and the crossings.
  // The other side's break set on a chord of a CURVE of this side is a point of
  // that curve's polyline (a sample); on a straight run it says nothing, and goes.
  const kind = (p: Prof, i: number, k: number, j: number): 'v' | 's' | null => {
    const t = S[i];
    if (i === k || i === j || cross.has(t)) return 'v';
    if (typeof p !== 'number' && p.some((q) => Math.abs(q.t - t) < 1e-9)) return profSample(p, t) ? 's' : 'v';
    return profCurve(p, t, t) ? 's' : null;
  };
  /** One side of the ring over S[k] … S[j], forward or back. */
  const side = (p: Prof, k: number, j: number, back: boolean): SecPt[] => {
    const idx: number[] = [];
    for (let i = k; i <= j; i++) idx.push(i);
    if (back) idx.reverse();
    const kept = idx.map((i) => ({ i, f: kind(p, i, k, j) })).filter((x) => x.f);
    return kept.map(({ i, f }, n) => {
      const q: SecPt = { t: S[i], h: profAt(p, S[i]) };
      if (f === 's') q.s = true;
      const nx = kept[n + 1];
      if (nx && profCurve(p, Math.min(S[i], S[nx.i]), Math.max(S[i], S[nx.i]))) q.c = true;
      return q;
    });
  };
  const out: SecPt[][] = [];
  let k = 0;
  while (k + 1 < S.length) {
    if (!((d(S[k]) + d(S[k + 1])) / 2 > 1e-6)) { k++; continue; }
    let j = k + 1;
    while (j + 1 < S.length && (d(S[j]) + d(S[j + 1])) / 2 > 1e-6) j++;
    out.push([...side(lo, k, j, false), ...side(hi, k, j, true)]);
    k = j;
  }
  return out;
}

/** A convex (t, h) polygon clipped to t0 ≤ t ≤ t1. */
function clipT(pts: readonly SecPt[], t0: number, t1: number): SecPt[] {
  const half = (ring: SecPt[], keep: (t: number) => number): SecPt[] => {
    const res: SecPt[] = [];
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i], q = ring[(i + 1) % ring.length];
      const fp = keep(p.t), fq = keep(q.t);
      if (fp >= 0) res.push(p);
      if ((fp > 0 && fq < 0) || (fp < 0 && fq > 0)) {
        const u = fp / (fp - fq);
        res.push({ t: p.t + (q.t - p.t) * u, h: p.h + (q.h - p.h) * u, ...(p.c ? { c: true } : {}) });
      }
    }
    return res;
  };
  return half(half([...pts], (t) => t - t0), (t) => t1 - t);
}

/** A polyline clipped to t0 ≤ t ≤ t1: the pieces inside. */
function clipLineT(pts: readonly SecPt[], t0: number, t1: number): SecPt[][] {
  const out: SecPt[][] = [];
  let cur: SecPt[] = [];
  const inside = (p: SecPt): boolean => p.t >= t0 - 1e-9 && p.t <= t1 + 1e-9;
  const at = (p: SecPt, q: SecPt, t: number): SecPt => {
    const u = (t - p.t) / (q.t - p.t);
    return { t, h: p.h + (q.h - p.h) * u, ...(p.c ? { c: true } : {}) };
  };
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i];
    if (inside(p)) cur.push(p);
    const q = pts[i + 1];
    if (!q) break;
    const lo = Math.min(p.t, q.t), hi = Math.max(p.t, q.t);
    if (inside(p) && inside(q)) continue;
    const ends = [t0, t1].filter((t) => t > lo + 1e-12 && t < hi - 1e-12).sort((a, b) => (q.t > p.t ? a - b : b - a));
    for (const t of ends) {
      const x = at(p, q, t);
      if (inside(p) && cur.length) { cur.push({ ...x, c: undefined }); out.push(cur); cur = []; }
      else cur.push(x);
    }
    if (!inside(q) && cur.length) { out.push(cur); cur = []; }
  }
  if (cur.length) out.push(cur);
  return out.map((l) => cleanPts(l, false)).filter((l) => l.length >= 2);
}

/** A local polygon's centre line, as two local points: for a quad, between the
 *  midpoints of its two shorter opposite sides (a band's ends). */
function centreLine(poly: readonly Vec2[]): [Vec2, Vec2] {
  const mid = (a: Vec2, b: Vec2): Vec2 => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const L = (a: Vec2, b: Vec2): number => Math.hypot(b.x - a.x, b.y - a.y);
  if (poly.length === 4) {
    const [a, b, c, d] = poly;
    return L(a, b) + L(c, d) <= L(b, c) + L(d, a) ? [mid(a, b), mid(c, d)] : [mid(b, c), mid(d, a)];
  }
  let li = 0;
  for (let i = 1; i < poly.length; i++) if (L(poly[i], poly[(i + 1) % poly.length]) > L(poly[li], poly[(li + 1) % poly.length])) li = i;
  const p = poly[li], q = poly[(li + 1) % poly.length], l = L(p, q) || 1, u = { x: (q.x - p.x) / l, y: (q.y - p.y) / l };
  const cx = poly.reduce((s, r) => s + r.x, 0) / poly.length, cy = poly.reduce((s, r) => s + r.y, 0) / poly.length;
  const ss = poly.map((r) => (r.x - cx) * u.x + (r.y - cy) * u.y);
  const s0 = Math.min(...ss), s1 = Math.max(...ss);
  return [{ x: cx + u.x * s0, y: cy + u.y * s0 }, { x: cx + u.x * s1, y: cy + u.y * s1 }];
}

/** A segment clipped to the viewed (+n) side of the plane, or null. */
function clipSegViewed(sec: SectionLine, a: Vec2, b: Vec2): [Vec2, Vec2] | null {
  const { n } = sectionFrame(sec);
  const f = (p: Vec2): number => (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y;
  const fa = f(a), fb = f(b);
  if (fa < 0 && fb < 0) return null;
  if (fa >= 0 && fb >= 0) return [a, b];
  const u = fa / (fa - fb), x = { x: a.x + (b.x - a.x) * u, y: a.y + (b.y - a.y) * u };
  return fa >= 0 ? [a, x] : [x, b];
}

function farDepthOf(sec: Pick<SectionLine, 'a' | 'b' | 'side'>, poly: Vec2[]): number {
  const { n } = sectionFrame(sec);
  let mx = -Infinity;
  for (const p of poly) mx = Math.max(mx, (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y);
  return mx;
}

interface ShapedCtx {
  inst: ProductInstance;
  sec: SectionLine;
  cf: CeilingField;
  tOf(world: Vec2): number;
  /** The surface along a WORLD segment, mapped to t (sorted by t). */
  along(a: Vec2, b: Vec2, which: 'outer' | 'under'): SecPt[];
  /** The same, in the segment's own direction (from a to b). */
  path(a: Vec2, b: Vec2, which: 'outer' | 'under'): SecPt[];
  /** The underside over the cut at chord distances [t0, t1]. */
  chord(t0: number, t1: number, which: 'outer' | 'under'): SecPt[];
  nLocal: Vec2;
}

function shapedCtx(inst: ProductInstance, sec: SectionLine, cf: CeilingField): ShapedCtx {
  const { u, n } = sectionFrame(sec);
  const tOf = (w: Vec2): number => (w.x - sec.a.x) * u.x + (w.y - sec.a.y) * u.y;
  const path = (a: Vec2, b: Vec2, which: 'outer' | 'under'): SecPt[] => {
    const ta = tOf(a), tb = tOf(b), L = Math.hypot(b.x - a.x, b.y - a.y);
    return profileAlong(cf, toInstanceLocal(inst, a), toInstanceLocal(inst, b), which).map((p): SecPt => {
      const q: SecPt = { t: ta + (tb - ta) * (L > 0 ? p.s / L : 0), h: p.h };
      if (p.sample) q.s = true;
      if (p.curveNext) q.c = true;
      return q;
    });
  };
  const along = (a: Vec2, b: Vec2, which: 'outer' | 'under'): SecPt[] => {
    const pts = path(a, b, which);
    if (tOf(b) >= tOf(a)) return pts;
    const r = pts.slice().reverse().map((p) => ({ t: p.t, h: p.h, ...(p.s ? { s: true } : {}) } as SecPt));
    for (let j = 0; j + 1 < r.length; j++) if (pts[pts.length - 2 - j].c) r[j].c = true;
    return r;
  };
  const at = (t: number): Vec2 => ({ x: sec.a.x + u.x * t, y: sec.a.y + u.y * t });
  return { inst, sec, cf, tOf, along, path, chord: (t0, t1, which) => along(at(t0), at(t1), which), nLocal: rotatePoint(n, -inst.rotation) };
}

/** The surface's slope at a piece's centroid along the view direction. */
function viewSlope(x: ShapedCtx, which: 'outer' | 'under', piece: number, ring: readonly Vec2[]): number {
  const c = { x: ring.reduce((s, p) => s + p.x, 0) / ring.length, y: ring.reduce((s, p) => s + p.y, 0) / ring.length };
  const [nx, ny, nz] = surfaceNormal(x.cf, which, piece, c);
  return nz > 1e-12 ? (-nx / nz) * x.nLocal.x + (-ny / nz) * x.nLocal.y : 0;
}

/** A ring's box: x0, y0, x1, y1. */
const boxOf = (r: readonly Vec2[]): [number, number, number, number] => {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of r) { x0 = Math.min(x0, p.x); y0 = Math.min(y0, p.y); x1 = Math.max(x1, p.x); y1 = Math.max(y1, p.y); }
  return [x0, y0, x1, y1];
};
/** Whether a point is near enough a ring's box to lie on the ring (`onRing`'s 1e-6, with room to spare). */
const nearBox = (b: readonly number[], p: Vec2): boolean => p.x > b[0] - 1e-3 && p.y > b[1] - 1e-3 && p.x < b[2] + 1e-3 && p.y < b[3] + 1e-3;

/** Whether a local segment lies on a ring's boundary. */
function onRing(a: Vec2, b: Vec2, ring: readonly Vec2[]): boolean {
  const d = (p: Vec2): number => {
    let m = Infinity;
    for (let i = 0; i < ring.length; i++) {
      const q = ring[i], r = ring[(i + 1) % ring.length], ex = r.x - q.x, ey = r.y - q.y, l2 = ex * ex + ey * ey || 1;
      const u = Math.max(0, Math.min(1, ((p.x - q.x) * ex + (p.y - q.y) * ey) / l2));
      m = Math.min(m, Math.hypot(p.x - q.x - ex * u, p.y - q.y - ey * u));
    }
    return m;
  };
  return d(a) < 1e-6 && d(b) < 1e-6 && d({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }) < 1e-6;
}

/**
 * One surface of the roof standing beyond the plane, one CONVEX piece at a
 * time: the outer surface where it rises away from the viewer (seen from
 * outside), at each piece's nearest depth; the underside where it falls away
 * (seen from inside), at its FARTHEST depth — seen horizontally it never hides
 * what stands under it, only what lies beyond the room. Each clipped to `ivs`.
 * Then the roof's lines (its creases, and its pieces' edges on the outline —
 * a rounded corner's chords as ONE curve), each at the nearest of its pieces'
 * depths, after them. `cut`: the plane crosses the room (else the whole roof is
 * beyond it, and only its outside is seen).
 */
function emitRoof(x: ShapedCtx, api: SecDraw, at: (d: number) => void, ceil: string, which: 'outer' | 'under', ivs: Array<[number, number]>, cut: boolean): void {
  const { inst, sec, cf } = x;
  if (which === 'under' && !cut) return;
  const seen: Array<{ ring: Vec2[]; depth: number; box: [number, number, number, number] }> = [];
  for (const pc of ceilingPieces(cf, which)) {
    const g = viewSlope(x, which, pc.piece, pc.ring);
    if (which === 'outer' ? !(g > 1e-9) : !(g < -1e-9)) continue;
    const world = pc.ring.map((p) => instanceToWorld(inst, p));
    const vis = cut ? clipToViewedSide(sec, world) : world;
    if (vis.length < 3) continue;
    const proj = vis.map((w): SecPt => ({ t: x.tOf(w), h: (which === 'outer' ? outerAt : underAt)(cf, toInstanceLocal(inst, w)) }));
    if (areaTH(proj) < 1e-6) continue;
    const depth = which === 'outer' ? Math.max(nearDepthOf(sec, vis), BEYOND_DEPTH) : Math.max(farDepthOf(sec, vis), BEYOND_DEPTH);
    let drawn = false;
    for (const [i0, i1] of ivs) {
      const c = cleanPts(clipT(proj, i0, i1), true);
      if (c.length < 3 || areaTH(c) < 1e-6) continue;
      at(depth);
      api.poly(c, 'roof', ceil);
      drawn = true;
    }
    if (drawn) seen.push({ ring: pc.ring, depth, box: boxOf(pc.ring) });
  }
  if (!seen.length) return;
  // The lines, as LOCAL polylines: the pieces' edges on the outline — a rounded
  // corner's chords chained into one curve —, then the creases between pieces.
  // `s0` / `s1`: the line starts / ends inside its arc (the next chord is of
  // another depth, or not seen), so that end is a sample too: only the arc's
  // own two ends — a ring vertex with one edge on it — are vertices.
  const lines: Array<{ pts: Vec2[]; curve: boolean; depth: number; edge?: number; s0?: boolean; s1?: boolean }> = [];
  const ring = which === 'outer' ? cf.outerRing : cf.innerRing;
  const corner = ringArcCorners(cf, which);
  const onEdge: Array<Array<{ a: Vec2; b: Vec2; depth: number }>> = ring.map(() => []);
  // Each outline edge's line, to pass over the pieces' edges that lie on none.
  const rl = ring.map((q, j) => {
    const r = ring[(j + 1) % ring.length], L = Math.hypot(r.x - q.x, r.y - q.y) || 1;
    return { q, ux: (r.x - q.x) / L, uy: (r.y - q.y) / L, L };
  });
  const onLine = (j: number, p: Vec2): boolean => {
    const e = rl[j], dx = p.x - e.q.x, dy = p.y - e.q.y, t = dx * e.ux + dy * e.uy;
    return Math.abs(dx * e.uy - dy * e.ux) < 2e-6 && t > -2e-6 && t < e.L + 2e-6;
  };
  // The outline edges whose line each point lies on, found once a point.
  const linesAt = new Map<Vec2, number[]>();
  const linesOf = (p: Vec2): number[] => {
    let l = linesAt.get(p);
    if (!l) { l = []; for (let j = 0; j < ring.length; j++) if (onLine(j, p)) l.push(j); linesAt.set(p, l); }
    return l;
  };
  // Every seen piece's edges, both ways round: a crease is one of them.
  const byEdge = new Map<Vec2, Map<Vec2, number[]>>();
  for (const p of seen) {
    for (let i = 0; i < p.ring.length; i++) {
      const a = p.ring[i], b = p.ring[(i + 1) % p.ring.length];
      for (const [u, v] of [[a, b], [b, a]] as const) {
        let m = byEdge.get(u);
        if (!m) byEdge.set(u, (m = new Map()));
        (m.get(v) ?? m.set(v, []).get(v)!).push(p.depth);
      }
      const lb = linesOf(b), k = linesOf(a).find((j) => lb.includes(j) && onRing(a, b, [ring[j], ring[(j + 1) % ring.length]])) ?? -1;
      if (k < 0) continue;
      const q = ring[k], r = ring[(k + 1) % ring.length];
      const fwd = (b.x - a.x) * (r.x - q.x) + (b.y - a.y) * (r.y - q.y) >= 0;
      onEdge[k].push(fwd ? { a, b, depth: p.depth } : { a: b, b: a, depth: p.depth });
    }
  }
  const near = (p: Vec2, q: Vec2): boolean => Math.hypot(p.x - q.x, p.y - q.y) < 1e-6;
  // Walk the ring from a straight run's start, so no curve is split at index 0.
  const n = ring.length, k0 = Math.max(0, corner.findIndex((c, j) => c !== corner[(j + n - 1) % n]));
  const inArc = (c: number, p: Vec2): boolean => c >= 0 && !ring.some((q, v) => near(p, q) && (corner[v] === c) !== (corner[(v + n - 1) % n] === c));
  type Chain = { pts: Vec2[]; curve: boolean; depth: number; corner: number; edge?: number; s0?: boolean; s1?: boolean };
  let open = null as Chain | null;
  for (let j = 0; j < n; j++) {
    const k = (k0 + j) % n;
    for (const e of onEdge[k].sort((u, v) => Math.hypot(u.a.x - ring[k].x, u.a.y - ring[k].y) - Math.hypot(v.a.x - ring[k].x, v.a.y - ring[k].y))) {
      const c = corner[k];
      const cur = open;
      const onArc: boolean = c >= 0 && !!cur && cur.corner === c && near(cur.pts[cur.pts.length - 1], e.a);
      if (cur && onArc && cur.depth === e.depth) { cur.pts.push(e.b); continue; }
      if (cur) lines.push(inArc(cur.corner, cur.pts[cur.pts.length - 1]) ? { ...cur, s1: true } : cur);
      open = { pts: [e.a, e.b], curve: c >= 0, depth: e.depth, corner: c, edge: k, ...(inArc(c, e.a) ? { s0: true } : {}) };
    }
  }
  if (open) lines.push(inArc(open.corner, open.pts[open.pts.length - 1]) ? { ...open, s1: true } : open);
  // The creases: a straight one is one segment; where a rounded corner's cone
  // runs into the flat or the floor, or two curved pieces meet (a dome's faces),
  // the crease is a CURVE, its chords chained.
  const pcs = (which === 'outer' ? cf.outer : cf.under).pieces;
  const curved: CurveChord[] = [], ends = new Map<string, number>();
  for (const cs of ceilingCreases(cf, which)) {
    if (isFacetSeam(cf, cs)) continue;
    const other = facetCurvePiece(cf, cs);
    const curve = other >= 0 ? `f${other}` : cs.pieces.every((i) => pcs[i].kind !== 'plane') ? `c${cs.pieces.join(',')}` : null;
    if (curve) for (const p of [cs.a, cs.b]) ends.set(chordKey(curve, p), (ends.get(chordKey(curve, p)) ?? 0) + 1);
    const exact = byEdge.get(cs.a)?.get(cs.b);
    const ds = exact ?? seen.filter((p) => nearBox(p.box, cs.a) && nearBox(p.box, cs.b) && onRing(cs.a, cs.b, p.ring)).map((p) => p.depth);
    if (!ds.length) continue;
    if (!curve) lines.push({ pts: [cs.a, cs.b], curve: false, depth: Math.min(...ds) });
    else curved.push({ a: cs.a, b: cs.b, depth: Math.min(...ds), curve });
  }
  lines.push(...chainCurve(curved, ends));
  // Where two pieces' edges meet on a straight outline run under a CURVED surface
  // (a barrel's strips on its end face) the joint is a sample of that curve,
  // unless the surface along the whole run has a true vertex there (its crown).
  const runProf = new Map<number, ProfilePt[]>();
  const curveJoint = (k: number, p: Vec2): boolean => {
    const a = ring[k], b = ring[(k + 1) % n];
    if (near(p, a) || near(p, b)) return false;
    if (!runProf.has(k)) runProf.set(k, profileAlong(cf, a, b, which));
    const sp = Math.hypot(p.x - a.x, p.y - a.y);
    return !runProf.get(k)!.some((q) => !q.sample && Math.abs(q.s - sp) < 1e-6);
  };
  for (const l of lines) {
    // Each chord along the surface; a curve's samples (its inner points, where
    // they are not clipped) are no vertex, and its chords no straight run.
    const prof: SecPt[] = [];
    for (let i = 0; i + 1 < l.pts.length; i++) {
      const wa = instanceToWorld(inst, l.pts[i]), wb = instanceToWorld(inst, l.pts[i + 1]);
      const seg = cut ? clipSegViewed(sec, wa, wb) : [wa, wb] as [Vec2, Vec2];
      if (!seg || Math.hypot(seg[1].x - seg[0].x, seg[1].y - seg[0].y) < 1e-6) continue;
      const piece = x.path(seg[0], seg[1], which);
      if (l.curve) {
        // Inside one chord of a curve every point is a sample — a kink there is
        // where the chord leaves the curve (a bridged hole of its tiling).
        piece.forEach((p, j) => { if (j + 1 < piece.length) p.c = true; if (j > 0 && j + 1 < piece.length) p.s = true; });
        if ((i > 0 || l.s0) && near(seg[0], wa)) piece[0].s = true;
        if ((i + 2 < l.pts.length || l.s1) && near(seg[1], wb)) piece[piece.length - 1].s = true;
      } else if (l.edge != null && piece.length >= 2) {
        if (piece[0].c && near(seg[0], wa) && curveJoint(l.edge, l.pts[i])) piece[0].s = true;
        if (piece[piece.length - 2].c && near(seg[1], wb) && curveJoint(l.edge, l.pts[i + 1])) piece[piece.length - 1].s = true;
      }
      const last = prof[prof.length - 1];
      if (last && Math.abs(last.t - piece[0].t) < 1e-6 && Math.abs(last.h - piece[0].h) < 1e-6) {
        if (piece[0].c) last.c = true;
        if (!piece[0].s) delete last.s;
        prof.push(...piece.slice(1));
      } else {
        if (last) emitRoofLine(api, at, ceil, prof.splice(0), ivs, l.depth);
        prof.push(...piece);
      }
    }
    emitRoofLine(api, at, ceil, prof, ivs, l.depth);
  }
}

/** A chord of a curved crease — a rounded corner's cone running into the flat
 *  or the floor, or two of a dome's faces meeting —, the crease it is part of
 *  (`curve`) and its depth. */
interface CurveChord { a: Vec2; b: Vec2; depth: number; curve: string }
const chordKey = (curve: string, p: Vec2): string => `${curve}:${p.x},${p.y}`;

/** The chords of each curved crease chained end to end into lines. A line
 *  breaks where the next chord is of another depth (or not seen); an end that
 *  another chord of the curve shares (`ends` counts every chord's ends, seen
 *  or not) is a sample (`s0` / `s1`): only the curve's own ends are vertices. */
function chainCurve(chords: readonly CurveChord[], ends: ReadonlyMap<string, number>): Array<{ pts: Vec2[]; curve: boolean; depth: number; s0?: boolean; s1?: boolean }> {
  const at = new Map<string, CurveChord[]>();
  for (const c of chords) for (const p of [c.a, c.b]) { const k = chordKey(c.curve, p); (at.get(k) ?? at.set(k, []).get(k)!).push(c); }
  const shared = (curve: string, p: Vec2): boolean => (ends.get(chordKey(curve, p)) ?? 0) > 1;
  const used = new Set<CurveChord>();
  const out: Array<{ pts: Vec2[]; curve: boolean; depth: number; s0?: boolean; s1?: boolean }> = [];
  const walk = (c0: CurveChord, from: Vec2): void => {
    const pts = [from];
    let c: CurveChord | undefined = c0, p = from;
    while (c && !used.has(c)) {
      used.add(c);
      const q: Vec2 = chordKey(c.curve, c.a) === chordKey(c.curve, p) ? c.b : c.a;
      pts.push(q);
      const next: CurveChord[] = (at.get(chordKey(c.curve, q)) ?? []).filter((d) => !used.has(d) && d.depth === c0.depth);
      c = next.length === 1 ? next[0] : undefined;
      p = q;
    }
    out.push({ pts, curve: true, depth: c0.depth, ...(shared(c0.curve, from) ? { s0: true } : {}), ...(shared(c0.curve, p) ? { s1: true } : {}) });
  };
  const sameDepth = (c: CurveChord, p: Vec2): number => (at.get(chordKey(c.curve, p)) ?? []).filter((d) => d.depth === c.depth).length;
  for (const c of chords) for (const p of [c.a, c.b]) if (!used.has(c) && sameDepth(c, p) !== 2) walk(c, p);
  for (const c of chords) if (!used.has(c)) walk(c, c.a);
  return out;
}

/** One roof line, clipped to the intervals, behind every cut face. */
function emitRoofLine(api: SecDraw, at: (d: number) => void, ceil: string, prof: SecPt[], ivs: Array<[number, number]>, depth: number): void {
  const c = cleanPts(prof, false);
  if (c.length < 2) return;
  for (const [i0, i1] of ivs) {
    for (const piece of clipLineT(c, i0, i1)) {
      at(Math.max(depth, BEYOND_DEPTH));
      api.line(piece, ceil, true);
    }
  }
}

/** A cut wall over [wa, wb] up to the underside `U`, its holes punched: the
 *  pier, the sill and the lintel as polygons, the glass and the window clipped
 *  by the ceiling — a pane under a slope is a trapezoid. */
function drawCutWallShaped(api: SecDraw, wa: number, wb: number, U: Prof, wallCol: string, holes: WallHole[], room: SecSrc): void {
  const clipped = holes
    .map((h) => ({ ...h, t0: Math.max(wa, Math.min(wb, h.t0)), t1: Math.max(wa, Math.min(wb, h.t1)) }))
    .filter((h) => h.t1 - h.t0 > 1e-3)
    .sort((a, b) => a.t0 - b.t0);
  const pieces: SecPt[][] = [];
  const panes: Array<() => void> = [];
  const solid = (t0: number, t1: number, lo: Prof, hi: Prof): void => { pieces.push(...bandBetween(t0, t1, lo, hi)); };
  let cur = wa;
  for (const h of clipped) {
    if (h.t0 > cur + 1e-3) solid(cur, h.t0, 0, U);
    const s0 = Math.max(cur, h.t0), s1 = Math.max(s0, h.t1);
    if (s1 > s0 + 1e-3) {
      if (h.sill > 0) solid(s0, s1, 0, profMin(h.sill, U, s0, s1));
      solid(s0, s1, h.arch, U);
      if (h.kind !== 'gap') {
        panes.push(() => {
          api.src(h.src);
          for (const r of bandBetween(s0, s1, h.sill, profMin(h.arch, U, s0, s1))) {
            if (h.kind === 'glass') emitPoly(api, r, 'glass', h.color ?? '');
            else emitPoly(api, r, 'window', wallCol);
          }
          api.src(room);
        });
      }
    }
    cur = Math.max(cur, h.t1);
  }
  if (cur < wb - 1e-3) solid(cur, wb, 0, U);
  for (const r of clipped.length ? seamless(pieces) : pieces) emitPoly(api, r, 'cut', wallCol);
  for (const p of panes) p();
}

/** A wall face in elevation up to `wtop`, its openings left clear. */
function fillElevWallShaped(api: SecDraw, t0: number, t1: number, wtop: Prof, wallCol: string, holes: Array<{ a: number; b: number; sill: number; arch: number }>): void {
  const clipped = holes
    .map((h) => ({ ...h, a: Math.max(t0, Math.min(t1, h.a)), b: Math.max(t0, Math.min(t1, h.b)) }))
    .filter((h) => h.b - h.a > 1e-3)
    .sort((x, y) => x.a - y.a);
  const wall = (a: number, b: number, lo: Prof, hi: Prof): void => { for (const r of bandBetween(a, b, lo, hi)) emitPoly(api, r, 'band', wallCol, 1); };
  let cur = t0;
  for (const h of clipped) {
    if (h.a > cur + 1e-3) wall(cur, h.a, 0, wtop);
    const s0 = Math.max(cur, h.a), s1 = Math.max(s0, h.b);
    if (s1 > s0 + 1e-3) {
      if (h.sill > 0) wall(s0, s1, 0, profMin(h.sill, wtop, s0, s1));
      wall(s0, s1, h.arch, wtop);
    }
    cur = Math.max(cur, h.b);
  }
  if (cur < t1 - 1e-3) wall(cur, t1, 0, wtop);
}

/** The holes of a room's outer walls seen in elevation: its openings and its
 *  glazing (panes and the door), as spans along t. */
function elevationHoles(inst: ProductInstance, sec: SectionLine, t0: number, t1: number): {
  openHoles: ElevHole[];
  glassHoles: Array<ElevHole & { color?: string; glazed?: true }>;
} {
  // A hole running round a corner — one on each side of it — leaves the plan no
  // wall there: each reaches its face's end at the outer corner, though a pane's
  // glass, mitred into the other's, stops short of it.
  const c = holeCorners(inst);
  const round = (seg: GlassSegment): Vec2[] => {
    const i = seg.edge, e = i != null && seg.innerWallId == null ? c?.edges[i] : undefined;
    if (!c || !e) return [];
    const j = (i! + 1) % c.n, out: Vec2[] = [];
    if (Math.min(seg.start, seg.end) <= 1e-3 && !isJoint(inst, i!) && c.ends(i!)) out.push(instanceToWorld(inst, e.a));
    if (Math.max(seg.start, seg.end) >= e.len - 1e-3 && !isJoint(inst, j) && c.starts(j)) out.push(instanceToWorld(inst, e.b));
    return out;
  };
  const openHoles: ElevHole[] = [];
  for (const seg of inst.openings || []) {
    if (seg.pocket && seg.pocket.from > 0.5) continue; // a pocket in the inner face: the outer one stays whole
    const band = [...openingHoleBand(inst, seg).map((p) => instanceToWorld(inst, p)), ...round(seg)];
    if (band.length < 3 || !viewedSide(sec, band)) continue;
    const [omn, omx] = projectSpan(sec, band);
    const a = Math.max(t0, omn), b = Math.min(t1, omx);
    if (b - a > 1e-3) openHoles.push({ a, b, sill: openingSill(seg), arch: openingArchitrave(inst, seg), host: hostLine(inst, seg), src: openingSrc(inst, seg) });
  }
  const glassHoles: Array<ElevHole & { color?: string; glazed?: true }> = [];
  for (const { seg, src, glazedH } of glazingOf(inst)) {
    const gfp = [...glassPolysLocal(inst, seg).flat().map((p) => instanceToWorld(inst, p)), ...round(seg)];
    if (gfp.length < 3 || !viewedSide(sec, gfp)) continue;
    const [gmn, gmx] = projectSpan(sec, gfp);
    const a = Math.max(t0, gmn), b = Math.min(t1, gmx);
    if (b - a <= 1e-3) continue;
    // A glazing's run is a hole up to its height, the glazing drawn apart.
    if (glazedH != null) glassHoles.push({ a, b, sill: 0, arch: glazedH, host: hostLine(inst, seg), src, glazed: true });
    else glassHoles.push({ a, b, sill: glassPaneBase(seg), arch: glassPaneTop(inst, seg), color: seg.color, host: hostLine(inst, seg), src });
  }
  return { openHoles, glassHoles };
}

/** The columns of a face's glazings' runs: holes no call of the face paints in
 *  (the glazing is its own emitter), which the occlusion must see through. */
function glazedRuns(holes: ReadonlyArray<ElevHole & { glazed?: true }>, s0: number, s1: number): Array<[number, number]> {
  return holes.filter((g) => g.glazed && g.a < s1 - 1e-3 && g.b > s0 + 1e-3).map((g) => [Math.max(g.a, s0), Math.min(g.b, s1)]);
}

/** A pane's, an opening's or a door's hole in a room seen in elevation, with the
 *  line of the outer face that holds it (`hostLine`). */
interface ElevHole { a: number; b: number; sill: number; arch: number; host: [Vec2, Vec2] | null; src: SecSrc }

/** The line of the outer face a feature stands in — its segment of the room's
 *  outline (a polygon edge, or a legacy rectangle's side), in world — or null
 *  for one glazed into a partition, which no outer face holds. */
function hostLine(inst: ProductInstance, seg: GlassSegment): [Vec2, Vec2] | null {
  if (seg.innerWallId != null) return null;
  const poly = inst.polygon;
  let a: Vec2, b: Vec2;
  if (seg.edge != null && poly && poly.length >= 3) {
    if (!(seg.edge >= 0 && seg.edge < poly.length)) return null;
    a = poly[seg.edge]; b = poly[(seg.edge + 1) % poly.length];
  } else {
    const hw = inst.width / 2, hd = inst.depth / 2;
    [a, b] = seg.wall === 'back' ? [{ x: hw, y: hd }, { x: -hw, y: hd }]
      : seg.wall === 'left' ? [{ x: -hw, y: hd }, { x: -hw, y: -hd }]
        : seg.wall === 'right' ? [{ x: hw, y: -hd }, { x: hw, y: hd }]
          : [{ x: -hw, y: -hd }, { x: hw, y: -hd }];
  }
  return [instanceToWorld(inst, a), instanceToWorld(inst, b)];
}

/** The holes a wall FACE holds: those whose host segment lies on it — on its
 *  line, sharing a stretch of its run (a face joins the in-line segments a
 *  wall's panes split it into, and stops where a rounded corner's arc begins,
 *  which may cover the host's middle). A face is never pierced by a feature of
 *  another wall:
 *  a pane in the FAR wall is no window in the near one, a pane at a corner none
 *  in the wall beside it, and one in a partition none in the outline (the solid
 *  walls hide them). */
function holesOnFace<H extends ElevHole>(face: readonly Vec2[], holes: readonly H[]): H[] {
  const [a, b] = face;
  const L = Math.hypot(b.x - a.x, b.y - a.y);
  if (L < 1e-9) return [];
  const ux = (b.x - a.x) / L, uy = (b.y - a.y) / L;
  const off = (p: Vec2): number => Math.abs((p.x - a.x) * uy - (p.y - a.y) * ux);
  const along = (p: Vec2): number => (p.x - a.x) * ux + (p.y - a.y) * uy;
  return holes.filter(({ host }) => {
    if (!host || off(host[0]) > 1e-3 || off(host[1]) > 1e-3) return false;
    const s0 = along(host[0]), s1 = along(host[1]);
    return Math.min(L, Math.max(s0, s1)) - Math.max(0, Math.min(s0, s1)) > 1e-3;
  });
}

/** A shaped room the plane does not cut: each wall face turned away from the
 *  viewer, its roof pieces, then each face turned toward the viewer — the wall
 *  up to where it meets the underside, the shell's edge (the fascia) above it,
 *  the silhouette up to the roof, and the glazing clipped by the ceiling. On a
 *  depth tie (pieces sharing the room's nearest point) a face toward the viewer
 *  stays over the roof — a gable's end wall over the pitch receding from its
 *  top — and the roof over a face turned away. */
function shapedElevation(x: ShapedCtx, api: SecDraw, at: (d: number, plan?: SecPlan) => void, len: number): void {
  const { inst, sec, cf } = x;
  const fp = instanceFootprint(inst);
  const [pmn, pmx] = projectSpan(sec, fp);
  const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
  if (t1 - t0 <= 1e-3) return;
  const wallCol = wallColorOf(inst);
  const faces = shapedElevationFaces(x, len);
  const shown = occludeFaces(faces);
  const room = srcOf(inst.id);
  const emit = (away: boolean): void => faces.forEach((f, i) => {
    if (f.away !== away) return;
    const p = facePlan(f);
    for (const s of shown[i]) { at(s.d, p); replayCall(s.call, api); }
  });
  emit(true);
  drawRoomContents(inst, sec, api, at, len, x);
  api.src(room);
  if (cf.thickness > 0) emitRoof(x, api, at, ceilingHex6(inst, wallCol), 'outer', [[t0, t1]], false);
  else edgeOnWalls(x, api, at, wallFaces(fp).filter((f) => viewedSide(sec, f)), polygonArea(fp) > 0 ? 1 : -1, t0, t1, wallCol);
  emit(false);
}

/** The shaped room's wall faces in elevation, each with its calls: the wall up
 *  to where it meets the underside, the fascia above it, the silhouette up to
 *  the roof, the glazing clipped by the ceiling; then its corners'
 *  stand-ins (`cornerStandIns`), with no hole, among the faces turned away. */
function shapedElevationFaces(x: ShapedCtx, len: number): ElevFace[] {
  const { inst, sec, cf } = x;
  const fp = instanceFootprint(inst);
  const wallCol = wallColorOf(inst), ceil = ceilingHex6(inst, wallCol);
  const [pmn, pmx] = projectSpan(sec, fp);
  const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
  if (t1 - t0 <= 1e-3) return [];
  const T = cf.thickness;
  const { openHoles, glassHoles } = elevationHoles(inst, sec, t0, t1);
  const within = (a: number, b: number, s0: number, s1: number): boolean => a < s1 - 1e-3 && b > s0 + 1e-3;
  const { n } = sectionFrame(sec);
  const sgn = polygonArea(fp) > 0 ? 1 : -1;
  const room = srcOf(inst.id);
  const out: ElevFace[] = [];
  const standIns = cornerStandIns(inst, sec);
  for (const face of [...wallFaces(fp), ...standIns].filter((f) => viewedSide(sec, f))) {
    const own = !standIns.includes(face);
    const [fmn, fmx] = projectSpan(sec, face);
    const s0 = Math.max(t0, fmn), s1 = Math.min(t1, fmx);
    if (s1 - s0 <= 1e-3) continue;
    const calls: SecCall[] = [];
    const api = recorder(calls);
    api.src(room);
    const top = x.along(face[0], face[1], 'outer'), wtop = x.along(face[0], face[1], 'under');
    const faceOpen = own ? holesOnFace(face, openHoles) : [], faceGlass = own ? holesOnFace(face, glassHoles) : [];
    fillElevWallShaped(api, s0, s1, wtop, wallCol, [...faceOpen, ...faceGlass]);
    if (T > 0 && own) for (const r of bandBetween(s0, s1, wtop, top)) emitPoly(api, r, 'band', ceil, 1); // over a stand-in, the shell's inside
    for (const r of bandBetween(s0, s1, 0, own ? top : wtop)) emitPoly(api, r, 'outline', wallCol);
    for (const g of faceGlass) {
      if (g.glazed || !within(g.a, g.b, s0, s1)) continue;
      const a = Math.max(g.a, s0), b = Math.min(g.b, s1);
      api.src(g.src);
      for (const r of bandBetween(a, b, g.sill, profMin(g.arch, wtop, a, b))) emitPoly(api, r, 'glass', g.color ?? '');
    }
    for (const h of faceOpen) {
      if (!within(h.a, h.b, s0, s1)) continue;
      const a = Math.max(h.a, s0), b = Math.min(h.b, s1);
      api.src(h.src);
      for (const r of bandBetween(a, b, h.sill, profMin(h.arch, wtop, a, b))) emitPoly(api, r, 'window', wallCol);
    }
    const dx = face[1].x - face[0].x, dy = face[1].y - face[0].y;
    out.push({ ends: faceEnds(sec, face), s0, s1, d: nearDepthOf(sec, face), calls, open: glazedRuns(faceGlass, s0, s1), away: !own || sgn * (dy * n.x - dx * n.y) > 0, inner: own ? innerEnds(sec, face, sgn, cf.wallT) : undefined });
  }
  return out;
}

/** Under a 0 mm ceiling nothing hides the wall tops: a wall seen END ON (its
 *  outer face under 10 mm across the view, `underBand`'s rule) still shows its
 *  thickness, up to the highest point of its top along each face — a gable's
 *  end wall, seen from an eave side, rises to the crown. Before the faces toward
 *  the viewer, which stand in front of it. */
function edgeOnWalls(x: ShapedCtx, api: SecDraw, at: (d: number, plan?: SecPlan) => void, faces: readonly Vec2[][], sgn: number, t0: number, t1: number, wallCol: string): void {
  const { sec, cf } = x, w = cf.wallT, fp = instanceFootprint(x.inst);
  if (!(w > 0)) return;
  for (const [a, b] of faces) {
    const [fmn, fmx] = projectSpan(sec, [a, b]);
    const dx = b.x - a.x, dy = b.y - a.y, L = Math.hypot(dx, dy);
    if (fmx - fmn >= 10 || L < 1e-9) continue;
    const inw = { x: (-sgn * dy * w) / L, y: (sgn * dx * w) / L };
    const ga = { x: a.x + inw.x, y: a.y + inw.y }, gb = { x: b.x + inw.x, y: b.y + inw.y };
    const band = [a, b, gb, ga];
    const [bmn, bmx] = projectSpan(sec, band);
    const s0 = Math.max(t0, bmn), s1 = Math.min(t1, bmx);
    if (s1 - s0 <= 1e-3) continue;
    // Its top's highest point along the outer face and along the inner one
    // (where that line is in the room), each over its own side of the band.
    const hOut = Math.max(...x.along(a, b, 'under').map((p) => p.h));
    let hIn = -Infinity;
    for (const [i0, i1] of segmentPolyIntervals(ga, gb, fp)) {
      const p0 = { x: ga.x + (gb.x - ga.x) * (i0 / L), y: ga.y + (gb.y - ga.y) * (i0 / L) };
      const p1 = { x: ga.x + (gb.x - ga.x) * (i1 / L), y: ga.y + (gb.y - ga.y) * (i1 / L) };
      for (const p of x.along(p0, p1, 'under')) hIn = Math.max(hIn, p.h);
    }
    const tOut = x.tOf({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }), tIn = x.tOf({ x: (ga.x + gb.x) / 2, y: (ga.y + gb.y) / 2 });
    const ends: SecPt[] = [{ t: tOut, h: hOut }, { t: tIn, h: Number.isFinite(hIn) ? hIn : hOut }];
    const top: Prof = ends.sort((p, q) => p.t - q.t);
    at(nearDepthOf(sec, band), { ring: band });
    for (const r of bandBetween(s0, s1, 0, top)) emitPoly(api, r, 'band', wallCol, 1);
    for (const r of bandBetween(s0, s1, 0, top)) emitPoly(api, r, 'outline', wallCol);
  }
}

/** A band standing beyond the cut (a pane, a partition, a heater wall) whose
 *  top follows the underside along its centre line, capped at `cap`, from `base`
 *  up, over the t spans `pieces`. A band seen end on (its centre line shorter
 *  than 10 mm across the view) is a rectangle up to its highest top. */
function underBand(x: ShapedCtx, api: SecDraw, local: readonly Vec2[], base: number, cap: number, pieces: Array<[number, number]>, style: PolyStyle, color: string): void {
  const [c0, c1] = centreLine(local);
  const w0 = instanceToWorld(x.inst, c0), w1 = instanceToWorld(x.inst, c1);
  const seg = clipSegViewed(x.sec, w0, w1) ?? [w0, w1];
  const U = x.along(seg[0], seg[1], 'under');
  const lo = U[0].t, hi = U[U.length - 1].t;
  const top: Prof = hi - lo < 10 ? Math.min(cap, Math.max(...U.map((p) => p.h))) : profMin(cap, U, lo, hi);
  for (const [a, b] of pieces) for (const r of bandBetween(a, b, base, top)) emitPoly(api, r, style, color);
}

/** A shaped room the plane cuts (§ *Sections* in docs/claude/sections.md, the shaped rules). */
function shapedSection(x: ShapedCtx, api: SecDraw, at: (d: number, plan?: SecPlan) => void): boolean {
  const { inst, sec, cf } = x;
  const outerIv = segmentPolyIntervals(sec.a, sec.b, instanceFootprint(inst));
  if (!outerIv.length) return false;
  const wallCol = wallColorOf(inst), ceil = ceilingHex6(inst, wallCol);
  const T = cf.thickness;
  const inner = innerFootprint(inst);
  const innerIv = inner.length >= 3 ? segmentPolyIntervals(sec.a, sec.b, inner) : [];
  const holes = cutWallHoles(inst, sec);
  const room = srcOf(inst.id);
  api.src(room);
  // 2. The underside beyond the cut, seen from inside (a 0 mm ceiling has none).
  if (T > 0) emitRoof(x, api, at, ceil, 'under', innerIv, true);
  // 3. The cut walls, up to the underside over them.
  at(CUT_DEPTH);
  for (const [o0, o1] of outerIv) {
    for (const [w0, w1] of subtractSpans(o0, o1, innerIv)) drawCutWallShaped(api, w0, w1, x.chord(w0, w1, 'under'), wallCol, holes, room);
  }
  // 4. The shell, cut: solid between the underside and the roof.
  at(CUT_DEPTH);
  if (T > 0) for (const [o0, o1] of outerIv) for (const r of bandBetween(o0, o1, x.chord(o0, o1, 'under'), x.chord(o0, o1, 'outer'))) emitPoly(api, r, 'cut', ceil);
  const spans = innerIv.length ? innerIv : outerIv;
  const clipSpans = (a: number, b: number): Array<[number, number]> => spans
    .map(([s0, s1]): [number, number] => [Math.max(s0, a), Math.min(s1, b)])
    .filter(([s0, s1]) => s1 - s0 > 1e-3);
  // 1. The walls seen from inside, up to the underside.
  drawBackWalls(inst, sec, api, at, clipSpans, 0, x);
  api.src(room);
  // 5. The room's corners beyond the cut, up to the underside at each.
  const seen: number[] = [];
  for (const corner of inner) {
    if (!viewedSide(sec, [corner])) continue;
    const t = projectSpan(sec, [corner])[0];
    if (!spans.some(([s0, s1]) => t >= s0 - 0.5 && t <= s1 + 0.5)) continue;
    if (seen.some((s) => Math.abs(s - t) < 1)) continue;
    seen.push(t);
    at(nearDepthOf(sec, [corner]), { ring: [corner] });
    api.vedge(t, 0, underAt(cf, toInstanceLocal(inst, corner)), wallCol);
  }
  // 6. Panes (and the door) beyond the cut, from their own base, under the ceiling.
  for (const { seg, src, glazedH } of glazingOf(inst)) {
    if (glazedH != null) continue;
    const polys = glassPolysLocal(inst, seg).filter((q) => q.length >= 3);
    const fp = polys.flat().map((p) => instanceToWorld(inst, p));
    if (fp.length < 3 || segmentPolyChord(sec.a, sec.b, fp) || !viewedSide(sec, fp)) continue;
    api.src(src);
    for (const q of polys) {
      const w = q.map((p) => instanceToWorld(inst, p));
      const [pmn, pmx] = projectSpan(sec, w);
      const pieces = clipSpans(pmn, pmx);
      if (!pieces.length) continue;
      at(nearDepthOf(sec, w), { ring: w });
      underBand(x, api, q, glassPaneBase(seg), glassPaneTop(inst, seg), pieces, 'glass', seg.color ?? '');
    }
  }
  // 7. Benches, heater walls and partitions.
  drawRoomInterior(inst, sec, api, at, clipSpans, x);
  api.src(room);
  // 8. The roof's outside beyond the cut, LAST of the room: everything in the
  // room standing above the shell at the cut lies behind the roof, so a band the
  // plane crosses (tagged like the roof, just beyond the plane) loses the tie.
  if (T > 0) emitRoof(x, api, at, ceil, 'outer', outerIv, true);
  return true;
}

// The recorded calls of each room and extruded polygon seen by each section: a
// section replays its emitter several times per mousemove (hover, snapping, the
// extent, the draw), and a miss records it again (a shaped room's tiling, every
// object's faces clipped by each other).
type ShapedEvent = { d: number; p?: SecPlan } | SecCall;

/** The recorded events, ONE cache for every kind: a SLOT per (kind, section,
 *  object), holding the events of the last key it was asked for — a section
 *  dragged overwrites its slots instead of keeping every position it passed. A
 *  key is fresh plain data and, for a room, the fields of the instance a
 *  section reads (`readFields`, kept as a copy: the instance is edited in
 *  place), each compared as data (`sameData`, `sameFields`), never built into
 *  a string. Least recently used first out, bounded by an estimate of the
 *  memory the entries hold (`eventsBytes`): what outlives its use is a slot of
 *  a deleted object or section. */
const EVENTS = new Map<string, { key: unknown; fields: Fields | null; ev: ShapedEvent[]; bytes: number }>();
let eventsHeld = 0, eventsLast = 0, eventsBuilt = 0;
/** The memory the cache may hold, as `eventsBytes` estimates it: some 8000 flat
 *  rooms' (section, room) pairs, 450 shaped rooms' with rounded corners, 100
 *  rounded domes'. */
let eventsBudget = 48e6;


/** An estimate of the memory an entry holds, from its key and its events: about
 *  140 bytes an event and 70 a point — V8's heap measured 0.8 … 1.3× of it for
 *  flat, rounded and sloped rooms, a rounded dome and a barrel (the most). */
function eventsBytes(key: unknown, ev: readonly ShapedEvent[]): number {
  const data = (v: unknown): number => {
    if (typeof v === 'string') return 16 + 2 * v.length;
    if (typeof v !== 'object' || v === null) return 8;
    if (v instanceof Float64Array) return 40 + 8 * v.length;
    let n = 40;
    if (Array.isArray(v)) for (const x of v) n += data(x);
    else for (const k in v) n += 8 + data((v as Record<string, unknown>)[k]);
    return n;
  };
  let n = 100 + data(key);
  for (const e of ev) {
    n += 140;
    if ('a' in e) for (const a of e.a) if (Array.isArray(a)) n += 70 * a.length;
  }
  return n;
}

/** The events `build` records for `key` and the fields `inst` reads (null:
 *  no instance), kept in `slot` of the one cache. */
function cachedEvents(slot: string, key: unknown, inst: ProductInstance | null, build: (ev: ShapedEvent[]) => void): ShapedEvent[] {
  const old = EVENTS.get(slot);
  EVENTS.delete(slot);
  if (old && sameData(old.key, key) && (!inst || (old.fields && sameFields(old.fields, inst)))) { EVENTS.set(slot, old); return old.ev; }
  if (old) eventsHeld -= old.bytes;
  const fields = inst ? readFields(inst) : null;
  const ev: ShapedEvent[] = [];
  build(ev);
  const bytes = eventsBytes([key, fields?.v ?? null], ev);
  EVENTS.set(slot, { key, fields, ev, bytes });
  eventsHeld += bytes;
  eventsLast = bytes;
  eventsBuilt++;
  for (const [k, v] of EVENTS) {
    if (eventsHeld <= eventsBudget || k === slot) break;
    EVENTS.delete(k);
    eventsHeld -= v.bytes;
  }
  return ev;
}

/** Test seam: the entries and the estimated memory the events cache holds, the
 *  estimate of the last entry recorded, how many it has recorded in all, and its
 *  budget (set with `budget`, the old one returned). */
export function sectionCacheHeld(budget?: number): { entries: number; bytes: number; last: number; built: number; budget: number } {
  const old = eventsBudget;
  if (budget != null) eventsBudget = budget;
  return { entries: EVENTS.size, bytes: eventsHeld, last: eventsLast, built: eventsBuilt, budget: old };
}

/** Instance fields no section ever reads, left out of a room's cache key: a
 *  product's DXF drawing or raster image would be compared on every replay,
 *  and a renamed or regrouped room recorded again. The bundled section module
 *  names none of them (test/section.test.mjs checks). A duct fan's figures are
 *  its own `fanModel` (partModel.ts), in the key like every other field. */
const UNREAD_FIELDS = new Set([...HEAVY_FIELDS, 'name', 'groupId', 'groupName', 'layer', 'partId', 'relId', 'frozen', 'lockResize', 'roomFixture', 'powerPerMeter', 'powerPerSqm', 'cubeSnap', 'roomAnchor']);

/** An instance's fields a section reads, copied, and how many. */
interface Fields { v: Record<string, unknown>; n: number }

function readFields(inst: ProductInstance): Fields {
  const v: Record<string, unknown> = {}, o = inst as unknown as Record<string, unknown>;
  let n = 0;
  for (const k in o) if (o[k] !== undefined && !UNREAD_FIELDS.has(k)) { v[k] = copyData(o[k]); n++; }
  return { v, n };
}

/** Does `inst` still hold the fields `kept` read from it? */
function sameFields(kept: Fields, inst: ProductInstance): boolean {
  const o = inst as unknown as Record<string, unknown>;
  let n = 0;
  for (const k in o) {
    if (o[k] === undefined || UNREAD_FIELDS.has(k)) continue;
    if (!sameData(kept.v[k], o[k])) return false;
    n++;
  }
  return n === kept.n;
}

/** The room a ceiling-grip drag holds: its section views replay their last
 *  recording until it is released (null) — main.ts `holdSections`, once the drag
 *  rests or ends. No clock here, so a frame however slow keeps it. */
let heldRoom: string | null = null;
export function holdRoomSections(id: string | null): void { heldRoom = id; }

function shapedEvents(inst: ProductInstance, sec: SectionLine, len: number): ShapedEvent[] {
  roomDoors(inst); // a legacy door is moved into `doors` when first read: before the key, not during the build
  const slot = `shaped:${sec.id}:${inst.id}`, kept = heldRoom === inst.id ? EVENTS.get(slot) : undefined;
  if (kept) return kept.ev;
  return cachedEvents(slot, [sec.a.x, sec.a.y, sec.b.x, sec.b.y, sec.side, len], inst, (ev) => {
    const api = recorder(ev);
    api.src(srcOf(inst.id));
    const at = (d: number, p?: SecPlan): void => { ev.push(p ? { d, p } : { d }); };
    const x = shapedCtx(inst, sec, resolveCeiling(inst));
    if (!shapedSection(x, api, at)) {
      const fp = instanceFootprint(inst);
      if (fp.length >= 3 && viewedSide(sec, fp)) shapedElevation(x, api, at, len);
    }
  });
}

/** Draw a shaped room (cut or in elevation) from its recorded calls. */
function replayShaped(inst: ProductInstance, sec: SectionLine, api: SecDraw, len: number, depth: Pick<DepthOrderedDraw, 'at'> | undefined): void {
  replayEvents(shapedEvents(inst, sec, len), api, depth);
}

/** An extruded polygon beyond the cut, face by face — an `elev` over each face's
 *  columns, drawn where no nearer face of it stands in front —, recorded once
 *  per section, polygon and colour: one with no fill takes the drawing's, and
 *  the canvas's is not the collectors', so in one slot the two would record it
 *  in turn on every replay. */
function polygonElevation(sec: SectionLine, id: string, fp: Vec2[], len: number, base0: number, top: number, color: string): ShapedEvent[] {
  const pts = new Float64Array(2 * fp.length);
  fp.forEach((p, i) => { pts[2 * i] = p.x; pts[2 * i + 1] = p.y; });
  return cachedEvents(`poly:${sec.id}:${id}:${color}`, [sec.a.x, sec.a.y, sec.b.x, sec.b.y, sec.side, len, pts, base0, top, color], null, (ev) => {
    const { u, n } = sectionFrame(sec);
    const at = (w: Vec2): { t: number; d: number } => ({ t: (w.x - sec.a.x) * u.x + (w.y - sec.a.y) * u.y, d: (w.x - sec.a.x) * n.x + (w.y - sec.a.y) * n.y });
    const ps = fp.map(at);
    const faces: SecFace[] = [];
    for (let i = 0; i < ps.length; i++) {
      // `faceEnds`, `viewedSide`, `projectSpan` and `nearDepthOf` of the face, the frame found once.
      const j = (i + 1) % ps.length, p = ps[i], q = ps[j];
      if (!(((fp[i].x + fp[j].x) / 2 - sec.a.x) * n.x + ((fp[i].y + fp[j].y) / 2 - sec.a.y) * n.y > 0)) continue; // behind the plane
      const t0 = Math.max(0, Math.min(p.t, q.t)), t1 = Math.min(len, Math.max(p.t, q.t));
      if (t1 - t0 <= 1e-3) continue; // edge-on to the view
      faces.push({ ends: [p, q], s0: t0, s1: t1, d: Math.min(p.d, q.d), calls: [{ k: 'elev', a: [t0, t1, base0, top, color] }] });
    }
    pushShown(ev, faces);
  });
}

/** Emit the generated section's geometry — cut profiles, projected elevations,
 *  room walls/benches, heater covers — through the given `SecDraw`. Shared by
 *  `drawSection` (which draws it) and `sectionVertices` (which collects its
 *  corners for snapping), so both stay in lock-step. */
function emitSectionGeometry(sec: SectionLine, instances: ProductInstance[], walls: Wall[], len: number, col: string, rawApi: SecDraw, polygons: Polygon[] = [], ordered = true, pipes: readonly SectionPipe[] = []): void {
  // Every call is tagged with its own depth and replayed farthest-first, so
  // occlusion is decided per piece rather than per object.
  const api = depthOrdered(rawApi, sec, ordered);
  const { profile, elev } = api;
  // The parts set into walls (a control unit's display) cut them: a pocket in a
  // drawn wall (below), a hole through a room's (among its openings).
  const cutParts = inWallParts(instances);
  instances = instances.map((i) => (i.autoBenches ? withWallCuts(i, cutParts) : i));

  // Painter's-algorithm depth: an object's distance from the cut plane along the
  // view direction (`+n`). Use the NEAREST footprint point — the face closest to
  // the viewer. Objects that straddle the plane (a cut) reach ≤ 0, so they sort
  // to the very front. Emitters are then drawn farthest-first (largest depth) so
  // a nearer object correctly occludes what sits behind it in the elevation.
  const nearDepth = (poly: Vec2[]): number => nearDepthOf(sec, poly);
  const emitters: Array<{ depth: number; obj: number; run: () => void }> = [];
  let objects = 0;

  // One object's body over one footprint: cut where the plane crosses it,
  // elevation where it stands beyond. Shared so a node object's per-segment
  // pieces and a plain box go through exactly the same rule.
  const drawBody = (inst: ProductInstance, fp: Vec2[], [e0, e1] = instanceElevation(inst), color = inst.color || col): void => {
    const chord = segmentPolyChord(sec.a, sec.b, fp);
    if (chord) { api.at(CUT_DEPTH); profile(chord[0], chord[1], e0, e1, false, color); return; } // cut: hatched
    if (!viewedSide(sec, fp)) return; // behind the view direction
    const [pmn, pmx] = projectSpan(sec, fp);
    const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
    if (t1 - t0 > 1e-3) { api.at(nearDepth(fp), { ring: fp }); elev(t0, t1, e0, e1, color); }
  };

  // A tilted body in the view: its outline beyond the cut, then its cut — each
  // clipped to the view's length.
  const { u: cutU, n: cutN } = sectionFrame(sec);
  const drawTiltedBody = (body: ReturnType<typeof bodyOf>, plan: Vec2[], color: string): void => {
    const { cut, seen } = bodyInSection(body, { a: sec.a, u: cutU, look: cutN });
    const th = (ring: Vec2[]): SecPt[] => clipToSpan(ring, 0, len).map((q) => ({ t: q.x, h: q.y }));
    if (seen) { api.at(beyondDepthOf(sec, plan), { ring: plan }); emitPoly(api, th(seen), 'elev', color); }
    if (cut) { api.at(CUT_DEPTH); emitPoly(api, th(cut), 'cut-hatched', color); }
  };

  // A part with a 3D model (partModel.ts): its solids beyond the plane, the far
  // ones first, then their slices where it cuts them — hatched, the water
  // see-through —, each solid's pieces drawn without the seams between them.
  const drawModel = (inst: ProductInstance, body: ReturnType<typeof bodyOf>, plan: Vec2[]): void => {
    const solids = modelInSection(inst, body, { a: sec.a, u: cutU, look: cutN }, [0, len]);
    const th = (pc: ModelPiece): SecPt[] => pc.pts.map((q, i) => ({ t: q.x, h: q.y, ...(pc.hidden[i] ? { x: true } : {}) }));
    api.at(beyondDepthOf(sec, plan), { ring: plan });
    for (const sd of solids) for (const pc of sd.seen) emitPoly(api, th(pc), sd.water ? 'band' : 'elev', sd.color, sd.water ? WATER_SEEN : undefined);
    api.at(CUT_DEPTH);
    for (const sd of solids) for (const pc of sd.cut) emitPoly(api, th(pc), sd.water ? 'band' : 'cut-hatched', sd.color, sd.water ? WATER_CUT : undefined);
  };

  // A room's glazing (roomGlazing.ts): its solids beyond the plane, then their
  // slices — the glass, the seals and the silicone see-through.
  const drawGlazing = (inst: ProductInstance, g: RoomGlazing): void => {
    const solids = glazingInSection(glazingWorldSolids(inst, g, wallColorOf(inst)), { a: sec.a, u: cutU, look: cutN }, len);
    const th = (pc: ModelPiece): SecPt[] => pc.pts.map((q, i) => ({ t: q.x, h: q.y, ...(pc.hidden[i] ? { x: true } : {}) }));
    const out = raiseApi(api, roomBase(inst)); // its heights are the room's, lifted with it
    for (const { solid, seen } of solids) {
      if (!seen.length) continue;
      const ring = solid.pieces.flat();
      out.src(srcOf(inst.id, solid.part.kind, solid.part.id));
      api.at(beyondDepthOf(sec, ring), { ring });
      for (const pc of seen) emitPoly(out, th(pc), solid.seeThrough ? 'glass' : 'elev', solid.color);
    }
    api.at(CUT_DEPTH);
    for (const { solid, cut } of solids) {
      if (!cut.length) continue;
      out.src(srcOf(inst.id, solid.part.kind, solid.part.id));
      for (const pc of cut) emitPoly(out, th(pc), solid.seeThrough ? 'glass' : 'cut-hatched', solid.color);
    }
  };

  // What stands round a part's body (partLook.ts): a guard cover's bands from the
  // floor to the body's top, each cut solid or seen at its own depth; the
  // clearance gap is air.
  const drawAttachment = (inst: ProductInstance, a: PartAttachment): void => {
    switch (a.kind) {
      case 'cover': {
        const top = instanceElevation(inst)[1];
        for (const b of a.bands) {
          const poly = boxCornersLocal(b).map((p) => instanceToWorld(inst, p));
          const chord = segmentPolyChord(sec.a, sec.b, poly);
          if (chord) { api.at(CUT_DEPTH); profile(chord[0], chord[1], 0, top, true, a.color); continue; }
          if (!viewedSide(sec, poly)) continue;
          const [pmn, pmx] = projectSpan(sec, poly);
          const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
          if (t1 - t0 > 1e-3) { api.at(nearDepth(poly), { ring: poly }); elev(t0, t1, 0, top, a.color); }
        }
        return;
      }
      case 'gap': return;
      default: unreachable(a, 'attachment');
    }
  };

  for (const inst of instances) {
    const obj = objects++;
    if (isRoomInstance(inst)) {
      const rapi = raiseApi(api, roomBase(inst)); // lift the room by its height-from-ground
      emitters.push({ depth: nearDepth(instanceFootprint(inst)), obj, run: () => { api.src(srcOf(inst.id)); drawRoomSection(inst, sec, rapi, len, api); } });
      for (const g of roomGlazings(inst)) {
        const plan = glazingWorldSolids(inst, g).flatMap((s) => s.pieces.flat());
        emitters.push({ depth: nearDepth(plan), obj, run: () => drawGlazing(inst, g) });
      }
      continue;
    }
    // A part (partLook.ts, as a section sees it: reading no drawing). A node
    // object's run is a ribbon: one piece per segment, each at its own depth.
    // Its `width` x `depth` box is the template's, centred on the instance
    // origin — not the run — so a bent bench used to be sectioned as a single
    // block, misplaced, at a depth that belonged to neither leg.
    const look = solidLook(inst);
    const slats = look.body.kind === 'run' ? nodeBenchSolids(inst, grilleUnder(instances, inst)) : [];
    if (slats.length) {
      const e0 = instanceElevation(inst)[0];
      const own = inst.color || col;
      for (const s of slats) {
        const c = s.part === 'frame' || s.part === 'support' ? structureColor(own) : own;
        const src = s.part === 'support' && s.support ? supportSrc(inst.id, s.support) : srcOf(inst.id);
        // A swept piece — a slat turned down at a closed end, a batten's slanted
        // top — as a room's benches draw theirs.
        const sw = s.sweep;
        emitters.push({ depth: nearDepth(s.poly), obj, run: sw
          ? () => { api.src(src); drawSweptBench(sec, raiseApi(api, e0), (d, plan) => api.at(d, plan), benchSweep(sw.profile, sw.pathAt, sw.quad), c, s.poly); }
          : () => { api.src(src); drawBody(inst, s.poly, [e0 + s.base, e0 + s.top], c); } });
      }
      continue;
    }
    const nodeSegs = look.body.kind === 'run' ? nodeSegmentFootprints(inst) : [];
    if (nodeSegs.length) {
      for (const fp of nodeSegs) emitters.push({ depth: nearDepth(fp), obj, run: () => { api.src(srcOf(inst.id)); drawBody(inst, fp); } });
      continue;
    }
    // What stands round its body first (a heater's cover), whatever the body.
    const attached = (): void => { for (const a of look.attachments) drawAttachment(inst, a); };
    // A part with a 3D model, upright or tilted: its model cut and seen.
    if (partModel(inst)) {
      const body = bodyOf(inst, instanceElevation(inst)[0], { w: inst.width, d: inst.depth, h: partHeight(inst) });
      const plan = planOutline(body);
      emitters.push({ depth: nearDepth(plan), obj, run: () => { api.src(srcOf(inst.id)); attached(); drawModel(inst, body, plan); } });
      continue;
    }
    // A tilted part's true geometry: where the plane slices its body (hatched, as
    // any cut part) and its outline beyond, seen.
    if (isTilted(inst)) {
      const body = bodyOf(inst, instanceElevation(inst)[0], { w: inst.width, d: inst.depth, h: partHeight(inst) });
      const plan = planOutline(body);
      emitters.push({ depth: nearDepth(plan), obj, run: () => { api.src(srcOf(inst.id)); attached(); drawTiltedBody(body, plan, inst.color || col); } });
      continue;
    }
    emitters.push({ depth: nearDepth(instanceFootprint(inst)), obj, run: () => { api.src(srcOf(inst.id)); attached(); drawBody(inst, instanceFootprint(inst)); } });
  }

  // A part's water risers (risers.ts): pipes up its room's wall, cut solid where
  // the plane crosses one, seen beyond only where nothing nearer hides them.
  for (const rz of partRisers(instances)) {
    const obj = objects++;
    const fp = riserRing(rz, 12), color = RISER_COLORS[rz.water];
    emitters.push({
      depth: nearDepth(fp), obj, run: () => {
        api.src(srcOf(rz.src));
        const chord = segmentPolyChord(sec.a, sec.b, fp);
        if (chord) { api.at(CUT_DEPTH); profile(chord[0], chord[1], rz.z0, rz.z1, true, color); return; }
        if (!viewedSide(sec, fp)) return;
        const [pmn, pmx] = projectSpan(sec, fp);
        const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
        if (t1 - t0 > 1e-3) { api.at(nearDepth(fp), { ring: fp }); elev(t0, t1, rz.z0, rz.z1, color); }
      },
    });
  }

  // User-drawn walls: cut ones as solid poché, walls beyond the cut projected —
  // one emitter PER PIECE (`wallSectionPieces`: a piece per segment, and over a
  // door / pane / opening one per layer that still stands there), so a wall
  // wrapping a room sorts its near and far runs independently instead of painting
  // the whole polyline at the nearest run's depth, and a doorway is a gap. Each
  // piece honours the wall's height-from-ground, matching the 3D preview.
  for (const wall of walls) {
    const obj = objects++;
    const color = wall.fill || USER_WALL_COLOR;
    const rect = (t0: number, t1: number, h0: number, h1: number): SecPt[] => [{ t: t0, h: h0 }, { t: t1, h: h0 }, { t: t1, h: h1 }, { t: t0, h: h1 }];
    const piece = (pc: WallPiece): void => {
      const fp = pc.fp;
      api.src(srcOf(wall.id, 'wallFeature', pc.feature));
      const chord = segmentPolyChord(sec.a, sec.b, fp);
      if (chord) {
        api.at(CUT_DEPTH);
        if (pc.glass) { api.glass(chord[0], chord[1], pc.z0, pc.z1, pc.color); return; }
        // A part's pocket the plane passes through: the wall with a notch, one
        // solid (the bands over and under it and beside it, seamless).
        const t0 = Math.min(chord[0], chord[1]), t1 = Math.max(chord[0], chord[1]);
        const ns = (pc.notches ?? []).flatMap((n) => {
          const c = segmentPolyChord(sec.a, sec.b, n.fp);
          return c ? [{ n, a: Math.max(t0, Math.min(c[0], c[1])), b: Math.min(t1, Math.max(c[0], c[1])) }] : [];
        });
        if (!ns.length) { profile(chord[0], chord[1], pc.z0, pc.z1, true, color); return; }
        // One pocket from one face (the usual): one outline, the notch in its side.
        const [one] = ns, lo = pc.z0, hi = pc.z1;
        if (ns.length === 1 && one.n.z0 > lo && one.n.z1 < hi && (one.a <= t0 + 1e-3 || one.b >= t1 - 1e-3)) {
          const { a, b, n: { z0, z1 } } = one;
          const poly: SecPt[] = b >= t1 - 1e-3
            ? [{ t: t0, h: lo }, { t: t1, h: lo }, { t: t1, h: z0 }, { t: a, h: z0 }, { t: a, h: z1 }, { t: t1, h: z1 }, { t: t1, h: hi }, { t: t0, h: hi }]
            : [{ t: t0, h: lo }, { t: t1, h: lo }, { t: t1, h: hi }, { t: t0, h: hi }, { t: t0, h: z1 }, { t: b, h: z1 }, { t: b, h: z0 }, { t: t0, h: z0 }];
          emitPoly(api, poly, 'cut', color);
          return;
        }
        const zs = [...new Set([pc.z0, pc.z1, ...ns.flatMap((x) => [x.n.z0, x.n.z1])])].filter((z) => z >= pc.z0 && z <= pc.z1).sort((p, q) => p - q);
        const rects: SecPt[][] = [];
        for (let k = 0; k + 1 < zs.length; k++) {
          const h0 = zs[k], h1 = zs[k + 1], mid = (h0 + h1) / 2;
          let at = t0;
          for (const x of ns.filter((x) => x.n.z0 < mid && mid < x.n.z1).sort((p, q) => p.a - q.a)) {
            if (x.a > at + 1e-3) rects.push(rect(at, x.a, h0, h1));
            at = Math.max(at, x.b);
          }
          if (t1 > at + 1e-3) rects.push(rect(at, t1, h0, h1));
        }
        for (const r of seamless(rects)) emitPoly(api, r, 'cut', color);
        return;
      }
      if (!viewedSide(sec, fp)) return;
      const [pmn, pmx] = projectSpan(sec, fp);
      const t0 = Math.max(0, pmn), t1 = Math.min(len, pmx);
      if (t1 - t0 <= 1e-3) return;
      api.at(nearDepth(fp), { ring: fp, wall: true });
      if (pc.glass) api.glass(t0, t1, pc.z0, pc.z1, pc.color);
      else elev(t0, t1, pc.z0, pc.z1, color);
    };
    // The wall over and under a part's hole through it: cut, ONE solid, no seam
    // between them.
    const pockets = new Map<string, WallPiece[]>();
    const pieces: WallPiece[][] = [];
    for (const pc of wallSectionPieces(wall, wallPockets(wall, cutParts))) {
      if (!pc.pocket) { pieces.push([pc]); continue; }
      const g = pockets.get(pc.pocket);
      if (g) g.push(pc);
      else { const ng = [pc]; pockets.set(pc.pocket, ng); pieces.push(ng); }
    }
    for (const group of pieces) {
      emitters.push({
        depth: Math.min(...group.map((pc) => nearDepth(pc.fp))),
        obj,
        run: () => {
          const chords = group.map((pc) => segmentPolyChord(sec.a, sec.b, pc.fp));
          if (group.length > 1 && chords.every((c) => c)) {
            api.src(srcOf(wall.id));
            api.at(CUT_DEPTH);
            const rects = group.map((pc, i): SecPt[] => rect(chords[i]![0], chords[i]![1], pc.z0, pc.z1));
            for (const r of seamless(rects)) emitPoly(api, r, 'cut', color);
            return;
          }
          for (const pc of group) piece(pc);
        },
      });
    }
  }

  // User-drawn polygons/ellipses: only those given a 3D height show in section
  // (a flat shape has no vertical extent), extruded from their base like a wall.
  for (const poly of polygons) {
    const obj = objects++;
    const h = polygonHeight(poly);
    if (h <= 0 || poly.points.length < 3) continue;
    const fp = poly.points;
    const base0 = polygonBase(poly), top = base0 + h;
    const color = poly.fill || col;
    const chord = segmentPolyChord(sec.a, sec.b, fp);
    if (chord) {
      emitters.push({ depth: CUT_DEPTH, obj, run: () => { api.src(srcOf(poly.id)); api.at(CUT_DEPTH); profile(chord[0], chord[1], base0, top, true, color); } });
      continue;
    }
    // Beyond the cut: one piece per FACE, each at its own depth — an extruded
    // polygon's outline is a ribbon of faces standing at different distances,
    // exactly like a room's elevation or a drawn wall's segments — each drawn
    // only where no nearer face of it stands in front (`occludeFaces`).
    const shown = polygonElevation(sec, poly.id, fp, len, base0, top, color);
    emitters.push({
      depth: nearDepth(fp),
      obj,
      run: () => { api.src(srcOf(poly.id)); replayEvents(shown, api, api); },
    });
  }

  // Pipes (physical connections): each run of the centreline is cut where it
  // crosses the plane (a ring, the bore clear) and seen beyond it as a band the
  // pipe's width, each at its own depth with its plan outline.
  for (const pipe of pipes) {
    const obj = objects++;
    for (const pc of pipeSectionPieces(sec, pipe, len)) {
      emitters.push({
        depth: pc.depth,
        obj,
        run: () => {
          api.src(srcOf(pipe.id));
          api.at(pc.depth, pc.plan ? { ring: pc.plan } : undefined);
          api.poly(pc.pts, pc.cut ? 'cut' : 'elev', pipe.color);
          if (pc.bore) api.poly(pc.bore, 'band', '#ffffff', 1);
        },
      });
    }
  }

  // Emission order is irrelevant now — every call carries its own depth (and
  // where it stands in plan), and the flush replays them farthest-first, but
  // where an object's face and another object's call say otherwise, so nearer
  // geometry occludes what's behind.
  for (const e of emitters) { api.of(e.obj); e.run(); }
  api.flush();
}

/** A pipe (a physical connection) as a section draws it: its centreline in 3D,
 *  elbows sampled (drawing mm, z up), its outer and inner radius, its colour. */
export interface SectionPipe { id: string; pts: ReadonlyArray<{ x: number; y: number; z: number }>; r: number; ri: number; color: string }

/** A pipe's pieces in a section: where a run crosses the plane, the ring cut
 *  there (an ellipse when the run crosses it slanting, its bore clear); each run
 *  — or what of it stands beyond the plane — as a band the pipe's width seen
 *  from the plane, kept to the view's length, with its depth and its plan. */
export function pipeSectionPieces(sec: Pick<SectionLine, 'a' | 'b' | 'side'>, pipe: SectionPipe, len: number): Array<{ pts: SecPt[]; bore?: SecPt[]; cut: boolean; depth: number; plan?: Vec2[] }> {
  const { u, n } = sectionFrame(sec);
  const dep = (p: { x: number; y: number }): number => (p.x - sec.a.x) * n.x + (p.y - sec.a.y) * n.y;
  const tOf = (p: { x: number; y: number }): number => (p.x - sec.a.x) * u.x + (p.y - sec.a.y) * u.y;
  const out: Array<{ pts: SecPt[]; bore?: SecPt[]; cut: boolean; depth: number; plan?: Vec2[] }> = [];
  const clip = (ps: SecPt[], keep: (p: SecPt) => boolean, at: number): SecPt[] => {
    const o: SecPt[] = [];
    ps.forEach((p, i) => {
      const q = ps[(i + 1) % ps.length];
      if (keep(p)) o.push(p);
      if (keep(p) !== keep(q)) { const f = (at - p.t) / (q.t - p.t); o.push({ t: at, h: p.h + (q.h - p.h) * f }); }
    });
    return o;
  };
  const inView = (pts: SecPt[]): SecPt[] => clip(clip(pts, (p) => p.t >= 0, 0), (p) => p.t <= len, len);
  // An ellipse at (t, h): `ra` along `a` (unit, in the view), `rb` across it.
  const ring = (t: number, h: number, a: { t: number; h: number }, ra: number, rb: number): SecPt[] => {
    const pts: SecPt[] = [];
    for (let k = 0; k < 24; k++) {
      const f = (k / 24) * Math.PI * 2;
      pts.push({ t: t + a.t * ra * Math.cos(f) - a.h * rb * Math.sin(f), h: h + a.h * ra * Math.cos(f) + a.t * rb * Math.sin(f) });
    }
    return pts;
  };
  for (let i = 0; i < pipe.pts.length - 1; i++) {
    let A = pipe.pts[i], B = pipe.pts[i + 1];
    const dA = dep(A), dB = dep(B);
    if (dA < 0 && dB < 0) continue;
    const L = Math.hypot(B.x - A.x, B.y - A.y, B.z - A.z);
    if (L < 1e-6) continue;
    if ((dA < 0) !== (dB < 0)) {
      // Cut: the ring where the run meets the plane, stretched along its slant.
      const f = dA / (dA - dB);
      const C = { x: A.x + (B.x - A.x) * f, y: A.y + (B.y - A.y) * f, z: A.z + (B.z - A.z) * f };
      const tc = tOf(C);
      if (tc >= -pipe.r && tc <= len + pipe.r) {
        const w = { t: (tOf(B) - tOf(A)) / L, h: (B.z - A.z) / L };
        const wl = Math.hypot(w.t, w.h);
        const axis = wl > 1e-6 ? { t: w.t / wl, h: w.h / wl } : { t: 1, h: 0 };
        const k = 1 / Math.max(0.25, Math.abs((dB - dA) / L));
        out.push({ pts: ring(tc, C.z, axis, pipe.r * k, pipe.r), bore: ring(tc, C.z, axis, pipe.ri * k, pipe.ri), cut: true, depth: CUT_DEPTH });
      }
      if (dA < 0) A = C; else B = C;
    }
    // Beyond: the band the run covers in the view (a circle seen end-on).
    const ta = tOf(A), tb = tOf(B);
    const dt = tb - ta, dh = B.z - A.z, l = Math.hypot(dt, dh);
    let pts: SecPt[];
    if (l < 1e-3) pts = ring(ta, A.z, { t: 1, h: 0 }, pipe.r, pipe.r);
    else {
      const o = { t: (-dh / l) * pipe.r, h: (dt / l) * pipe.r };
      pts = [{ t: ta + o.t, h: A.z + o.h }, { t: tb + o.t, h: B.z + o.h }, { t: tb - o.t, h: B.z - o.h }, { t: ta - o.t, h: A.z - o.h }];
    }
    pts = inView(pts);
    if (pts.length < 3) continue;
    const pl = Math.hypot(B.x - A.x, B.y - A.y);
    const nx = pl > 1e-6 ? (-(B.y - A.y) / pl) * pipe.r : pipe.r, ny = pl > 1e-6 ? ((B.x - A.x) / pl) * pipe.r : 0;
    const plan = [{ x: A.x + nx, y: A.y + ny }, { x: B.x + nx, y: B.y + ny }, { x: B.x - nx, y: B.y - ny }, { x: A.x - nx, y: A.y - ny }];
    out.push({ pts, cut: false, depth: Math.max(Math.min(dep(A), dep(B)), BEYOND_DEPTH), plan });
  }
  return out;
}

/**
 * Test seam: every draw call the section makes, in FINAL PAINT ORDER (after the
 * depth sort), tagged with the emitter that produced it. Occlusion is "what is
 * painted later wins", so ordering can be asserted directly in a unit test
 * instead of being eyeballed on a canvas.
 */
export function sectionDrawOrder(
  sec: SectionLine,
  instances: ProductInstance[],
  walls: Wall[] = [],
  polygons: Polygon[] = [],
): Array<{ kind: string; style?: PolyStyle; t0: number; t1: number; h0: number; h1: number; color?: string }> {
  const log: Array<{ kind: string; style?: PolyStyle; t0: number; t1: number; h0: number; h1: number; color?: string }> = [];
  const box = (pts: readonly SecPt[]): { t0: number; t1: number; h0: number; h1: number } => ({
    t0: Math.min(...pts.map((p) => p.t)), t1: Math.max(...pts.map((p) => p.t)),
    h0: Math.min(...pts.map((p) => p.h)), h1: Math.max(...pts.map((p) => p.h)),
  });
  const rec = (kind: string) => (t0: number, t1: number, h0: number, h1: number, ...rest: unknown[]): void => {
    const color = rest.find((r) => typeof r === 'string') as string | undefined;
    log.push({ kind, t0, t1, h0, h1, color });
  };
  const collect: SecDraw = {
    src: () => {},
    profile: (t0, t1, h0, h1, _filled, color) => { log.push({ kind: 'profile', t0, t1, h0, h1, color }); },
    band: rec('band'),
    elev: rec('elev'),
    outline: rec('outline'),
    vedge: (t, h0, h1, color) => { log.push({ kind: 'vedge', t0: t, t1: t, h0, h1, color }); },
    glass: (t0, t1, h0, h1) => { log.push({ kind: 'glass', t0, t1, h0, h1 }); },
    window: rec('window'),
    poly: (pts, style, color) => { log.push({ kind: 'poly', style, ...box(pts), color }); },
    line: (pts, color) => { log.push({ kind: 'line', ...box(pts), color }); },
  };
  emitSectionGeometry(sec, instances, walls, sectionView(sec).len, '#000000', collect, polygons);
  return log;
}

/** Every world-space vertex of the generated section drawing: the corners of
 *  each cut/projected quad, the endpoints of each vertical room-corner edge,
 *  and the two ground-line ends. Lets the cursor (and so the dimension tool)
 *  snap to points on a section elevation — bench tops, wall corners, the floor
 *  and ceiling lines. Reuses `emitSectionGeometry` so it can never drift from
 *  what's actually drawn. */
export function sectionVertices(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): Vec2[] {
  const view = sectionView(sec);
  const base = (t: number, h: number): Vec2 => ({
    x: view.O.x + view.T.x * t + view.N.x * h,
    y: view.O.y + view.T.y * t + view.N.y * h,
  });
  const out: Vec2[] = [base(0, 0), base(view.len, 0)]; // ground line ends
  const quad = (t0: number, t1: number, h0: number, h1: number): void => {
    out.push(base(t0, h0), base(t1, h0), base(t1, h1), base(t0, h1));
  };
  const collect: SecDraw = {
    src: () => {},
    profile: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    band: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    elev: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    outline: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    vedge: (t, h0, h1) => { out.push(base(t, h0), base(t, h1)); },
    glass: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    window: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    // A roof piece's corners are tessellation and convex-split artefacts: its
    // real edges arrive as lines.
    poly: (pts, style) => { if (style !== 'roof') for (const p of pts) if (!p.s) out.push(base(p.t, p.h)); },
    line: (pts) => { for (const p of pts) if (!p.s) out.push(base(p.t, p.h)); },
  };
  emitSectionGeometry(sec, instances, walls, view.len, '#000000', collect, polygons, false, pipes); // a set: no paint order
  return out;
}

/** `sectionVertices`' twin: the same geometry as RUNS rather than corners, so the
 *  derived object snaps (midpoint / nearest / perpendicular / intersection) reach
 *  a section elevation too — its corners were snappable while the wall line
 *  between them was not. Every emitter that draws a quad contributes its four
 *  sides; `vedge` is already a single run. Replays `emitSectionGeometry` for the
 *  same reason its twin does: neither can drift from what is actually drawn. */
export function sectionEdges(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): Array<[Vec2, Vec2] | [Vec2, Vec2, boolean]> {
  const view = sectionView(sec);
  const base = (t: number, h: number): Vec2 => ({
    x: view.O.x + view.T.x * t + view.N.x * h,
    y: view.O.y + view.T.y * t + view.N.y * h,
  });
  // Each run in the view frame while it is collected: a quad's side, or an edge
  // of a poly or a line with its ends' flags.
  type Run = { a: SecPt; b: SecPt; c: boolean };
  const all: Run[] = [];
  // A run along a curve carries `true`: its midpoint is not on the curve. A
  // clip edge (`x`) is no run, nor a mask's repeat of its call's edge (`r`).
  const runs = (pts: readonly SecPt[], closed: boolean): void => {
    const n = pts.length;
    for (let i = 0; i < (closed ? n : n - 1); i++) {
      const p = pts[i], q = pts[(i + 1) % n];
      if (p.x || p.r || Math.hypot(q.t - p.t, q.h - p.h) < 1e-9) continue;
      all.push({ a: p, b: q, c: !!p.c });
    }
  };
  const quad = (t0: number, t1: number, h0: number, h1: number): void => {
    const p: SecPt[] = [{ t: t0, h: h0 }, { t: t1, h: h0 }, { t: t1, h: h1 }, { t: t0, h: h1 }];
    for (let i = 0; i < 4; i++) all.push({ a: p[i], b: p[(i + 1) % 4], c: false });
  };
  const collect: SecDraw = {
    src: () => {},
    profile: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    band: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    elev: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    outline: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    vedge: (t, h0, h1) => { all.push({ a: { t, h: h0 }, b: { t, h: h1 }, c: false }); },
    glass: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    window: (t0, t1, h0, h1) => quad(t0, t1, h0, h1),
    poly: (pts, style) => { if (style !== 'roof') runs(pts, true); },
    line: (pts) => runs(pts, false),
  };
  emitSectionGeometry(sec, instances, walls, view.len, '#000000', collect, polygons, true, pipes);
  const out: Array<[Vec2, Vec2] | [Vec2, Vec2, boolean]> = [[base(0, 0), base(view.len, 0)]]; // the ground line
  for (const r of joinRunsAtSamples(all)) out.push(r.c ? [base(r.a.t, r.a.h), base(r.b.t, r.b.h), true] : [base(r.a.t, r.a.h), base(r.b.t, r.b.h)]);
  return out;
}

/** The runs with the straight ones (none along a curve) that meet end to end,
 *  in line, at a SAMPLE (`s`) joined — across pieces too: a call split where
 *  its paint depth changes (`occludeFaces`) leaves a sample on its straight
 *  edge, which is no vertex, so the edge stays one run from vertex to vertex
 *  and its midpoint is the real one. Where several runs meet at one sample (two
 *  faces' outlines split there along the corner they share), all on one line,
 *  each run one way is joined to one the other way, as many of each, nearest
 *  ends together; else none is. The others keep their order. */
function joinRunsAtSamples<R extends { a: SecPt; b: SecPt; c: boolean }>(runs: readonly R[]): R[] {
  const key = (p: SecPt): string => `${p.t},${p.h}`;
  const live: Array<R | null> = runs.slice();
  const ends = new Map<string, number[]>();
  const note = (i: number, p: SecPt): void => { if (!p.s) return; const k = key(p), l = ends.get(k); if (l) l.push(i); else ends.set(k, [i]); };
  live.forEach((r, i) => { if (!r!.c) { note(i, r!.a); note(i, r!.b); } });
  const work = [...ends.keys()];
  while (work.length) {
    const k = work.pop()!;
    const at = [...new Set(ends.get(k))].filter((i) => live[i] && (key(live[i]!.a) === k || key(live[i]!.b) === k));
    if (at.length < 2) continue;
    const far = (i: number): SecPt => (key(live[i]!.a) === k ? live[i]!.b : live[i]!.a);
    const r0 = live[at[0]]!, P = key(r0.a) === k ? r0.a : r0.b;
    const dir = (i: number): { x: number; y: number; l: number } => { const f = far(i), x = f.t - P.t, y = f.h - P.h; return { x, y, l: Math.hypot(x, y) }; };
    const u = dir(at[0]);
    const one: number[] = [], other: number[] = [];
    let inLine = u.l > 0;
    for (const i of at) {
      const v = dir(i);
      if (!(v.l > 0) || Math.abs(u.x * v.y - u.y * v.x) > 1e-9 * u.l * v.l) { inLine = false; break; }
      (u.x * v.x + u.y * v.y > 0 ? one : other).push(i);
    }
    if (!inLine || one.length !== other.length) continue;
    const byLength = (a: number, b: number): number => dir(a).l - dir(b).l || a - b;
    one.sort(byLength); other.sort(byLength);
    one.forEach((x, m) => {
      // One run from far end to far end, in the direction of the first.
      const i = Math.min(x, other[m]), j = Math.max(x, other[m]), ri = live[i]!, fi = far(i), fj = far(j);
      live[i] = { ...ri, ...(key(ri.a) === k ? { a: fj, b: fi } : { a: fi, b: fj }) };
      live[j] = null;
      for (const p of [fi, fj]) if (p.s) { const q = key(p), l = ends.get(q); if (l) l.push(i); else ends.set(q, [i]); work.push(q); }
    });
  }
  return live.filter((r): r is R => !!r);
}

/** Test seam: a room's wall faces seen in elevation, each with its calls, BEFORE
 *  their self-occlusion — what `occludeFaces` resolves. None for a room the
 *  plane cuts or that stands behind it. */
export function elevationFaces(sec: SectionLine, inst: ProductInstance): ElevFace[] {
  const fp = instanceFootprint(inst);
  if (fp.length < 3 || !viewedSide(sec, fp) || segmentPolyIntervals(sec.a, sec.b, fp).length) return [];
  const len = sectionView(sec).len;
  if (!isFlatCeiling(inst)) return shapedElevationFaces(shapedCtx(inst, sec, resolveCeiling(inst)), len);
  const x = flatElevationInput(inst, sec, len, fp);
  return x ? flatElevationFaces(x, sec) : [];
}

/** One draw call of a generated section, with its whole geometry — see `sectionPieces`. */
export interface SectionPiece {
  /** The `SecDraw` member that made it. */
  kind: 'profile' | 'band' | 'elev' | 'outline' | 'vedge' | 'glass' | 'window' | 'poly' | 'line';
  /** How it is painted: the member's own name, or a poly's style. */
  style: Exclude<SectionPiece['kind'], 'poly'> | PolyStyle;
  /** A profile's solid (true) or hatched (false) look. */
  filled?: boolean;
  /** A band's opacity. */
  alpha?: number;
  /** A line's dashed look. */
  dashed?: boolean;
  color?: string;
  /** A quad's four corners — (t0, h0), (t1, h0), (t1, h1), (t0, h1) —, a vedge's two ends, a poly's ring or a line's points. */
  pts: SecPt[];
  /** The object (and feature) it shows. */
  src?: SecSrc;
}

/** `sectionDrawOrder`'s twin with the full geometry: every draw call in FINAL
 *  paint order, as its points, look and colour — what an occlusion test needs to
 *  tell whether a later OPAQUE call covers an earlier one in (t, h), and what a
 *  vector export reads. `sectionDrawOrder` keeps its own record (the goldens are
 *  its JSON). */
export function sectionPieces(sec: SectionLine, instances: ProductInstance[], walls: Wall[] = [], polygons: Polygon[] = [], pipes: readonly SectionPipe[] = []): SectionPiece[] {
  const out: SectionPiece[] = [];
  const quad = (t0: number, t1: number, h0: number, h1: number): SecPt[] =>
    [{ t: t0, h: h0 }, { t: t1, h: h0 }, { t: t1, h: h1 }, { t: t0, h: h1 }];
  let src: SecSrc | null = null;
  const put = (p: SectionPiece): void => { out.push(src ? { ...p, src } : p); };
  const collect: SecDraw = {
    src: (s) => { src = s; },
    profile: (t0, t1, h0, h1, filled, color) => put({ kind: 'profile', style: 'profile', filled, color, pts: quad(t0, t1, h0, h1) }),
    band: (t0, t1, h0, h1, color, alpha) => put({ kind: 'band', style: 'band', alpha, color, pts: quad(t0, t1, h0, h1) }),
    elev: (t0, t1, h0, h1, color) => put({ kind: 'elev', style: 'elev', color, pts: quad(t0, t1, h0, h1) }),
    outline: (t0, t1, h0, h1, color) => put({ kind: 'outline', style: 'outline', color, pts: quad(t0, t1, h0, h1) }),
    vedge: (t, h0, h1, color) => put({ kind: 'vedge', style: 'vedge', color, pts: [{ t, h: h0 }, { t, h: h1 }] }),
    glass: (t0, t1, h0, h1, color) => put({ kind: 'glass', style: 'glass', color, pts: quad(t0, t1, h0, h1) }),
    window: (t0, t1, h0, h1, color) => put({ kind: 'window', style: 'window', color, pts: quad(t0, t1, h0, h1) }),
    poly: (pts, style, color, alpha) => put({ kind: 'poly', style, color, ...(alpha == null ? {} : { alpha }), pts: pts.map((p) => ({ ...p })) }),
    line: (pts, color, dashed) => put({ kind: 'line', style: 'line', color, dashed, pts: pts.map((p) => ({ ...p })) }),
  };
  emitSectionGeometry(sec, instances, walls, sectionView(sec).len, '#000000', collect, polygons, true, pipes);
  return out;
}

/** A world point in a section view's own `(t, h)` frame. */
export function sectionFramePoint(sec: SectionLine, p: Vec2): { t: number; h: number } {
  const { O, T, N } = sectionView(sec);
  const dx = p.x - O.x, dy = p.y - O.y;
  return { t: dx * T.x + dy * T.y, h: dx * N.x + dy * N.y };
}

/** One key per source: its object, and its feature when it has one. */
export function srcKey(s: SecSrc): string {
  return s.part ? `${s.id}|${s.part.kind}|${s.part.id}` : s.id;
}

/** The looks that paint their area: a point inside takes them. The others
 *  (an outline, a window's frame, an edge, a line) take only their strokes. */
const FILLED_PIECES: ReadonlySet<SectionPiece['style']> = new Set(['profile', 'band', 'elev', 'glass', 'cut', 'cut-hatched', 'roof']);

function pieceTakes(pc: SectionPiece, t: number, h: number, tol: number): boolean {
  const ps = pc.pts, n = ps.length;
  if (!n) return false;
  const closed = pc.kind !== 'line' && pc.kind !== 'vedge';
  for (let i = 0; i < (closed ? n : n - 1); i++) {
    const a = ps[i], b = ps[(i + 1) % n];
    const dt = b.t - a.t, dh = b.h - a.h, l2 = dt * dt + dh * dh;
    const u = l2 > 0 ? Math.max(0, Math.min(1, ((t - a.t) * dt + (h - a.h) * dh) / l2)) : 0;
    if (Math.hypot(t - (a.t + u * dt), h - (a.h + u * dh)) <= tol) return true;
  }
  if (!closed || !FILLED_PIECES.has(pc.style) || n < 3) return false;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const a = ps[i], b = ps[j];
    if ((a.h > h) !== (b.h > h) && t < ((b.t - a.t) * (h - a.h)) / (b.h - a.h) + a.t) inside = !inside;
  }
  return inside;
}

/** What a section view shows at a world point, painted on top first: each
 *  source once — what a click there takes, then what lies under it. A filled
 *  piece takes a point inside it, any piece one within `tol` mm of its strokes. */
export function sectionStackAt(sec: SectionLine, instances: ProductInstance[], walls: Wall[], polygons: Polygon[], p: Vec2, tol: number, pipes: readonly SectionPipe[] = []): SecSrc[] {
  const { t, h } = sectionFramePoint(sec, p);
  const pieces = sectionPieces(sec, instances, walls, polygons, pipes);
  const out: SecSrc[] = [], seen = new Set<string>();
  for (let i = pieces.length - 1; i >= 0; i--) {
    const pc = pieces[i];
    if (!pc.src || !pieceTakes(pc, t, h, tol)) continue;
    const k = srcKey(pc.src);
    if (!seen.has(k)) { seen.add(k); out.push(pc.src); }
    // A room's pane, opening or door is its room's too: the room comes next
    // there, though nothing of it is painted behind the hole. A support's
    // trestle is its bench's: the bench comes next.
    const part = pc.src.part, kind = part?.kind;
    if (part?.bench) {
      const bench = srcOf(pc.src.id, 'bench', part.bench), bk = srcKey(bench);
      if (!seen.has(bk)) { seen.add(bk); out.push(bench); }
    }
    if (kind === 'pane' || kind === 'opening' || kind === 'door') {
      const room = srcOf(pc.src.id), rk = srcKey(room);
      if (!seen.has(rk)) { seen.add(rk); out.push(room); }
    }
  }
  return out;
}

/** A dashed stroke from `p` to `q` (screen px), `on` px drawn and `off` not,
 *  as the subpaths that dash it in step with its LINE: from the line's own
 *  origin along it, the same whichever way the stroke runs — so every stroke
 *  along one line dashes alike (a call's edge and its mask's repeat of it, an
 *  edge's pieces split where its paint depth changes). Each subpath starts
 *  where a dash does, or inside one: stroked with `setLineDash([on, off])` at
 *  no offset. */
export function anchoredDashes(p: Vec2, q: Vec2, on: number, off: number): Array<[Vec2, Vec2]> {
  const L = Math.hypot(q.x - p.x, q.y - p.y), period = on + off;
  if (!(L > 0) || !(period > 0)) return [];
  // The line's own direction: rightward, or down a line that runs (all but) straight down.
  const [a, b] = (Math.abs(q.x - p.x) > 1e-9 * L ? q.x < p.x : q.y < p.y) ? [q, p] : [p, q];
  const ux = (b.x - a.x) / L, uy = (b.y - a.y) / L;
  const s0 = a.x * ux + a.y * uy, next = Math.ceil(s0 / period) * period - s0; // the next dash, from a
  const at = (s: number): Vec2 => ({ x: a.x + ux * s, y: a.y + uy * s });
  const out: Array<[Vec2, Vec2]> = [];
  if (next - off > 0) out.push([a, at(Math.min(next - off, L))]); // the rest of the dash a is in
  if (next < L) out.push([at(next), b]);
  return out;
}

/** 45° hatch fill clipped to a quad (or any polygon). */
function hatch(ctx: CanvasRenderingContext2D, quad: Vec2[], color: string): void {
  const xs = quad.map((p) => p.x), ys = quad.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  ctx.save();
  ctx.beginPath();
  quad.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
  ctx.closePath(); ctx.clip();
  ctx.strokeStyle = color; ctx.globalAlpha = 0.5; ctx.lineWidth = 0.75;
  // Lines x + y = c on one grid of c, so hatches side by side run on.
  const step = 6;
  ctx.beginPath();
  for (let c = Math.floor((minX + minY) / step) * step; c < maxX + maxY; c += step) {
    ctx.moveTo(c - maxY, maxY); ctx.lineTo(c - minY, minY);
  }
  ctx.stroke();
  ctx.restore();
}

/** Distance (screen px) from a point to the section line, for hit-testing. */
export function sectionDistance(vp: Draw2, sec: SectionLine, screen: Vec2): number {
  const A = vp.worldToScreen(sec.a), B = vp.worldToScreen(sec.b);
  const dx = B.x - A.x, dy = B.y - A.y;
  const l2 = dx * dx + dy * dy || 1;
  let t = ((screen.x - A.x) * dx + (screen.y - A.y) * dy) / l2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(screen.x - (A.x + t * dx), screen.y - (A.y + t * dy));
}
